import {t, useLanguage} from '../i18n';
import {interfaceLocale} from '../i18n';
import {EXCEPTION_KEYS} from "../adminCalendar/scheduleExceptions";
import {getEffectiveManualAuthSession} from "../utils/manualAuth";
import {usePendingEditor} from "../hooks/useEditorNavigation";
// LOOP WORKERS RESPONSIVE TOOLTIP HARDENING V23 — remove native browser tooltips and switch rows by container width.
// LOOP WORKER PERMISSIONS REVAMP V99 — compact permission command center with collapsible connected accounts and permission groups.
// LOOP AGENDA MADRE WORKER PROFILE PARITY V93 — paired with backend profile resolver; connected accounts UI preserved.
// LOOP WORKER CONNECTED ACCOUNTS V92 — list and remove linked accounts per worker.
// LOOP WORKER INVITE DEEPLINK V87 — QR/link use production HTTPS URL; app links/custom scheme supported.
// LOOP AGENDA MADRE WORKER ACCESS MIRROR V86 — mirrors worker name/photo/availability for worker agenda.
// LOOP WORKER ACCESS V3 — final worker modal polish; simplified access tab; WhatsApp Próximamente.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { collection, getDocs, doc, getDoc, getDocFromServer, updateDoc, writeBatch, setDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db, storage, auth } from "../firebase";
import fsClient from "../data/firestoreClient";
import { isIosCapacitor } from "../utils/nativeHttp";

import defaultWorker from "../assets/default user/defaultrabajador.jpg";
import ImageCropper from "../components/ImageCropper";
import { DEFAULT_WORKER_PERMISSIONS, LOOP_BUSINESS_ROLES, WORKER_PERMISSION_GROUPS, getRolePermissionPreset, getAccessRole, normalizeWorkerPermissions } from "../adminCalendar/workerPermissions";
import { IMAGE_UPLOAD_ACCEPT, prepareImageFilesForUpload } from "../utils/imageUploadUtils";
import { PhoneInput, VerifiedPhoneBadge } from "../components/PhoneKit";
import { LOOP_PHONE_COUNTRIES, normalizePhoneToE164, parsePhoneValue } from "../utils/phoneUtils";
import QRCode from "qrcode";
import {manageWorkerAccess, workerAccessErrorMessage} from "../services/workerAccess";
import "./WorkerAccess.css";

const DAYS = [
  { key: "sunday", label: "Dom" },
  { key: "monday", label: "Lun" },
  { key: "tuesday", label: "Mar" },
  { key: "wednesday", label: "Mié" },
  { key: "thursday", label: "Jue" },
  { key: "friday", label: "Vie" },
  { key: "saturday", label: "Sáb" },
];

const WEEK_DISPLAY_DAYS = [
  { key: "monday", label: "Lun" },
  { key: "tuesday", label: "Mar" },
  { key: "wednesday", label: "Mié" },
  { key: "thursday", label: "Jue" },
  { key: "friday", label: "Vie" },
  { key: "saturday", label: "Sáb" },
  { key: "sunday", label: "Dom" },
];

function ensureAvailabilityShape(avail) {
  const base = avail && typeof avail === "object" ? { ...avail } : {};
  for (const d of DAYS) {
    if (!Array.isArray(base[d.key])) base[d.key] = [];
  }
  return base;
}

function isValidTimeHHMM(s) {
  const m = String(s || "").match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return false;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return false;
  if (hh < 0 || hh > 23) return false;
  if (mm < 0 || mm > 59) return false;
  return true;
}

/**
 * Clean availability coming from Firestore:
 * - ensures all 7 days exist
 * - removes invalid ranges like {} or {from:"",to:""}
 * - keeps only {from,to} with valid HH:MM
 */
function sanitizeAvailability(avail) {
  const shaped = ensureAvailabilityShape(avail);
  const out = {};
  for (const d of DAYS) {
    const ranges = Array.isArray(shaped[d.key]) ? shaped[d.key] : [];
    const cleaned = ranges
      .map((r) => ({
        from: typeof r?.from === "string" ? r.from : "",
        to: typeof r?.to === "string" ? r.to : "",
      }))
      .filter((r) => isValidTimeHHMM(r.from) && isValidTimeHHMM(r.to));
    out[d.key] = cleaned;
  }
  return out;
}

function normalizeAnyRanges(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "object") {
    if (value.closed === true || value.enabled === false) return [];
    if (Array.isArray(value.ranges)) return value.ranges;
    if (value.from || value.start || value.fromTime || value.startTime) return [value];
  }
  return [];
}

function normalizeAvailabilityFromAny(raw) {
  if (!raw || typeof raw !== "object") return null;
  const out = {};
  let hasAnyDay = false;
  for (const d of DAYS) {
    const ranges = normalizeAnyRanges(raw[d.key])
      .map((r) => ({
        from: String(r?.from || r?.start || r?.fromTime || r?.startTime || "").trim(),
        to: String(r?.to || r?.end || r?.toTime || r?.endTime || "").trim(),
      }))
      .filter((r) => isValidTimeHHMM(r.from) && isValidTimeHHMM(r.to));
    if (ranges.length) hasAnyDay = true;
    out[d.key] = ranges;
  }
  return hasAnyDay ? ensureAvailabilityShape(out) : null;
}

function getBusinessAvailabilitySource(data) {
  if (!data || typeof data !== "object") return null;
  return data.availability || data.openingHours || data.hours || data.schedule || data.businessAvailability || null;
}

/* ✅ Default availability: Mon–Sat 08:00–17:00, Sunday off */
function defaultAvailabilityMonSat8to17() {
  return ensureAvailabilityShape({
    monday: [{ from: "08:00", to: "17:00" }],
    tuesday: [{ from: "08:00", to: "17:00" }],
    wednesday: [{ from: "08:00", to: "17:00" }],
    thursday: [{ from: "08:00", to: "17:00" }],
    friday: [{ from: "08:00", to: "17:00" }],
    saturday: [{ from: "08:00", to: "17:00" }],
    sunday: [],
  });
}

function availabilityIsEmpty(avail) {
  if (!avail || typeof avail !== "object") return true;
  const shaped = ensureAvailabilityShape(avail);
  return DAYS.every((d) => !Array.isArray(shaped[d.key]) || shaped[d.key].length === 0);
}

function clampTimeString(s) {
  if (!s) return "09:00";
  const m = String(s).match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return "09:00";
  let hh = Math.max(0, Math.min(23, Number(m[1])));
  let mm = Math.max(0, Math.min(59, Number(m[2])));
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

function minutesBetween(from, to) {
  const [fh, fm] = String(from || "00:00").split(":").map(Number);
  const [th, tm] = String(to || "00:00").split(":").map(Number);
  const a = fh * 60 + fm;
  const b = th * 60 + tm;
  const diff = b - a;
  return diff > 0 ? diff : 0;
}

function formatDurationMins(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h <= 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function formatTimeShort(time) {
  const clean = clampTimeString(time);
  const [hh, mm] = clean.split(":");
  return `${hh}:${mm}`;
}

function formatRangeShort(range) {
  if (!range || !range.from || !range.to) return "Cerrado";
  return `${formatTimeShort(range.from)}–${formatTimeShort(range.to)}`;
}

function dayScheduleLabel(ranges) {
  if (!Array.isArray(ranges) || ranges.length === 0) return "Cerrado";
  const main = ranges[0];
  const breakR = ranges[1];
  if (breakR) return `${formatRangeShort(main)} · descanso ${formatRangeShort(breakR)}`;
  return formatRangeShort(main);
}

function summarizeWeeklyAvailability(avail) {
  const shaped = ensureAvailabilityShape(avail);
  const monday = shaped.monday || [];
  const tuesday = shaped.tuesday || [];
  const wednesday = shaped.wednesday || [];
  const thursday = shaped.thursday || [];
  const friday = shaped.friday || [];
  const saturday = shaped.saturday || [];
  const sunday = shaped.sunday || [];

  const weekdaySame = [tuesday, wednesday, thursday, friday].every((ranges) => JSON.stringify(ranges) === JSON.stringify(monday));
  const saturdaySameAsWeekday = JSON.stringify(saturday) === JSON.stringify(monday);
  const sundayClosed = !Array.isArray(sunday) || sunday.length === 0;

  if (monday.length > 0 && weekdaySame && saturdaySameAsWeekday && sundayClosed) {
    return `Lun–Sáb · ${dayScheduleLabel(monday)} · Dom cerrado`;
  }
  if (monday.length > 0 && weekdaySame && sundayClosed) {
    return `Lun–Vie · ${dayScheduleLabel(monday)} · Dom cerrado`;
  }

  const openDays = WEEK_DISPLAY_DAYS.filter((d) => Array.isArray(shaped[d.key]) && shaped[d.key].length > 0);
  if (openDays.length === 0) return "Sin horario configurado";
  return `${openDays.length} días activos · ${openDays.map((d) => d.label).join(", ")}`;
}

function prettyDayNameEs(dayKey) {
  switch (dayKey) {
    case "monday":
      return "Lunes";
    case "tuesday":
      return "Martes";
    case "wednesday":
      return "Miércoles";
    case "thursday":
      return "Jueves";
    case "friday":
      return "Viernes";
    case "saturday":
      return "Sábado";
    case "sunday":
      return "Domingo";
    default:
      return dayKey;
  }
}

/** YYYY-MM-DD of today in local time */
function ymdToday() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function rangeIsPast(r, todayYmd) {
  if (!r) return false;
  const from = String(r.from || "");
  const to = String(r.to || "");
  const end = to || from;
  if (!end) return false;
  return end < todayYmd;
}

function splitBlockedRanges(blockedRanges, todayYmd) {
  const list = Array.isArray(blockedRanges) ? blockedRanges : [];
  const active = [];
  const history = [];
  for (const r of list) {
    if (rangeIsPast(r, todayYmd)) history.push(r);
    else active.push(r);
  }
  active.sort((a, b) => String(a.from).localeCompare(String(b.from)));
  history.sort((a, b) => String(b.from).localeCompare(String(a.from)));
  return { active, history };
}

function isNativeRuntimeNow() {
  try {
    if (typeof window === "undefined") return false;
    const cap = window.Capacitor;
    if (!cap) return false;
    const platform = typeof cap.getPlatform === "function" ? cap.getPlatform() : cap.platform || null;
    return !!(platform && platform !== "web");
  } catch {
    return false;
  }
}

// LOOP WORKER PERMISSIONS FINAL DEBUG HOTFIX — server verification + visible debug hooks.
function workerPersistNow() {
  return isIosCapacitor() ? new Date().toISOString() : serverTimestamp();
}

async function workerUpdateDocByPath(path, payload) {
  if (isIosCapacitor() && typeof fsClient.updateDocByPath === "function") {
    return fsClient.updateDocByPath(path, payload, { db });
  }
  return updateDoc(doc(db, path), payload);
}

async function workerSetDocByPath(path, payload, options = { merge: true }) {
  if (isIosCapacitor() && typeof fsClient.setDocByPath === "function") {
    return fsClient.setDocByPath(path, payload, { db, merge: options?.merge !== false });
  }
  return setDoc(doc(db, path), payload, options);
}

async function workerGetDocByPath(path, options = {}) {
  if (isIosCapacitor() && typeof fsClient.getDocByPath === "function") {
    return fsClient.getDocByPath(path, { db });
  }
  const ref = doc(db, path);
  let snap = null;
  if (options?.fromServer && typeof getDocFromServer === "function") {
    try {
      snap = await getDocFromServer(ref);
    } catch (serverErr) {
      console.warn("WorkersSection: getDocFromServer failed; falling back to getDoc", serverErr);
      snap = await getDoc(ref);
    }
  } else {
    snap = await getDoc(ref);
  }
  return snap && snap.exists() ? { id: snap.id, ...(snap.data() || {}) } : null;
}

function workerPermissionsEqual(expected = {}, actual = {}) {
  try {
    const keys = Object.keys(DEFAULT_WORKER_PERMISSIONS || {});
    return keys.every((key) => Boolean(expected?.[key]) === Boolean(actual?.[key]));
  } catch (_) {
    return false;
  }
}


function normalizeWorkerType(employee) {
  const raw = String(employee?.workerType || employee?.staffType || employee?.type || "").trim().toLowerCase();
  if (["operational", "operativo", "operations", "staff", "support"].includes(raw)) return "operational";
  if (["service", "servicio", "bookable", "public"].includes(raw)) return "service";
  if (employee?.isOperationalWorker === true || employee?.operationalOnly === true || employee?.hiddenFromBooking === true) return "operational";
  return "service";
}

function workerTypeCopy(workerType) {
  return workerType === "operational"
    ? {
        title: "Equipo operativo",
        badge: "Operativo",
        description: "Se usa para agenda interna y asignación operativa. No aparece para que el cliente lo elija al reservar.",
      }
    : {
        title: "Trabajador de servicio",
        badge: "Servicio",
        description: "Aparece en el flujo de reserva cuando el cliente puede elegir quién realizará el servicio.",
      };
}

function getServiceDisplayName(service) {
  if (!service || typeof service !== "object") return "";
  const candidates = [
    service.name,
    service.title,
    service.label,
    service.serviceName,
    service.displayName,
  ];

  for (const value of candidates) {
    const clean = String(value || "").trim();
    if (clean) return clean;
  }

  return "Servicio";
}

function looksLikeServiceCode(value) {
  const s = String(value || "").trim();
  if (!s) return true;

  // Firestore/doc ids, timestamps, tmp ids, UUID-ish or very long slugs should not be shown to business users.
  if (/^(tmp-|svc-|service-|character-)/i.test(s)) return true;
  if (/^[a-z0-9_-]{12,}$/i.test(s) && /[0-9]/.test(s)) return true;
  if (/^\d{6,}$/.test(s)) return true;
  if (/^[a-f0-9]{16,}$/i.test(s)) return true;
  return false;
}


const WORKER_PHOTO_MAX_MB = 12;
const WORKER_PHOTO_SUPPORTED_TYPES = new Set(["image/jpeg", "image/jpg", "image/png", "image/webp"]);
const WORKER_PHOTO_SUPPORTED_EXTENSIONS = new Set(["jpg", "jpeg", "png", "webp"]);
const WORKER_PHOTO_HEIC_EXTENSIONS = new Set(["heic", "heif"]);
const HEIC2ANY_CDN = "https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js";

function workerPhotoExtension(file) {
  const name = String(file?.name || "").toLowerCase();
  const parts = name.split(".");
  return parts.length > 1 ? parts.pop() : "";
}

function workerPhotoBaseName(file) {
  const name = String(file?.name || "worker-photo").trim() || "worker-photo";
  return name.replace(/\.[^.]+$/, "") || "worker-photo";
}

function workerPhotoIsHeicLike(file) {
  const ext = workerPhotoExtension(file);
  const type = String(file?.type || "").toLowerCase();
  return WORKER_PHOTO_HEIC_EXTENSIONS.has(ext) || type.includes("heic") || type.includes("heif");
}



function normalizeCommissionType(value) {
  const raw = String(value || "none").trim().toLowerCase();
  if (["percentage", "percent", "%", "porcentaje"].includes(raw)) return "percentage";
  if (["fixed", "flat", "monto", "amount"].includes(raw)) return "fixed";
  return "none";
}

function normalizeCommissionValue(value) {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function normalizeWorkerStringArray(value) {
  return Array.from(new Set((Array.isArray(value) ? value : [])
    .map((item) => {
      if (typeof item === "string" || typeof item === "number") return String(item).trim();
      if (item && typeof item === "object") return String(item.id || item.serviceId || item.projectId || item.uid || "").trim();
      return "";
    })
    .filter(Boolean)));
}

function buildPlainWorkerPermissions(employee = {}) {
  return normalizeWorkerPermissions(employee);
}

function normalizeWorkerAccessEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function safeWorkerAccessDocId(emailValue) {
  return normalizeWorkerAccessEmail(emailValue);
}

function generateWorkerInviteToken() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID().replace(/-/g, "");
    }
  } catch (_) {}
  return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}_${Math.random().toString(36).slice(2, 12)}`;
}

const WORKER_INVITE_EXPIRATION_DAYS = 30;

function workerInviteExpiresAtDate(days = WORKER_INVITE_EXPIRATION_DAYS) {
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000);
}

async function sha256Hex(value) {
  const text = String(value || "");
  if (!text) throw new Error("missing_token_for_hash");
  if (typeof crypto === "undefined" || !crypto.subtle || typeof TextEncoder === "undefined") {
    throw new Error("secure_hash_unavailable");
  }
  const encoded = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest("SHA-256", encoded);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

const LOOP_WORKER_INVITE_PUBLIC_BASE_URL = "https://www.loopreservas.com";

function normalizeLoopPublicBaseUrl(value) {
  const raw = String(value || "").trim().replace(/\/+$/, "");
  if (!raw) return "";
  try {
    const url = new URL(raw);
    if (url.protocol !== "https:") return "";
    return `${url.protocol}//${url.host}`.replace(/\/+$/, "");
  } catch (_) {
    return "";
  }
}

function getLoopPublicBaseUrl() {
  const envBase = normalizeLoopPublicBaseUrl(
    (typeof process !== "undefined" && process.env && (
      process.env.REACT_APP_PUBLIC_BASE_URL ||
      process.env.REACT_APP_LOOP_PUBLIC_URL ||
      process.env.REACT_APP_LOOP_PUBLIC_BASE_URL
    )) || ""
  );
  if (envBase) return envBase;

  try {
    const origin = String(window?.location?.origin || "").replace(/\/+$/, "");
    const host = String(window?.location?.hostname || "").toLowerCase();
    if (["loopreservas.com", "www.loopreservas.com"].includes(host) && origin.startsWith("https://")) {
      return origin;
    }
  } catch (_) {}

  return LOOP_WORKER_INVITE_PUBLIC_BASE_URL;
}

function buildWorkerInviteQuery({ businessId, inviteId, token }) {
  const qs = new URLSearchParams({
    businessId: String(businessId || ""),
    inviteId: String(inviteId || ""),
    token: String(token || ""),
  });
  return qs.toString();
}

function buildWorkerInviteUrl({ businessId, inviteId, token }) {
  return `${getLoopPublicBaseUrl()}/worker-invite?${buildWorkerInviteQuery({ businessId, inviteId, token })}`;
}

function buildWorkerInviteAppUrl({ businessId, inviteId, token }) {
  return `loopreservas://worker-invite?${buildWorkerInviteQuery({ businessId, inviteId, token })}`;
}

function formatWorkerInviteExpiry(value) {
  try {
    const raw = value?.toDate ? value.toDate() : value;
    const d = raw instanceof Date ? raw : raw ? new Date(raw) : null;
    if (!d || Number.isNaN(d.getTime())) return "30 días";
    return d.toLocaleDateString(interfaceLocale(), { day: "2-digit", month: "short", year: "numeric" });
  } catch (_) {
    return "30 días";
  }
}

function workerInviteAccessState(employee = {}) {
  const status = String(employee.accessStatus || employee.inviteStatus || "").toLowerCase();
  const linkedUid = employee.userUid || employee.authUid || employee.linkedUserId || employee.linkedUserUid || null;
  const invitedEmail = normalizeWorkerAccessEmail(employee.inviteEmail || employee.email || employee.employeeEmail || "");
  const authEmail = normalizeWorkerAccessEmail(employee.authEmail || employee.authEmailLower || employee.loginEmail || "");
  const inviteStatus = String(employee.inviteStatus || "").toLowerCase();
  const expiresRaw = employee.inviteExpiresAt?.toDate ? employee.inviteExpiresAt.toDate() : employee.inviteExpiresAt;
  const expiresAt = expiresRaw ? new Date(expiresRaw) : null;
  const expired = Boolean(expiresAt && !Number.isNaN(expiresAt.getTime()) && expiresAt.getTime() < Date.now());
  const hasSecureInvite = Boolean(
    employee.activeInviteId ||
    employee.inviteId ||
    employee.activeInviteToken ||
    employee.inviteToken ||
    employee.accessInviteToken ||
    employee.inviteExpiresAt ||
    status === "pending" ||
    inviteStatus === "pending"
  );

  if (status === 'suspended' || status === 'revoked') {
    return {key: status, label: status === 'suspended' ? 'Suspendido' : 'Desvinculado', tone: 'pending', helper: status === 'suspended' ? 'No puede entrar hasta que reactives su acceso.' : 'Comparte una nueva invitación para conectar una cuenta.', invitedEmail, authEmail, expiresAt: null};
  }
  if (linkedUid || status === "linked") {
    return {
      key: "linked",
      label: "Vinculado",
      tone: "linked",
      helper: "La cuenta del trabajador ya quedó conectada por UID. Puede entrar aunque Apple use correo privado.",
      invitedEmail,
      authEmail,
      linkedUid: linkedUid ? String(linkedUid) : "",
      expiresAt,
    };
  }

  if (expired && hasSecureInvite) {
    return {
      key: "expired",
      label: "Expirado",
      tone: "expired",
      helper: "Este link ya expiró. Genera una nueva invitación para que el trabajador pueda vincularse.",
      invitedEmail,
      authEmail: "",
      linkedUid: "",
      expiresAt,
    };
  }

  if (hasSecureInvite) {
    return {
      key: "pending",
      label: "Pendiente",
      tone: "pending",
      helper: expiresAt ? `Envía el link para que el trabajador vincule su cuenta. Expira el ${formatWorkerInviteExpiry(expiresAt)}.` : "Envía el link para que el trabajador inicie sesión y vincule su cuenta.",
      invitedEmail,
      authEmail: "",
      linkedUid: "",
      expiresAt,
    };
  }

  return {
    key: "none",
    label: "Sin acceso",
    tone: "none",
    helper: "Genera un link seguro de un solo uso. WhatsApp queda marcado como Próximamente.",
    invitedEmail,
    authEmail: "",
    linkedUid: "",
    expiresAt: null,
  };
}

function workerInviteStatusLabel(employee = {}) {
  return workerInviteAccessState(employee).label;
}

// LOOP WORKER CONNECTED ACCOUNTS V92 — visible account registry + selective unlink controls.
function workerConnectionDateLabel(value) {
  try {
    const raw = value?.toDate ? value.toDate() : value;
    const d = raw instanceof Date ? raw : raw ? new Date(raw) : null;
    if (!d || Number.isNaN(d.getTime())) return "Sin fecha";
    return d.toLocaleString(interfaceLocale(), {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_) {
    return "Sin fecha";
  }
}

function firstWorkerConnectionValue(...values) {
  for (const value of values) {
    const clean = String(value || "").trim();
    if (clean) return clean;
  }
  return "";
}

function buildWorkerConnectionRow(source, docId, data = {}, employee = {}) {
  const uid = firstWorkerConnectionValue(
    data.uid,
    data.userUid,
    data.authUid,
    data.linkedUserId,
    data.linkedUserUid,
    source === "uid" ? docId : ""
  );
  const email = normalizeWorkerAccessEmail(firstWorkerConnectionValue(
    data.authEmail,
    data.authEmailLower,
    data.email,
    data.emailLower,
    data.workerEmail,
    data.workerEmailLower,
    data.employeeEmail,
    data.employeeEmailLower,
    data.inviteEmail,
    data.inviteEmailLower,
    source === "email" ? docId : ""
  ));
  const name = firstWorkerConnectionValue(
    data.workerName,
    data.employeeName,
    data.name,
    data.displayName,
    employee?.name,
    email,
    uid
  );
  const primaryUid = firstWorkerConnectionValue(
    employee?.userUid,
    employee?.authUid,
    employee?.linkedUserId,
    employee?.linkedUserUid
  );
  const primaryEmail = normalizeWorkerAccessEmail(firstWorkerConnectionValue(
    employee?.authEmail,
    employee?.authEmailLower,
    employee?.loginEmail,
    employee?.loginEmailLower
  ));
  const isPrimaryUid = Boolean(uid && primaryUid && uid === primaryUid);
  const isPrimaryEmail = Boolean(email && primaryEmail && email === primaryEmail);
  const linkedAt = data.linkedAt || data.createdAt || data.updatedAt || employee?.linkedAt || employee?.updatedAt || null;

  return {
    key: `${source}:${docId}`,
    source,
    docId: String(docId || ""),
    uid,
    email,
    name,
    role: data.accessRole || data.loopRole || data.role || employee?.accessRole || "worker",
    active: data.active !== false,
    status: data.accessStatus || data.inviteStatus || employee?.accessStatus || "linked",
    linkedAt,
    linkedAtLabel: workerConnectionDateLabel(linkedAt),
    isPrimary: isPrimaryUid || isPrimaryEmail,
    isPrimaryUid,
    isPrimaryEmail,
  };
}

function dedupeWorkerConnections(rows = []) {
  const activeRows = (rows || []).filter((row) => row && row.active !== false);
  const uidEmails = new Set(
    activeRows
      .filter((row) => row.uid && row.email)
      .map((row) => normalizeWorkerAccessEmail(row.email))
      .filter(Boolean)
  );
  const map = new Map();
  activeRows.forEach((row) => {
    if (!row || row.active === false) return;
    if (!row.uid && row.email && uidEmails.has(normalizeWorkerAccessEmail(row.email))) return;
    const signature = row.uid ? `uid:${row.uid}` : row.email ? `email:${row.email}` : row.key;
    const current = map.get(signature);
    if (!current || (row.isPrimary && !current.isPrimary) || (row.source === "uid" && current.source !== "uid")) {
      map.set(signature, row);
    }
  });
  return Array.from(map.values()).sort((a, b) => {
    if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
    if (a.source !== b.source) return a.source === "uid" ? -1 : 1;
    return String(a.email || a.uid || "").localeCompare(String(b.email || b.uid || ""));
  });
}

function maskWorkerUid(value) {
  const s = String(value || "").trim();
  if (!s) return "";
  if (s.length <= 10) return s;
  return `${s.slice(0, 6)}…${s.slice(-4)}`;
}

function formatAccessCountdown(ms) {
  const total = Math.max(0, Math.floor(Number(ms || 0) / 1000));
  if (total >= 86400) return `${Math.ceil(total / 86400)} días`;
  if (total >= 3600) return `${Math.floor(total / 3600)} h ${Math.floor((total % 3600) / 60)} min`;
  const mm = Math.floor(total / 60);
  const ss = total % 60;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

function accessShareStatusCopy(status) {
  if (status === "linked") {
    return { label: "Cuenta conectada", helper: "El trabajador ya quedó conectado con la empresa.", tone: "linked" };
  }
  if (status === "checking") {
    return { label: "Esperando conexión", helper: "Mantén esta ventana abierta. Loop actualizará el estado cuando el trabajador entre.", tone: "checking" };
  }
  return { label: "Todavía no conectado", helper: "Comparte el acceso. Loop actualizará este estado cuando el trabajador entre.", tone: "pending" };
}

function buildWorkerAccessFields(emailValue, uidValue = null) {
  const email = normalizeWorkerAccessEmail(emailValue);
  const uid = uidValue ? String(uidValue) : null;
  const payload = {
    email,
    emailLower: email,
    emailNormalized: email,
    workerEmail: email,
    workerEmailLower: email,
    workerEmailNormalized: email,
    employeeEmail: email,
    employeeEmailLower: email,
    employeeEmailNormalized: email,
    inviteEmail: email,
    inviteEmailLower: email,
    inviteEmailNormalized: email,
    contactEmail: email,
    contactEmailLower: email,
    contactEmailNormalized: email,
    accessEmails: email ? [email] : [],
    allowedEmails: email ? [email] : [],
    accessStatus: uid ? "linked" : (email ? "invited" : "secure_link"),
  };
  if (uid) {
    payload.userUid = uid;
    payload.authUid = uid;
    payload.linkedUserId = uid;
    payload.linkedUserUid = uid;
  }
  return payload;
}

function buildWorkerPhoneIdentityFields(phoneValue, existing = {}) {
  const parsed = parsePhoneValue(phoneValue || existing.phoneE164 || existing.phoneNormalized || existing.phone || existing.whatsapp || "", { defaultCountry: "PA" });
  const phoneDisplay = parsed.display || "";
  const phoneE164 = parsed.e164 || "";
  const previousE164 = String(existing.phoneE164 || existing.phoneNormalized || existing.workerPhoneE164 || "").trim();
  const keepVerified = Boolean(existing.phoneVerified || existing.workerPhoneVerified) && phoneE164 && previousE164 === phoneE164;
  const hasPhone = Boolean(phoneE164);

  return {
    phone: phoneDisplay,
    telefono: phoneDisplay,
    whatsapp: phoneDisplay,
    whatsApp: phoneDisplay,
    phoneDisplay,
    phoneE164,
    phoneNormalized: phoneE164,
    telefonoNormalized: phoneE164,
    workerPhone: phoneDisplay,
    workerPhoneDisplay: phoneDisplay,
    workerPhoneE164: phoneE164,
    workerPhoneNormalized: phoneE164,
    employeePhone: phoneDisplay,
    employeePhoneDisplay: phoneDisplay,
    employeePhoneE164: phoneE164,
    employeePhoneNormalized: phoneE164,
    phoneCountryCode: parsed.countryCode || "PA",
    phoneDialCode: parsed.dialCode || "+507",
    phoneNational: parsed.nationalNumber || "",
    phoneNationalFormatted: parsed.nationalFormatted || "",
    whatsappDigits: parsed.whatsappDigits || "",
    whatsappPhoneStatus: hasPhone ? (parsed.isValid ? "valid" : "invalid") : "missing",
    phoneVerified: keepVerified,
    workerPhoneVerified: keepVerified,
    phoneVerificationProvider: keepVerified ? (existing.phoneVerificationProvider || existing.workerPhoneVerificationProvider || "whatsapp") : null,
    phoneVerifiedAt: keepVerified ? (existing.phoneVerifiedAt || existing.workerPhoneVerifiedAt || null) : null,
    workerPhoneVerifiedAt: keepVerified ? (existing.workerPhoneVerifiedAt || existing.phoneVerifiedAt || null) : null,
    inviteChannel: existing.inviteChannel || "secure_link",
    workerInviteChannel: existing.workerInviteChannel || "secure_link",
    preferredInviteChannel: existing.preferredInviteChannel || "secure_link",
    whatsappInviteStatus: hasPhone ? "coming_soon" : "missing_phone",
    whatsappInviteReady: false,
  };
}

function workerPhoneStatusCopy(employee = {}) {
  const phone = employee.phoneE164 || employee.phoneNormalized || employee.workerPhoneE164 || normalizePhoneToE164(employee.phone || employee.whatsapp || "", "PA");
  if (!phone) {
    return { label: "WhatsApp opcional", tone: "missing", helper: "Puedes guardar el número como contacto. El envío por WhatsApp estará disponible próximamente." };
  }
  if (employee.phoneVerified === true || employee.workerPhoneVerified === true) {
    return { label: "WhatsApp guardado", tone: "verified", helper: "Número guardado como contacto. Invitación por WhatsApp: próximamente." };
  }
  if (employee.inviteStatus === "pending" || employee.whatsappInviteStatus === "pending") {
    return { label: "WhatsApp próximamente", tone: "ready", helper: "Contacto guardado. El acceso se gestiona desde la pestaña Acceso." };
  }
  return { label: "WhatsApp próximamente", tone: "ready", helper: "Número guardado. Por ahora usa link seguro; WhatsApp se activará cuando esté listo." };
}

function workerHasActiveLegacyAccess(employee = {}) {
  const access = workerInviteAccessState(employee);
  if (access.key === "linked" || access.key === "pending") return true;
  return Boolean(
    employee.userUid ||
    employee.authUid ||
    employee.linkedUserId ||
    employee.linkedUserUid ||
    employee.email ||
    employee.emailLower ||
    employee.emailNormalized ||
    employee.workerEmail ||
    employee.workerEmailLower ||
    employee.workerEmailNormalized ||
    employee.employeeEmail ||
    employee.employeeEmailLower ||
    employee.employeeEmailNormalized ||
    employee.inviteEmail ||
    employee.inviteEmailLower ||
    employee.inviteEmailNormalized ||
    employee.contactEmail ||
    employee.contactEmailLower ||
    employee.contactEmailNormalized ||
    (Array.isArray(employee.accessEmails) && employee.accessEmails.length) ||
    (Array.isArray(employee.allowedEmails) && employee.allowedEmails.length)
  );
}

function workerHasProtectedLegacyAccess(employee = {}) {
  const access = workerInviteAccessState(employee);
  if (access.key === "linked" || access.key === "pending") return false;

  const legacyEmails = [
    employee.email,
    employee.emailLower,
    employee.emailNormalized,
    employee.workerEmail,
    employee.workerEmailLower,
    employee.workerEmailNormalized,
    employee.employeeEmail,
    employee.employeeEmailLower,
    employee.employeeEmailNormalized,
    employee.inviteEmail,
    employee.inviteEmailLower,
    employee.inviteEmailNormalized,
    employee.contactEmail,
    employee.contactEmailLower,
    employee.contactEmailNormalized,
    employee.userEmail,
    employee.userEmailLower,
    employee.loginEmail,
    employee.loginEmailLower,
    ...(Array.isArray(employee.accessEmails) ? employee.accessEmails : []),
    ...(Array.isArray(employee.allowedEmails) ? employee.allowedEmails : []),
  ].filter(Boolean).map(normalizeWorkerAccessEmail);

  return legacyEmails.some(Boolean);
}

function workerConnectionStatusCopy(employee = {}) {
  const access = workerInviteAccessState(employee);
  if (access.key === "linked") {
    return {
      key: "linked",
      tone: "linked",
      icon: "✓",
      label: "Sincronizado",
      helper: "Cuenta conectada",
      description: "Este trabajador ya está conectado con la empresa.",
      permissionsTitle: "Permisos activos",
      permissionsHelper: "Puedes ajustar sus accesos de agenda, reservas y operación.",
      actionLabel: "Cambiar conexión",
    };
  }

  if (workerHasProtectedLegacyAccess(employee)) {
    return {
      key: "legacy",
      tone: "legacy",
      icon: "!",
      label: "Acceso anterior protegido",
      helper: "Puede seguir entrando",
      description: "Este trabajador conserva su acceso anterior. Puedes conectar la cuentalo por UID cuando tengas oportunidad.",
      permissionsTitle: "Permisos activos con acceso anterior",
      permissionsHelper: "Este trabajador puede seguir entrando. Sus permisos se mantienen visibles y editables.",
      actionLabel: "Conectar cuenta trabajador",
    };
  }

  return {
    key: "pending",
    tone: "pending",
    icon: "×",
    label: "Aún no conectado",
    helper: "Ninguna cuenta conectada",
    description: "Conecta su cuenta para activar agenda, reservas y permisos.",
    permissionsTitle: "Permisos bloqueados hasta conectar la cuenta",
    permissionsHelper: "Cuando el trabajador conecte su cuenta, podrás activar o ajustar sus permisos desde aquí.",
    actionLabel: "Conectar trabajador",
  };
}

function workerMigrationStatusCopy(employee = {}) {
  const access = workerInviteAccessState(employee);
  const phoneStatus = workerPhoneStatusCopy(employee);
  const legacyActive = workerHasActiveLegacyAccess(employee);

  if (access.key === "linked") {
    return {
      tone: "complete",
      label: phoneStatus.tone === "missing" ? "Acceso activo" : "Acceso activo · WhatsApp próximamente",
      helper: "El trabajador ya está vinculado por UID. WhatsApp queda solo como contacto/futuro canal de invitación.",
    };
  }

  if (access.key === "pending") {
    return {
      tone: "pending_ready",
      label: "Invitación segura pendiente",
      helper: "Comparte el link de un solo uso. El trabajador podrá entrar con Apple, Google o email y Loop lo vinculará por UID.",
    };
  }

  if (legacyActive) {
    return {
      tone: "needs_confirm",
      label: "Acceso legado protegido",
      helper: "El acceso existente se conserva, pero la identidad premium será UID al aceptar una invitación segura.",
    };
  }

  return {
    tone: phoneStatus.tone === "missing" ? "missing" : "ready",
    label: "Sin acceso · genera link seguro",
    helper: "Crea una invitación de un solo uso. WhatsApp se mostrará como Próximamente, no como autenticación activa.",
  };
}

function workerMigrationActionCopy(employee = {}) {
  const migration = workerMigrationStatusCopy(employee);
  if (["complete"].includes(migration.tone)) return "Completo";
  if (["pending_ready"].includes(migration.tone)) return "Compartir link";
  if (["ready", "needs_confirm", "missing"].includes(migration.tone)) return "Generar link";
  return "Revisar";
}

function workerEmailInviteFallbackReady(employee = {}) {
  return Boolean(employee?.id);
}

function workerWhatsAppStandbyCopy(employee = {}) {
  const phoneStatus = workerPhoneStatusCopy(employee);
  if (phoneStatus.tone === "missing") {
    return {
      label: "WhatsApp próximamente",
      helper: "Puedes guardar el número como contacto. El envío de invitaciones por WhatsApp no está activo todavía.",
      tone: "missing",
    };
  }
  return {
    label: "WhatsApp próximamente",
    helper: "Número guardado. Por ahora usa link seguro; WhatsApp queda como canal futuro.",
    tone: "standby",
  };
}

function workerCommissionSummary(employee = {}) {
  const type = normalizeCommissionType(employee.defaultCommissionType || employee.commissionType || employee.workerCommissionType);
  const value = normalizeCommissionValue(employee.defaultCommissionValue ?? employee.commissionValue ?? employee.workerCommissionValue);
  if (type === "percentage" && value > 0) return `${value}% por reserva`;
  if (type === "fixed" && value > 0) return `$${value % 1 === 0 ? value.toFixed(0) : value.toFixed(2)} por reserva`;
  return "Sin comisión automática";
}

function formatWorkerCommissionMoney(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return "$0";
  return `$${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}`;
}

function workerCommissionTypeLabel(employee = {}) {
  const type = normalizeCommissionType(employee.defaultCommissionType || employee.commissionType || employee.workerCommissionType);
  const value = normalizeCommissionValue(employee.defaultCommissionValue ?? employee.commissionValue ?? employee.workerCommissionValue);
  if (type === "percentage" && value > 0) return `${value}%`;
  if (type === "fixed" && value > 0) return `${formatWorkerCommissionMoney(value)}`;
  if (type === "percentage") return "Porcentaje";
  if (type === "fixed") return "Monto fijo";
  return "Sin comisión";
}

function workerCommissionBaseLabel(employee = {}) {
  return employee?.commissionIncludesTips ? "servicio + propina" : "solo servicio";
}

function workerCommissionApplyLabel(employee = {}) {
  return employee?.commissionOnlyCompleted !== false ? "reservas completadas" : "todas las reservas";
}

function workerCommissionSummarySentence(employee = {}) {
  const name = String(employee?.name || "Este trabajador").trim() || "Este trabajador";
  const type = normalizeCommissionType(employee.defaultCommissionType || employee.commissionType || employee.workerCommissionType);
  const value = normalizeCommissionValue(employee.defaultCommissionValue ?? employee.commissionValue ?? employee.workerCommissionValue);
  if (type === "none" || value <= 0) {
    return `${name} no tiene comisión automática configurada.`;
  }
  const amount = type === "percentage" ? `${value}%` : formatWorkerCommissionMoney(value);
  return `${name} recibirá ${amount} por ${workerCommissionApplyLabel(employee)}, calculado sobre ${workerCommissionBaseLabel(employee)}.`;
}

function workerCommissionExample(employee = {}) {
  const type = normalizeCommissionType(employee.defaultCommissionType || employee.commissionType || employee.workerCommissionType);
  const value = normalizeCommissionValue(employee.defaultCommissionValue ?? employee.commissionValue ?? employee.workerCommissionValue);
  if (type === "none" || value <= 0) return "Activa un modelo de comisión para ver un ejemplo automático.";
  const service = 30;
  const tip = employee?.commissionIncludesTips ? 5 : 0;
  const base = service + tip;
  const payout = type === "percentage" ? base * (value / 100) : value;
  const baseText = employee?.commissionIncludesTips ? "$30 servicio + $5 propina" : "$30 servicio";
  return `Ejemplo: con ${baseText}, el payout sería ${formatWorkerCommissionMoney(payout)}.`;
}

function loadWorkerImage(file, timeoutMs = 7000) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("No image file"));
      return;
    }

    const url = URL.createObjectURL(file);
    const img = new Image();
    let done = false;
    const finish = (ok, value) => {
      if (done) return;
      done = true;
      try { URL.revokeObjectURL(url); } catch (_) {}
      if (ok) resolve(value);
      else reject(value instanceof Error ? value : new Error(String(value || "Image load failed")));
    };

    img.onload = () => finish(true, img);
    img.onerror = () => finish(false, new Error("Browser could not decode image"));
    img.src = url;
    setTimeout(() => finish(false, new Error("Image load timeout")), timeoutMs);
  });
}

function canvasToBlob(canvas, type = "image/jpeg", quality = 0.92) {
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Canvas export failed"));
      }, type, quality);
    } catch (err) {
      reject(err);
    }
  });
}

async function convertReadableImageToJpegFile(file) {
  const img = await loadWorkerImage(file);
  const width = img.naturalWidth || img.width || 0;
  const height = img.naturalHeight || img.height || 0;
  if (!width || !height) throw new Error("Invalid image size");

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not available");
  ctx.drawImage(img, 0, 0, width, height);

  const blob = await canvasToBlob(canvas, "image/jpeg", 0.92);
  return new File([blob], `${workerPhotoBaseName(file)}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
}

function loadScriptOnce(src) {
  return new Promise((resolve, reject) => {
    if (typeof document === "undefined") {
      reject(new Error("Document not available"));
      return;
    }
    const existing = document.querySelector(`script[data-loop-worker-photo-lib="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === "true") resolve();
      else {
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener("error", () => reject(new Error("Script failed")), { once: true });
      }
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.defer = true;
    script.dataset.loopWorkerPhotoLib = src;
    script.onload = () => { script.dataset.loaded = "true"; resolve(); };
    script.onerror = () => reject(new Error("No pudimos cargar el conversor HEIC."));
    document.head.appendChild(script);
  });
}

async function loadHeic2Any() {
  if (typeof window !== "undefined" && typeof window.heic2any === "function") return window.heic2any;
  await loadScriptOnce(HEIC2ANY_CDN);
  if (typeof window !== "undefined" && typeof window.heic2any === "function") return window.heic2any;
  throw new Error("Conversor HEIC no disponible");
}

async function createWorkerInviteQrDataUrl(url) {
  const cleanUrl = String(url || "").trim();
  if (!cleanUrl) throw new Error("missing_worker_invite_url_for_qr");

  // Local QR generation. Do not use a CDN or external image generator because
  // the invite URL contains a one-time token.
  return QRCode.toDataURL(cleanUrl, {
    errorCorrectionLevel: "M",
    margin: 2,
    width: 360,
    color: {
      dark: "#003F3A",
      light: "#FFFFFF",
    },
  });
}

async function convertHeicToJpegFile(file) {
  // Safari/macOS can sometimes decode HEIC natively. Try the zero-dependency path first.
  try {
    return await convertReadableImageToJpegFile(file);
  } catch (_) {
    // Fall back to heic2any for Chrome/Edge/Firefox and unsupported HEIC previews.
  }

  const heic2any = await loadHeic2Any();
  const converted = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.92 });
  const blob = Array.isArray(converted) ? converted[0] : converted;
  if (!blob) throw new Error("HEIC conversion returned empty file");
  return new File([blob], `${workerPhotoBaseName(file)}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
}

function validateWorkerPhotoFile(file) {
  return new Promise((resolve) => {
    if (!file) {
      resolve({ ok: false, message: "No encontramos la imagen seleccionada." });
      return;
    }

    const ext = workerPhotoExtension(file);
    const type = String(file.type || "").toLowerCase();
    const sizeMb = Number(file.size || 0) / (1024 * 1024);

    if (sizeMb > WORKER_PHOTO_MAX_MB) {
      resolve({
        ok: false,
        message: `La imagen pesa ${sizeMb.toFixed(1)}MB. Usa una imagen menor a ${WORKER_PHOTO_MAX_MB}MB.`,
      });
      return;
    }

    if (type && !WORKER_PHOTO_SUPPORTED_TYPES.has(type) && !WORKER_PHOTO_SUPPORTED_EXTENSIONS.has(ext)) {
      resolve({ ok: false, message: "Formato no soportado. Usa JPG, PNG, WEBP o HEIC." });
      return;
    }

    if (!type && !WORKER_PHOTO_SUPPORTED_EXTENSIONS.has(ext)) {
      resolve({ ok: false, message: "No pudimos reconocer el formato. Usa JPG, PNG, WEBP o HEIC." });
      return;
    }

    loadWorkerImage(file)
      .then(() => resolve({ ok: true }))
      .catch(() => resolve({
        ok: false,
        message: "El navegador no pudo leer esa imagen. Prueba con JPG, PNG o WEBP.",
      }));
  });
}


function workerUploadHandlerLooksUnavailable(fn) {
  if (typeof fn !== "function") return true;
  try {
    const name = String(fn.name || "");
    const body = Function.prototype.toString.call(fn);
    return name.includes("handleInlineUploadUnavailable") || body.includes("todavía vive en editar empresa") || body.includes("carga de imagen/archivo");
  } catch (_) {
    return false;
  }
}

async function uploadWorkerPhotoDirectly({ businessId, employeeId, businessDoc, file, onProgress = () => {} }) {
  if (!businessId || !employeeId) throw new Error("missing_worker_photo_context");
  if (!file) throw new Error("missing_worker_photo_file");
  if (!storage) throw new Error("storage_not_initialized");

  const user = auth && auth.currentUser ? auth.currentUser : null;
  try {
    if (user && typeof user.getIdToken === "function") await user.getIdToken(true);
  } catch (tokenErr) {
    console.warn("WorkersSection: worker photo token refresh skipped", tokenErr);
  }

  const ownerUid = String(
    businessDoc?.ownerUid ||
    businessDoc?.ownerId ||
    businessDoc?.createdBy ||
    user?.uid ||
    ""
  ).trim();

  if (!ownerUid) throw new Error("missing_worker_photo_owner_uid");

  const ext = workerPhotoExtension(file) || "jpg";
  const safeExt = ["jpg", "jpeg", "png", "webp"].includes(ext.toLowerCase()) ? ext.toLowerCase() : "jpg";
  const path = `businesses/${ownerUid}/${businessId}/employees/${employeeId}/photo-${Date.now()}.${safeExt}`;
  const ref = storageRef(storage, path);
  const metadata = {
    contentType: file.type || "image/jpeg",
    customMetadata: {
      ownerUid,
      businessId: String(businessId),
      employeeId: String(employeeId),
      source: "workers-section-direct-upload",
    },
  };

  const task = uploadBytesResumable(ref, file, metadata);
  await new Promise((resolve, reject) => {
    task.on(
      "state_changed",
      (snap) => {
        try {
          const pct = snap.totalBytes ? Math.round((snap.bytesTransferred / snap.totalBytes) * 100) : 0;
          onProgress(pct);
        } catch (_) {}
      },
      reject,
      resolve
    );
  });

  const url = await getDownloadURL(task.snapshot.ref);
  await workerUpdateDocByPath(`businesses/${businessId}/employees/${employeeId}`, {
    photoUrl: url,
    photoURL: url,
    imageUrl: url,
    updatedAt: workerPersistNow(),
  });
  return url;
}

async function prepareWorkerPhotoFile(file, setStatus = () => {}) {
  if (!file) return { ok: false, message: "No encontramos la imagen seleccionada." };

  if (workerPhotoIsHeicLike(file)) {
    try {
      setStatus("Convirtiendo HEIC a JPG...");
      const converted = await convertHeicToJpegFile(file);
      setStatus("Validando imagen...");
      const validation = await validateWorkerPhotoFile(converted);
      if (!validation.ok) return validation;
      return { ok: true, file: converted, converted: true };
    } catch (err) {
      console.error("WorkersSection: HEIC conversion failed", err);
      return {
        ok: false,
        message: "No pudimos convertir la foto HEIC automáticamente. Intenta exportarla como JPG desde Fotos/Vista Previa y súbela nuevamente.",
      };
    }
  }

  setStatus("Validando imagen...");
  const validation = await validateWorkerPhotoFile(file);
  if (!validation.ok) return validation;
  return { ok: true, file, converted: false };
}


const PHONE_COUNTRIES = LOOP_PHONE_COUNTRIES.map((item) => ({
  code: item.dialCode,
  country: item.countryCode,
  label: item.label,
  placeholder: item.placeholder,
}));

function detectPhoneCountry(value) {
  return parsePhoneValue(value, { defaultCountry: "PA" }).dialCode || "+507";
}

function getPhoneNationalNumber(value) {
  return parsePhoneValue(value, { defaultCountry: "PA" }).nationalFormatted || "";
}

function isWorkerVisibleToCustomers(employee) {
  if (normalizeWorkerType(employee) !== "service") return false;
  if (employee?.visibleToCustomers === false) return false;
  if (employee?.showOnBusinessPage === false) return false;
  if (employee?.hiddenFromPublicProfile === true) return false;
  if (employee?.hiddenFromBooking === true) return false;
  if (employee?.canBeBookedOnline === false) return false;
  return true;
}

function NiceSwitch({ checked, onChange, disabled, ariaLabel }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      aria-label={ariaLabel}
      aria-pressed={checked}
      disabled={disabled}
      className={[
        "relative w-[52px] h-[30px] rounded-full border transition",
        disabled ? "opacity-60" : "",
        checked ? "border-emerald-600 bg-emerald-500/90" : "border-slate-300 bg-slate-200",
      ].join(" ")}
      title={checked ? "Disponible" : t("No disponible")}
    >
      <span
        className={[
          "absolute top-[3px] w-[24px] h-[24px] rounded-full bg-white shadow transition-all",
          checked ? "left-[25px]" : "left-[3px]",
        ].join(" ")}
      />
    </button>
  );
}

function TabPill({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "px-3 py-2 rounded-full text-sm font-extrabold border whitespace-nowrap",
        active ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-200",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function SectionCard({ title, right, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-start justify-between gap-3">
        <div>
          <div className="font-black text-slate-900">{title}</div>
        </div>
        {right ? <div className="shrink-0">{right}</div> : null}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

export default function WorkersSection({
  businessId,
  businessDoc = null,
  employees = [],
  services = [],
  onDeleteEmployee = () => {},
  openCropper = null,
  forceDirectWorkerPhotoUpload = false,
  onUploadEmployeeProjects = async () => {},
  onSaveEmployee = async () => {},
  onAccessSaved = () => {},
  onAddEmployee = async () => {},
  showPageHeader = true,
  pageTitle = "Equipo",
  pageSubtitle = "Gestiona personas, servicios y acceso seguro.",
  externalSaveSignal = 0,
}) {
  useLanguage();
  const [modalOpen, setModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [activeTab, setActiveTab] = useState("general");
  const [agendaSearch, setAgendaSearch] = useState("");

  const [saving, setSaving] = useState(false);
  usePendingEditor({pending:modalOpen || addModalOpen,busy:saving,title:"Equipo",discard:()=>{setModalOpen(false);setAddModalOpen(false);setEditingEmployee(null);}});
  const externalSaveSignalRef = useRef(externalSaveSignal);
  const [workerPhotoStatus, setWorkerPhotoStatus] = useState("");

  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const editorBodyRef = useRef(null);

  // Legacy business-level noWorkers is intentionally no longer used as the worker type source of truth.
  // The worker type now lives per employee in workerType: "service" | "operational".
  const [businessNoWorkers, setBusinessNoWorkers] = useState(false);
  const [togglingNoWorkers, setTogglingNoWorkers] = useState(false);
  const [localWorkerTypes, setLocalWorkerTypes] = useState({});
  const [showTeamOnBusinessPage, setShowTeamOnBusinessPage] = useState(true);
  const [togglingShowTeam, setTogglingShowTeam] = useState(false);
  const [workerFilter, setWorkerFilter] = useState("all");
  const [workerSearch, setWorkerSearch] = useState("");
  const [inviteCopying, setInviteCopying] = useState(false);
  const [accessHistory, setAccessHistory] = useState(null);
  useEffect(() => setAccessHistory(null), [editingEmployee?.id]);
  const [quickInviteCopyingId, setQuickInviteCopyingId] = useState("");
  const [normalizingWorkers, setNormalizingWorkers] = useState(false);
  const [workerBackfillResult, setWorkerBackfillResult] = useState(null);
  const [accessDetailsOpen, setAccessDetailsOpen] = useState(false);
  const [accessQrOpen, setAccessQrOpen] = useState(false);
  const [accessQrLoading, setAccessQrLoading] = useState(false);
  const [accessQrUrl, setAccessQrUrl] = useState("");
  const [accessQrDataUrl, setAccessQrDataUrl] = useState("");
  const [accessShareOpen, setAccessShareOpen] = useState(false);
  const [accessShareMode, setAccessShareMode] = useState("choose"); // choose | qr | link
  const [accessShareUrl, setAccessShareUrl] = useState("");
  const [accessShareDataUrl, setAccessShareDataUrl] = useState("");
  const [accessShareLoading, setAccessShareLoading] = useState(false);
  const [accessShareError, setAccessShareError] = useState('');
  const accessShareRequestRef = useRef(0);
  const [accessShareCopied, setAccessShareCopied] = useState(false);
  const [accessShareSessionExpiresAt, setAccessShareSessionExpiresAt] = useState(null);
  const [accessShareSyncStatus, setAccessShareSyncStatus] = useState("pending");
  const [accessShareNow, setAccessShareNow] = useState(() => Date.now());
  const [workerConnections, setWorkerConnections] = useState([]);
  const [workerConnectionsLoading, setWorkerConnectionsLoading] = useState(false);
  const [workerConnectionsError, setWorkerConnectionsError] = useState("");
  const [workerConnectionRemovingKey, setWorkerConnectionRemovingKey] = useState("");
  const [permissionAccordionOpen, setPermissionAccordionOpen] = useState({ accounts: false, groups: {} });
  const [compensationEditorOpen, setCompensationEditorOpen] = useState(false);
  const [workerPhotoCropFile, setWorkerPhotoCropFile] = useState(null);
  const [workerPhotoCropMeta, setWorkerPhotoCropMeta] = useState(null);

  // Add modal fields
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newWorkerType, setNewWorkerType] = useState("service");

  // Blocked dates popup state
  const [blockedModalOpen, setBlockedModalOpen] = useState(false);
  const [blockedTab, setBlockedTab] = useState("active"); // "active" | "history"

  // Mobile usability: accordion for schedule day cards
  const [openDayKey, setOpenDayKey] = useState("");

  // Services tab search
  const [workerServiceSearch, setWorkerServiceSearch] = useState("");

  // Business schedule used by “Usar horario de la empresa” inside worker schedule.
  const [businessAvailability, setBusinessAvailability] = useState(() =>
    normalizeAvailabilityFromAny(getBusinessAvailabilitySource(businessDoc))
  );

  // ✅ detect native runtime once
  const isNativeRuntime = useMemo(() => isNativeRuntimeNow(), []);

  // ✅ give space for your fixed green header (and safe-area on iOS)
  // Tune HEADER_GUARD_PX if needed (this is the key fix for your screenshot)
  const HEADER_GUARD_PX = 110; // <- adjust to 120/130 if still hidden
  const modalOverlayPadStyle = {
    paddingTop: `calc(env(safe-area-inset-top, 0px) + ${isNativeRuntime ? HEADER_GUARD_PX : 88}px)`,
    paddingBottom: "24px",
  };

  const nativeOverlayTopPadStyle = isNativeRuntime
    ? {
        paddingTop: `calc(env(safe-area-inset-top, 0px) + ${HEADER_GUARD_PX}px)`,
      }
    : null;

  useEffect(() => {
    if (!modalOpen) {
      setEditingEmployee(null);
      setActiveTab("general");
      setBlockedModalOpen(false);
      setBlockedTab("active");
      setOpenDayKey("");
      setWorkerServiceSearch("");
      setWorkerPhotoStatus("");
      setAccessDetailsOpen(false);
      setAccessQrOpen(false);
      setAccessQrLoading(false);
      setAccessQrUrl("");
      setAccessQrDataUrl("");
      setAccessShareOpen(false);
      accessShareRequestRef.current += 1;
      setAccessShareError('');
      setAccessShareMode("choose");
      setAccessShareUrl("");
      setAccessShareDataUrl("");
      setAccessShareLoading(false);
      setAccessShareCopied(false);
      setAccessShareSessionExpiresAt(null);
      setAccessShareSyncStatus("pending");
      setWorkerConnections([]);
      setWorkerConnectionsLoading(false);
      setWorkerConnectionsError("");
      setWorkerConnectionRemovingKey("");
      setPermissionAccordionOpen({ accounts: false, groups: {} });
      setCompensationEditorOpen(false);
      setWorkerPhotoCropFile(null);
      setWorkerPhotoCropMeta(null);
    }
  }, [modalOpen]);

  useEffect(() => {
    if (!modalOpen) return undefined;
    const timer = setTimeout(() => {
      try {
        if (editorBodyRef.current && typeof editorBodyRef.current.scrollTo === "function") {
          editorBodyRef.current.scrollTo({ top: 0, behavior: "smooth" });
        }
      } catch (_) {}
    }, 0);
    return () => clearTimeout(timer);
  }, [activeTab, modalOpen]);

  useEffect(() => {
    if (!modalOpen || typeof document === "undefined") return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [modalOpen]);

  useEffect(() => {
    if (!addModalOpen || typeof document === "undefined") return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [addModalOpen]);

  useEffect(() => {
    if (!accessShareOpen) return undefined;
    setAccessShareNow(Date.now());
    const timer = setInterval(() => setAccessShareNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [accessShareOpen]);

  useEffect(() => {
    if (!accessShareOpen || !businessId || !editingEmployee?.id) return undefined;
    let cancelled = false;

    const checkLinked = async () => {
      try {
        setAccessShareSyncStatus((prev) => (prev === "linked" ? prev : "checking"));
        const latest = await workerGetDocByPath(`businesses/${businessId}/employees/${editingEmployee.id}`, { fromServer: true });
        if (cancelled || !latest) return;
        const latestAccess = workerInviteAccessState(latest);
        if (latestAccess.key === "linked") {
          setEditingEmployee((prev) => prev && String(prev.id) === String(editingEmployee.id) ? { ...prev, ...latest } : prev);
          setAccessShareSyncStatus("linked");
        } else {
          setAccessShareSyncStatus("pending");
        }
      } catch (_) {
        if (!cancelled) setAccessShareSyncStatus("pending");
      }
    };

    checkLinked();
    const timer = setInterval(checkLinked, 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [accessShareOpen, businessId, editingEmployee?.id]);

  useEffect(() => {
    if (!modalOpen || activeTab !== "permissions" || !businessId || !editingEmployee?.id) return;
    loadWorkerConnections(editingEmployee);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, activeTab, businessId, editingEmployee?.id, editingEmployee?.userUid, editingEmployee?.authUid, editingEmployee?.linkedUserId, editingEmployee?.linkedUserUid, editingEmployee?.authEmail, editingEmployee?.authEmailLower]);

  // Live-sync photo changes from parent employees
  useEffect(() => {
    if (!modalOpen || !editingEmployee?.id) return;
    const latest = (employees || []).find((e) => String(e.id) === String(editingEmployee.id));
    if (!latest) return;

    const nextPhoto = latest.photoUrl || latest.photoURL || latest.photo || null;
    if (nextPhoto && nextPhoto !== editingEmployee.photoUrl) {
      setEditingEmployee((prev) => ({ ...prev, photoUrl: nextPhoto }));
    }
  }, [employees, modalOpen, editingEmployee?.id, editingEmployee?.photoUrl]);

  // Load business.noWorkers
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!businessId) {
        if (mounted) {
          setBusinessNoWorkers(false);
          setBusinessAvailability(normalizeAvailabilityFromAny(getBusinessAvailabilitySource(businessDoc)));
        }
        return;
      }
      try {
        const bref = doc(db, "businesses", businessId);
        const snap = await getDoc(bref);
        if (mounted && snap && snap.exists()) {
          const data = snap.data() || {};
          setBusinessNoWorkers(Boolean(data.noWorkers));
          setShowTeamOnBusinessPage(data.showTeamOnBusinessPage !== false && data.hideTeamOnBusinessPage !== true);
          setBusinessAvailability(normalizeAvailabilityFromAny(getBusinessAvailabilitySource(data)));
        } else if (mounted) {
          setBusinessNoWorkers(false);
          setBusinessAvailability(normalizeAvailabilityFromAny(getBusinessAvailabilitySource(businessDoc)));
        }
      } catch (e) {
        console.warn("WorkersSection: read business flag failed", e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [businessId, businessDoc]);

  const toggleNoWorkers = async (checked) => {
    if (!businessId) return;
    if (checked) {
      const ok = window.confirm("Esto hará que los clientes reserven por servicio/experiencia y no por trabajador. Tu equipo seguirá disponible para asignación operativa. ¿Continuar?");
      if (!ok) return;
    }
    setTogglingNoWorkers(true);
    try {
      await workerUpdateDocByPath(`businesses/${businessId}`, { noWorkers: Boolean(checked), updatedAt: workerPersistNow() });
      // Importante: no deshabilitamos empleados. En modo eventos/noWorkers el equipo queda como staff operativo asignable.

      setBusinessNoWorkers(Boolean(checked));
      alert("Configuración guardada");
    } catch (err) {
      console.error("WorkersSection.toggleNoWorkers error", err);
      alert("No se pudo actualizar. Revisa la consola.");
    } finally {
      setTogglingNoWorkers(false);
    }
  };

  const toggleShowTeamOnBusinessPage = async (checked) => {
    if (!businessId) return;
    setTogglingShowTeam(true);
    try {
      await workerUpdateDocByPath(`businesses/${businessId}`, {
        showTeamOnBusinessPage: Boolean(checked),
        hideTeamOnBusinessPage: !Boolean(checked),
        updatedAt: workerPersistNow(),
      });
      setShowTeamOnBusinessPage(Boolean(checked));
    } catch (err) {
      console.error("WorkersSection.toggleShowTeamOnBusinessPage error", err);
      alert("No se pudo actualizar la visibilidad del equipo.");
    } finally {
      setTogglingShowTeam(false);
    }
  };

  const openEdit = (emp) => {
    let blockedRanges = [];
    if (Array.isArray(emp?.blockedRanges)) blockedRanges = emp.blockedRanges.slice();
    else if (Array.isArray(emp?.blockedDates)) blockedRanges = emp.blockedDates.slice().map((d) => ({ from: d, to: d }));

    const sanitized = sanitizeAvailability(emp?.availability);
    // Do not auto-fill 08:00–17:00 when an existing worker has no schedule.
    // Empty availability must stay explicit so the agenda can show “sin horario”
    // instead of silently pretending the worker works 8–5.
    const nextAvailability = sanitized;

    const effectiveWorkerType = localWorkerTypes[String(emp?.id)] || normalizeWorkerType(emp);

    setEditingEmployee({
      ...emp,
      workerType: effectiveWorkerType,
      staffType: effectiveWorkerType,
      type: effectiveWorkerType,
      availability: nextAvailability,
      services: Array.isArray(emp?.services) ? emp.services.slice() : [],
      projects: Array.isArray(emp?.projects) ? emp.projects.slice() : [],
      blockedRanges,
      __newBlockedFrom: "",
      __newBlockedTo: "",
      defaultCommissionType: normalizeCommissionType(emp?.defaultCommissionType || emp?.commissionType || emp?.workerCommissionType),
      defaultCommissionValue: normalizeCommissionValue(emp?.defaultCommissionValue ?? emp?.commissionValue ?? emp?.workerCommissionValue ?? 0),
      commissionIncludesTips: Boolean(emp?.commissionIncludesTips),
      commissionOnlyCompleted: emp?.commissionOnlyCompleted !== false,
      accessRole: getAccessRole(emp) === 'owner' ? 'admin' : getAccessRole(emp),
      compensationVisibilityOverride: emp?.compensationVisibilityOverride || "business_default",
      permissions: normalizeWorkerPermissions(emp),
    });
    setActiveTab("general");
    setModalOpen(true);
  };

  const permissionActorUid = auth?.currentUser?.uid || getEffectiveManualAuthSession()?.user?.uid;
  const canGrantScheduleExceptions = Boolean(permissionActorUid && permissionActorUid === (businessDoc?.ownerUid || businessDoc?.requestedByUid));
  const closeEdit = () => setModalOpen(false);
  const updateField = (field, value) => setEditingEmployee((prev) => ({ ...prev, [field]: value }));
  const updatePermission = (key, value) => {
    if (EXCEPTION_KEYS.includes(key) && !canGrantScheduleExceptions) return;
    setEditingEmployee((prev) => {
      const accessRole = getAccessRole(prev || {});
      const nextPermissions = buildPlainWorkerPermissions({
        ...(prev || {}),
        accessRole,
        permissions: { ...(prev?.permissions || {}), [key]: Boolean(value) },
      });
      return {
        ...prev,
        accessRole,
        loopRole: accessRole,
        securityRole: accessRole,
        permissions: nextPermissions,
      };
    });
  };

  const updateAccessRole = (roleKey) => {
    const accessRole = getAccessRole({ accessRole: roleKey });
    const preset = getRolePermissionPreset(accessRole);
    setEditingEmployee((prev) => ({
      ...prev,
      accessRole,
      loopRole: accessRole,
      securityRole: accessRole,
      permissions: buildPlainWorkerPermissions({ accessRole, permissions: {...preset,...(!canGrantScheduleExceptions ? Object.fromEntries(EXCEPTION_KEYS.map(key=>[key,prev.permissions?.[key] === true])) : {})} }),
    }));
  };

  const togglePermissionAccounts = () => {
    setPermissionAccordionOpen((prev) => ({
      ...prev,
      accounts: !prev.accounts,
    }));
  };

  const togglePermissionGroupPanel = (groupKey) => {
    const safeKey = String(groupKey || "general");
    setPermissionAccordionOpen((prev) => ({
      ...prev,
      groups: {
        ...(prev.groups || {}),
        [safeKey]: !Boolean(prev.groups?.[safeKey]),
      },
    }));
  };

  const ensureWorkerInviteLink = async (employeeLike = editingEmployee, { copy = false, forceNew = true } = {}) => {
    if (!businessId || !employeeLike?.id) throw new Error("missing_worker_invite_context");
    const result = await manageWorkerAccess(businessId, employeeLike.id, 'invite');
    const {inviteId, token, expiresAt} = result;
    const url = buildWorkerInviteUrl({businessId, inviteId, token});
    setEditingEmployee(prev => prev?.id === employeeLike.id ? {...prev, accessVersion: 2, accessStatus: 'pending', inviteStatus: 'pending', activeInviteId: inviteId, inviteId, inviteExpiresAt: expiresAt} : prev);
    if (copy) {
      try { await navigator.clipboard.writeText(url); }
      catch (_) { window.prompt('Copia esta invitación. Solo puede usarse una vez:', url); }
    }
    return {...result, url, inviteEmail: employeeLike.email || ''};
  };

  const handleCopyInviteLink = async () => {
    if (!editingEmployee) return;
    setInviteCopying(true);
    try {
      await ensureWorkerInviteLink(editingEmployee, { copy: true });
    } catch (err) {
      console.error("worker invite link failed", err);
      alert("No se pudo generar/copiar la invitación. Revisa la consola.");
    } finally {
      setInviteCopying(false);
    }
  };


  const openAccessShareChooser = () => {
    if (!editingEmployee?.id) return;
    setAccessShareOpen(true);
    setAccessShareError('');
    setAccessShareMode("choose");
    setAccessShareCopied(false);
    setAccessShareSyncStatus(editingAccessState.key === "linked" ? "linked" : "pending");
  };

  const closeAccessShareModal = () => {
    accessShareRequestRef.current += 1;
    setAccessShareError('');
    setAccessShareOpen(false);
    setAccessShareMode("choose");
    setAccessShareLoading(false);
    setAccessShareCopied(false);
    setAccessShareUrl("");
    setAccessShareDataUrl("");
    setAccessShareSessionExpiresAt(null);
  };

  const prepareAccessShareLink = async ({ mode = "link", forceNew = false } = {}) => {
    if (!editingEmployee) return null;
    const requestId = ++accessShareRequestRef.current;
    const existingUrl = !forceNew && accessShareUrl && accessShareSessionExpiresAt > Date.now() ? accessShareUrl : '';
    setAccessShareError('');
    setAccessShareOpen(true);
    setAccessShareMode(mode);
    setAccessShareLoading(true);
    setAccessShareCopied(false);
    setAccessShareUrl("");
    setAccessShareDataUrl("");
    setAccessShareSyncStatus(editingAccessState.key === "linked" ? "linked" : "pending");
    if (!existingUrl) setAccessShareSessionExpiresAt(null);

    try {
      const result = existingUrl ? {url:existingUrl, expiresAt:accessShareSessionExpiresAt} : await ensureWorkerInviteLink(editingEmployee, { copy: false, forceNew });
      if (requestId !== accessShareRequestRef.current) return null;
      const url = result?.url || "";
      if (!url) throw new Error('missing_invite_url');
      const expiry = new Date(result.expiresAt).getTime();
      setAccessShareSessionExpiresAt(Number.isFinite(expiry) ? expiry : null);
      setAccessShareUrl(url);
      if (mode === "qr") {
        try {
          const qrDataUrl = await createWorkerInviteQrDataUrl(url);
          if (requestId !== accessShareRequestRef.current) return null;
          setAccessShareDataUrl(qrDataUrl);
        } catch (qrErr) {
          console.warn("WorkersSection: QR render failed", qrErr);
          setAccessShareDataUrl("");
        }
      }
      return result;
    } catch (err) {
      if (requestId !== accessShareRequestRef.current) return null;
      console.error("worker invite share failed", err);
      setAccessShareError(workerAccessErrorMessage(err));
      return null;
    } finally {
      if (requestId === accessShareRequestRef.current) setAccessShareLoading(false);
    }
  };

  const handleShowInviteQr = async () => {
    await prepareAccessShareLink({ mode: "qr" });
  };

  const handleOpenInviteLinkPanel = async () => {
    await prepareAccessShareLink({ mode: "link" });
  };

  const copyAccessShareUrl = async () => {
    if (!accessShareUrl) return;
    try {
      await navigator.clipboard.writeText(accessShareUrl);
      setAccessShareCopied(true);
      setTimeout(() => setAccessShareCopied(false), 2400);
    } catch (_) {
      window.prompt("Copia este link:", accessShareUrl);
    }
  };

  const handleEmailCurrentAccessLink = async () => {
    if (!editingEmployee) return;
    const email = normalizeWorkerAccessEmail(editingEmployee.email || editingEmployee.inviteEmail || editingEmployee.employeeEmail || "");
    if (!email) {
      alert("Agrega un correo opcional en General para enviarlo por email. También puedes copiar el link.");
      return;
    }
    const url = accessShareUrl || (await prepareAccessShareLink({ mode: "link", forceNew: true }))?.url || "";
    if (!url) return;
    const subject = encodeURIComponent("Tu acceso a Loop Reservas");
    const body = encodeURIComponent(`Hola ${editingWorkerName},\n\nTe comparto tu acceso a Loop Reservas. Abre este link para conectarte al equipo:\n\n${url}\n\nEste link es seguro, de un solo uso y expira automáticamente.`);
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
  };

  const handleEmailInviteLink = async () => {
    if (!editingEmployee) return;
    const email = normalizeWorkerAccessEmail(editingEmployee.email || editingEmployee.inviteEmail || editingEmployee.employeeEmail || "");
    if (!email) {
      alert("Agrega un correo opcional si quieres enviarlo por email. También puedes copiar el link o mostrar el QR.");
      return;
    }
    setInviteCopying(true);
    try {
      const result = await ensureWorkerInviteLink(editingEmployee, { copy: false, forceNew: true });
      const subject = encodeURIComponent("Tu acceso a Loop Reservas");
      const body = encodeURIComponent(`Hola ${editingWorkerName},\n\nTe comparto tu acceso a Loop Reservas. Abre este link para conectarte al equipo:\n\n${result.url}\n\nEste link es seguro, de un solo uso y expira automáticamente.`);
      window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
    } catch (err) {
      console.error("worker email invite failed", err);
      alert("No se pudo preparar el correo. Revisa la consola.");
    } finally {
      setInviteCopying(false);
    }
  };

  const handleQuickCopyInviteLink = async (employeeLike) => {
    if (!employeeLike) return;
    const employeeId = String(employeeLike.id || "");
    setQuickInviteCopyingId(employeeId);
    try {
      await ensureWorkerInviteLink(employeeLike, { copy: true });
    } catch (err) {
      console.error("worker quick invite link failed", err);
      alert("No se pudo generar/copiar el link seguro. Revisa la consola.");
    } finally {
      setQuickInviteCopyingId("");
    }
  };

  const handleWhatsAppInviteStandby = (employeeLike = editingEmployee) => {
    const employeeName = String(employeeLike?.name || "este trabajador").trim() || "este trabajador";
    const phone = employeeLike?.phoneDisplay || employeeLike?.phoneE164 || employeeLike?.phoneNormalized || employeeLike?.whatsapp || "sin WhatsApp guardado";
    const phoneStatus = workerPhoneStatusCopy(employeeLike || {});
    const prefix = phoneStatus.tone === "missing"
      ? "Todavía falta agregar un WhatsApp válido."
      : "WhatsApp ya está preparado como canal futuro.";
    alert(`${prefix}

Trabajador: ${employeeName}
WhatsApp: ${phone}

WhatsApp está marcado como Próximamente. Mientras tanto, usa el link seguro de un solo uso. El teléfono queda guardado solo como contacto, no como identidad de acceso.`);
  };

  const handleResetWorkerAccess = async () => {
    if (!editingEmployee || !businessId) return;
    if (!window.confirm('¿Desvincular esta cuenta? Perderá el acceso. Sus reservas y permisos se conservan. Después podrás compartir una nueva invitación.')) return;
    setInviteCopying(true);
    try {
      const result = await manageWorkerAccess(businessId, editingEmployee.id, 'unlink');
      setEditingEmployee(prev => ({...prev, ...result, userUid: null, authUid: null, linkedUserId: null, linkedUserUid: null, authEmail: null, inviteStatus: 'revoked', activeInviteId: null}));
      setWorkerConnections([]);
    } catch (err) { setWorkerConnectionsError(err.message || 'No pudimos desvincular la cuenta.'); }
    finally { setInviteCopying(false); }
  };

  const changeWorkerAccessStatus = async () => {
    if (!editingEmployee?.id) return;
    const operation = editingEmployee.accessStatus === 'suspended' ? 'resume' : 'suspend';
    if (operation === 'suspend' && !window.confirm('¿Suspender el acceso? No podrá entrar a la agenda hasta que lo reactives. Sus reservas se conservan.')) return;
    setInviteCopying(true);
    try {
      const result = await manageWorkerAccess(businessId, editingEmployee.id, operation);
      setEditingEmployee(prev => ({...prev, ...result}));
    } catch (err) { setWorkerConnectionsError(err.message || 'No pudimos cambiar el acceso.'); }
    finally { setInviteCopying(false); }
  };

  const loadWorkerConnections = async (employeeLike = editingEmployee) => {
    if (!businessId || !employeeLike?.id) {
      setWorkerConnections([]);
      return [];
    }

    setWorkerConnectionsLoading(true);
    setWorkerConnectionsError("");

    try {
      const employeeId = String(employeeLike.id);
      const rows = [];

      const pushEmployeePrimary = () => {
        const uid = firstWorkerConnectionValue(
          employeeLike.userUid,
          employeeLike.authUid,
          employeeLike.linkedUserId,
          employeeLike.linkedUserUid
        );
        const email = normalizeWorkerAccessEmail(firstWorkerConnectionValue(
          employeeLike.authEmail,
          employeeLike.authEmailLower,
          employeeLike.loginEmail,
          employeeLike.loginEmailLower
        ));
        if (!uid && !email) return;
        rows.push(buildWorkerConnectionRow(uid ? "uid" : "email", uid || email, {
          uid,
          email,
          authEmail: email,
          employeeId,
          employeeName: employeeLike.name,
          workerName: employeeLike.name,
          active: true,
          accessStatus: employeeLike.accessStatus || "linked",
          linkedAt: employeeLike.linkedAt || employeeLike.updatedAt || null,
        }, employeeLike));
      };

      pushEmployeePrimary();

      try {
        const accessSnap = await getDocs(collection(db, `businesses/${businessId}/workerAccess`));
        accessSnap.forEach((docSnap) => {
          const data = docSnap.data() || {};
          if (data.active === false) return;
          if (String(data.employeeId || data.workerId || "") !== employeeId) return;
          rows.push(buildWorkerConnectionRow("uid", docSnap.id, data, employeeLike));
        });
      } catch (uidErr) {
        console.warn("WorkersSection: workerAccess connections read skipped", uidErr);
      }

      try {
        const emailSnap = await getDocs(collection(db, `businesses/${businessId}/workerAccessEmails`));
        emailSnap.forEach((docSnap) => {
          const data = docSnap.data() || {};
          if (data.active === false) return;
          if (String(data.employeeId || data.workerId || "") !== employeeId) return;
          rows.push(buildWorkerConnectionRow("email", docSnap.id, data, employeeLike));
        });
      } catch (emailErr) {
        console.warn("WorkersSection: workerAccessEmails connections read skipped", emailErr);
      }

      const normalized = dedupeWorkerConnections(rows);
      setWorkerConnections(normalized);
      return normalized;
    } catch (err) {
      console.error("WorkersSection: worker connections load failed", err);
      setWorkerConnectionsError("No pudimos cargar las cuentas conectadas. Intenta actualizar.");
      setWorkerConnections([]);
      return [];
    } finally {
      setWorkerConnectionsLoading(false);
    }
  };

  const removeWorkerConnection = async (connection) => {
    if (!connection) return;
    // A worker has one canonical account. Retire all old mirrors together.
    await handleResetWorkerAccess();
  };

  const addBlockedRange = (from, to) => {
    if (!from || !to) return;
    const a = from <= to ? from : to;
    const b = from <= to ? to : from;
    setEditingEmployee((prev) => {
      const cur = Array.isArray(prev.blockedRanges) ? prev.blockedRanges.slice() : [];
      if (!cur.some((r) => r.from === a && r.to === b)) cur.push({ from: a, to: b });
      cur.sort((x, y) => (x.from < y.from ? -1 : x.from > y.from ? 1 : 0));
      return { ...prev, blockedRanges: cur, __newBlockedFrom: "", __newBlockedTo: "" };
    });
  };

  const removeBlockedRange = (range) => {
    setEditingEmployee((prev) => {
      const cur = Array.isArray(prev.blockedRanges)
        ? prev.blockedRanges.filter((r) => !(r.from === range.from && r.to === range.to))
        : [];
      return { ...prev, blockedRanges: cur };
    });
  };

  const toggleService = (serviceId) => {
    setEditingEmployee((prev) => {
      const cur = Array.isArray(prev.services) ? prev.services.slice() : [];
      const exists = cur.includes(serviceId);
      const next = exists ? cur.filter((s) => s !== serviceId) : cur.concat(serviceId);
      return { ...prev, services: next };
    });
  };

  const assignAllServicesToWorker = () => {
    setEditingEmployee((prev) => ({
      ...prev,
      services: (services || []).map((s) => s.id),
    }));
  };

  const clearWorkerServices = () => {
    setEditingEmployee((prev) => ({ ...prev, services: [] }));
  };

  const handleSelectProjectFiles = async (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length || !editingEmployee) return;
    try {
      const prepared = await prepareImageFilesForUpload(selected, {
        purpose: "foto de proyecto del trabajador",
        namePrefix: "loop-worker-project",
        maxMb: 18,
      });
      if (!prepared.ok) {
        alert(prepared.message || "No pudimos cargar esas fotos. Usa JPG, PNG, WEBP o HEIC/HEIF.");
        return;
      }
      await onUploadEmployeeProjects(editingEmployee.id, prepared.files);
    } catch (err) {
      console.error("upload projects error", err);
      alert("Error subiendo proyectos. Revisa la consola.");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSave = async () => {
    if (!editingEmployee) return;
    setSaving(true);
    try {
      const cleanWorkerType = normalizeWorkerType(editingEmployee);
      const publicVisible = cleanWorkerType === "service" && isWorkerVisibleToCustomers({ ...editingEmployee, workerType: cleanWorkerType });
      const accessRole = getAccessRole(editingEmployee);
      const normalizedPermissions = buildPlainWorkerPermissions({
        ...editingEmployee,
        accessRole,
        permissions: editingEmployee.permissions || {},
      });
      const normalizedServices = normalizeWorkerStringArray(editingEmployee.services);
      const normalizedProjects = Array.isArray(editingEmployee.projects) ? editingEmployee.projects : [];
      const normalizedBlockedRanges = Array.isArray(editingEmployee.blockedRanges) ? editingEmployee.blockedRanges : [];
      const workerPhoneFields = buildWorkerPhoneIdentityFields(editingEmployee.phone || editingEmployee.phoneE164 || editingEmployee.whatsapp || "", editingEmployee);
      const payload = {
        id: editingEmployee.id,
        businessId,
        name: (editingEmployee.name || "").trim(),
        displayName: (editingEmployee.name || "").trim(),
        employeeName: (editingEmployee.name || "").trim(),
        workerName: (editingEmployee.name || "").trim(),
        ...(editingEmployee.photoUrl ? {
          photoUrl: editingEmployee.photoUrl,
          workerPhotoUrl: editingEmployee.photoUrl,
          employeePhotoUrl: editingEmployee.photoUrl,
          imageUrl: editingEmployee.photoUrl,
          avatarUrl: editingEmployee.photoUrl,
        } : {}),
        ...buildWorkerAccessFields(editingEmployee.email, editingEmployee.userUid || editingEmployee.authUid || editingEmployee.linkedUserId || null),
        ...workerPhoneFields,
        role: editingEmployee.role || "",
        accessRole,
        loopRole: accessRole,
        securityRole: accessRole,
        compensationVisibilityOverride: editingEmployee.compensationVisibilityOverride || "business_default",
        workerType: cleanWorkerType,
        staffType: cleanWorkerType,
        type: cleanWorkerType,
        isOperationalWorker: cleanWorkerType === "operational",
        operationalOnly: cleanWorkerType === "operational",
        hiddenFromBooking: !publicVisible,
        hiddenFromPublicProfile: !publicVisible,
        visibleToCustomers: publicVisible,
        showOnBusinessPage: publicVisible,
        canBeBookedOnline: publicVisible,
        availability: sanitizeAvailability(editingEmployee.availability),
        services: normalizedServices,
        serviceIds: normalizedServices,
        projects: normalizedProjects,
        blockedRanges: normalizedBlockedRanges,
        defaultCommissionType: normalizeCommissionType(editingEmployee.defaultCommissionType),
        defaultCommissionValue: normalizeCommissionValue(editingEmployee.defaultCommissionValue),
        commissionIncludesTips: Boolean(editingEmployee.commissionIncludesTips),
        commissionOnlyCompleted: editingEmployee.commissionOnlyCompleted !== false,
        permissions: normalizedPermissions,
        workerPermissions: normalizedPermissions,
        permissionsManualOverride: true,
        permissionsUpdatedAt: workerPersistNow(),
      };
      const savedAccess = await manageWorkerAccess(businessId, editingEmployee.id, 'save', payload);
      onAccessSaved({...editingEmployee, ...payload, ...savedAccess, accessVersion: 2});
      setLocalWorkerTypes(prev => ({...prev, [String(editingEmployee.id)]: cleanWorkerType}));

      closeEdit();
    } catch (err) {
      console.error("save employee failed", err);
      const message = String(err?.message || err || "");
      if (message.includes("permissions_write_verification_failed")) {
        alert("No se guardaron los permisos en Firestore. El sistema detectó que el documento del trabajador no cambió después de guardar. Revisa reglas de Firestore o el path businesses/{businessId}/employees/{employeeId}.");
      } else {
        alert("No se pudo guardar trabajador. Revisa la consola.");
      }
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (externalSaveSignalRef.current === externalSaveSignal) return;
    externalSaveSignalRef.current = externalSaveSignal;
    if (!externalSaveSignal) return;
    if (modalOpen && editingEmployee) {
      handleSave();
      return;
    }
    try { console.info("[WorkersSection] external save ignored: no open worker editor"); } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalSaveSignal]);

  const openWorkerPhotoCropper = (file, meta = {}) => {
    if (!file || !editingEmployee?.id) return;
    setWorkerPhotoCropFile(file);
    setWorkerPhotoCropMeta({
      sourceFormat: meta.sourceFormat || (meta.converted ? "heic-converted-jpeg" : "standard-image"),
      converted: Boolean(meta.converted),
      openedAt: Date.now(),
    });
    setWorkerPhotoStatus(meta.converted ? "HEIC convertido. Ajusta la imagen..." : "Ajusta la imagen...");
  };

  const closeWorkerPhotoCropper = () => {
    setWorkerPhotoCropFile(null);
    setWorkerPhotoCropMeta(null);
    setWorkerPhotoStatus("");
  };

  const handleWorkerPhotoCropComplete = async ({ blob, url } = {}) => {
    if (!editingEmployee?.id || !businessId) {
      closeWorkerPhotoCropper();
      return;
    }

    const sourceName = workerPhotoCropFile?.name || `worker-photo-${Date.now()}.jpg`;
    const uploadFile = blob
      ? new File([blob], `${workerPhotoBaseName({ name: sourceName })}.jpg`, { type: blob.type || "image/jpeg", lastModified: Date.now() })
      : workerPhotoCropFile;

    if (!uploadFile) {
      closeWorkerPhotoCropper();
      alert("No pudimos preparar la foto recortada. Intenta nuevamente.");
      return;
    }

    try {
      setWorkerPhotoStatus("Subiendo foto...");
      const uploadedUrl = await uploadWorkerPhotoDirectly({
        businessId,
        employeeId: editingEmployee.id,
        businessDoc,
        file: uploadFile,
        onProgress: (pct) => setWorkerPhotoStatus(pct > 0 ? `Subiendo foto ${pct}%...` : "Subiendo foto..."),
      });

      const nextEmployeePhotoState = {
        ...editingEmployee,
        photoUrl: uploadedUrl,
        photoURL: uploadedUrl,
        imageUrl: uploadedUrl,
        photoUpdatedAt: new Date().toISOString(),
      };

      setEditingEmployee((prev) => prev ? {
        ...prev,
        photoUrl: uploadedUrl,
        photoURL: uploadedUrl,
        imageUrl: uploadedUrl,
        photoUpdatedAt: nextEmployeePhotoState.photoUpdatedAt,
      } : prev);

      try {
        if (typeof onSaveEmployee === "function") await onSaveEmployee(nextEmployeePhotoState);
      } catch (syncErr) {
        console.warn("WorkersSection: parent photo sync skipped", syncErr);
      }

      setWorkerPhotoCropFile(null);
      setWorkerPhotoCropMeta(null);
      setWorkerPhotoStatus("Foto actualizada");
      setTimeout(() => setWorkerPhotoStatus(""), 1400);
    } catch (uploadErr) {
      console.error("WorkersSection: worker cropped photo upload failed", uploadErr);
      setWorkerPhotoStatus("");
      alert("No se pudo subir la foto del trabajador. Revisa sesión, Storage Rules o ownerUid del negocio.");
    }
  };

  const onSelectPhoto = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (e?.target) e.target.value = "";
    if (!f || !editingEmployee) return;

    setWorkerPhotoStatus(workerPhotoIsHeicLike(f) ? "Convirtiendo HEIC a JPG..." : "Validando imagen...");

    const prepared = await prepareWorkerPhotoFile(f, setWorkerPhotoStatus);
    if (!prepared.ok) {
      setWorkerPhotoStatus("");
      alert(prepared.message || "No pudimos cargar esa imagen. Usa JPG, PNG, WEBP o HEIC.");
      return;
    }

    // Unified worker-photo flow:
    // Agenda and BusinessEditPage now use the same cropper, rendered by portal above the worker editor modal.
    // This avoids the old split behavior: Agenda uploaded directly without cropper, while settings opened the parent cropper behind the modal.
    openWorkerPhotoCropper(prepared.file, {
      converted: prepared.converted,
      sourceFormat: prepared.converted ? "heic-converted-jpeg" : "standard-image",
    });
  };

  const handleAddWorkerClick = () => {
    setNewName("");
    setNewRole("");
    setNewPhone("");
    setNewWorkerType("service");
    setAddModalOpen(true);
  };

  const submitAddWorker = async () => {
    if (!newName || !newName.trim()) {
      alert("Ingrese nombre");
      return;
    }
    try {
      const cleanWorkerType = newWorkerType === "operational" ? "operational" : "service";
      const phoneFields = buildWorkerPhoneIdentityFields(newPhone);
      const businessDefaultAvailability = normalizeAvailabilityFromAny(businessAvailability);
      const initialWorkerAvailability = sanitizeAvailability(businessDefaultAvailability || defaultAvailabilityMonSat8to17());
      await onAddEmployee({
        accessVersion: 2,
        accessStatus: 'pending',
        inviteStatus: 'pending',
        name: newName.trim(),
        role: newRole || "",
        ...phoneFields,
        workerType: cleanWorkerType,
        staffType: cleanWorkerType,
        type: cleanWorkerType,
        isOperationalWorker: cleanWorkerType === "operational",
        operationalOnly: cleanWorkerType === "operational",
        hiddenFromBooking: cleanWorkerType === "operational",
        hiddenFromPublicProfile: cleanWorkerType === "operational",
        visibleToCustomers: cleanWorkerType === "service",
        showOnBusinessPage: cleanWorkerType === "service",
        canBeBookedOnline: cleanWorkerType === "service",
        availability: initialWorkerAvailability,
        schedule: initialWorkerAvailability,
        workingHours: initialWorkerAvailability,
        availabilitySource: businessDefaultAvailability ? "business_default_copy" : "standard_default_copy",
        defaultCommissionType: "none",
        defaultCommissionValue: 0,
        commissionIncludesTips: false,
        commissionOnlyCompleted: true,
        accessRole: "worker",
        loopRole: "worker",
        securityRole: "worker",
        compensationVisibilityOverride: "business_default",
        permissions: getRolePermissionPreset("worker"),
      });
      setAddModalOpen(false);
      setNewName("");
      setNewRole("");
      setNewPhone("");
      setNewWorkerType("service");
    } catch (e) {
      console.error("add worker failed", e);
      alert("No se pudo agregar trabajador. Revisa la consola.");
    }
  };

  const serviceNameById = (id) => {
    const s = services.find((x) => String(x.id) === String(id));
    if (s) return getServiceDisplayName(s);
    return looksLikeServiceCode(id) ? "" : String(id || "").trim();
  };

  const scheduleTotalsByDay = useMemo(() => {
    const out = {};
    const avail = ensureAvailabilityShape(editingEmployee?.availability);
    for (const d of DAYS) {
      const ranges = Array.isArray(avail[d.key]) ? avail[d.key] : [];
      out[d.key] = ranges.reduce((sum, r) => sum + minutesBetween(r.from, r.to), 0);
    }
    return out;
  }, [editingEmployee?.availability]);

  const setDayEnabled = (dayKey, enabled) => {
    setEditingEmployee((prev) => {
      const avail = ensureAvailabilityShape(prev?.availability);
      if (!enabled) {
        avail[dayKey] = [];
      } else {
        if (!avail[dayKey] || avail[dayKey].length === 0) {
          avail[dayKey] = [{ from: "10:00", to: "19:00" }];
        }
      }
      return { ...prev, availability: avail };
    });
  };

  const setDayRange = (dayKey, idx, key, value) => {
    setEditingEmployee((prev) => {
      const avail = ensureAvailabilityShape(prev?.availability);
      const list = Array.isArray(avail[dayKey]) ? avail[dayKey].slice() : [];
      if (!list[idx]) return prev;
      list[idx] = { ...list[idx], [key]: clampTimeString(value) };
      avail[dayKey] = list;
      return { ...prev, availability: avail };
    });
  };

  const addBreak = (dayKey) => {
    setEditingEmployee((prev) => {
      const avail = ensureAvailabilityShape(prev?.availability);
      const list = Array.isArray(avail[dayKey]) ? avail[dayKey].slice() : [];
      if (list.length < 2) list.push({ from: "13:00", to: "14:00" });
      avail[dayKey] = list;
      return { ...prev, availability: avail };
    });
  };

  const removeRangeAt = (dayKey, idx) => {
    setEditingEmployee((prev) => {
      const avail = ensureAvailabilityShape(prev?.availability);
      const list = Array.isArray(avail[dayKey]) ? avail[dayKey].slice() : [];
      avail[dayKey] = list.filter((_, i) => i !== idx);
      return { ...prev, availability: avail };
    });
  };

  const setAvailabilityQuick = (nextAvailability) => {
    setEditingEmployee((prev) => ({
      ...prev,
      availability: sanitizeAvailability(nextAvailability),
    }));
  };

  const applyStandardSchedule = () => {
    setAvailabilityQuick(defaultAvailabilityMonSat8to17());
    setOpenDayKey("");
  };

  const applyBusinessSchedule = () => {
    const next = normalizeAvailabilityFromAny(businessAvailability);
    if (!next) {
      alert("Todavía no hay un horario de empresa configurado en Disponibilidad.");
      return;
    }
    setAvailabilityQuick(next);
    setOpenDayKey("");
  };

  const copyMondayToWeekdays = () => {
    setEditingEmployee((prev) => {
      const avail = ensureAvailabilityShape(prev?.availability);
      const monday = Array.isArray(avail.monday) && avail.monday.length > 0 ? avail.monday.map((r) => ({ ...r })) : [{ from: "08:00", to: "17:00" }];
      return {
        ...prev,
        availability: sanitizeAvailability({
          ...avail,
          monday: monday.map((r) => ({ ...r })),
          tuesday: monday.map((r) => ({ ...r })),
          wednesday: monday.map((r) => ({ ...r })),
          thursday: monday.map((r) => ({ ...r })),
          friday: monday.map((r) => ({ ...r })),
        }),
      };
    });
    setOpenDayKey("");
  };

  const closeAllDays = () => {
    const closed = {};
    for (const d of DAYS) closed[d.key] = [];
    setAvailabilityQuick(closed);
    setOpenDayKey("");
  };

  const todayYmd = useMemo(() => ymdToday(), []);
  const blockedSplit = useMemo(() => splitBlockedRanges(editingEmployee?.blockedRanges, todayYmd), [editingEmployee?.blockedRanges, todayYmd]);

  const workerRows = useMemo(() => {
    return (employees || []).map((emp) => {
      const workerType = localWorkerTypes[String(emp.id)] || normalizeWorkerType(emp);
      const assignedServiceNames = Array.isArray(emp.services)
        ? emp.services.map((sid) => serviceNameById(sid)).filter(Boolean)
        : [];
      return { emp, workerType, assignedServiceNames };
    });
  }, [employees, localWorkerTypes, services]);

  const workerStats = useMemo(() => {
    const total = workerRows.length;
    const service = workerRows.filter((row) => row.workerType === "service").length;
    const operational = workerRows.filter((row) => row.workerType === "operational").length;
    const linked = workerRows.filter((row) => workerInviteAccessState(row.emp).key === "linked").length;
    const pendingAccess = workerRows.filter((row) => workerInviteAccessState(row.emp).key === "pending").length;
    const noAccess = workerRows.filter((row) => workerInviteAccessState(row.emp).key === "none").length;
    const expiredAccess = workerRows.filter((row) => workerInviteAccessState(row.emp).key === "expired").length;
    const legacyActive = workerRows.filter((row) => workerHasActiveLegacyAccess(row.emp)).length;
    const whatsappVerified = workerRows.filter((row) => workerPhoneStatusCopy(row.emp).tone === "verified").length;
    const whatsappReady = workerRows.filter((row) => ["ready", "pending"].includes(workerPhoneStatusCopy(row.emp).tone)).length;
    const whatsappMissing = workerRows.filter((row) => workerPhoneStatusCopy(row.emp).tone === "missing").length;
    const needsConfirmation = workerRows.filter((row) => {
      const migration = workerMigrationStatusCopy(row.emp);
      return ["needs_confirm", "pending_ready", "ready"].includes(migration.tone);
    }).length;
    return { total, service, operational, linked, pendingAccess, noAccess, expiredAccess, legacyActive, whatsappVerified, whatsappReady, whatsappMissing, needsConfirmation };
  }, [workerRows]);

  function buildWorkerWhatsAppBackfillPatch(employee = {}, options = {}) {
    const phoneSource =
      employee.phoneE164 ||
      employee.phoneNormalized ||
      employee.workerPhoneE164 ||
      employee.employeePhoneE164 ||
      employee.phone ||
      employee.whatsapp ||
      employee.workerPhone ||
      employee.employeePhone ||
      "";

    const phoneFields = buildWorkerPhoneIdentityFields(phoneSource, employee);
    const legacyAccessActive = workerHasActiveLegacyAccess(employee);
    const accessState = workerInviteAccessState(employee);
    const mergedForStatus = { ...employee, ...phoneFields };
    const migrationStatus = workerMigrationStatusCopy(mergedForStatus);
    const hasPhone = Boolean(phoneFields.phoneE164);
    const hasVerifiedPhone = phoneFields.phoneVerified === true || phoneFields.workerPhoneVerified === true;

    const patch = {
      ...phoneFields,
      legacyAccessActive,
      currentAccessProtected: legacyAccessActive,
      currentAccessType: accessState.key,
      whatsappMigrationStatus: migrationStatus.tone,
      whatsappMigrationLabel: migrationStatus.label,
      whatsappMigrationHelper: migrationStatus.helper,
      whatsappMigrationPhase: "worker_secure_link_foundation_3A2",
      identityMigrationVersion: 2,
      identityMigrationSource: "worker_secure_link_backfill",
      workerIdentityMode: legacyAccessActive ? "uid_or_legacy_access" : "secure_link_pending",
      workerPhoneRequiredSoon: false,
      whatsappInviteStatus: hasPhone ? "coming_soon" : "missing_phone",
      whatsappInviteReady: false,
      inviteChannel: employee.inviteChannel || "secure_link",
      workerInviteChannel: employee.workerInviteChannel || "secure_link",
      preferredInviteChannel: employee.preferredInviteChannel || "secure_link",
    };

    if (options.includeTimestamps === true) {
      patch.currentAccessProtectedAt = workerPersistNow();
      patch.whatsappMigrationUpdatedAt = workerPersistNow();
    }

    return patch;
  }

  function workerNeedsWhatsAppBackfill(employee = {}) {
    const patch = buildWorkerWhatsAppBackfillPatch(employee);
    const checks = [
      "legacyAccessActive",
      "currentAccessProtected",
      "currentAccessType",
      "whatsappMigrationStatus",
      "whatsappMigrationPhase",
      "identityMigrationVersion",
      "workerIdentityMode",
      "workerPhoneRequiredSoon",
      "whatsappInviteStatus",
      "whatsappInviteReady",
      "inviteChannel",
      "workerInviteChannel",
      "preferredInviteChannel",
      "phoneE164",
      "phoneDisplay",
      "phoneNormalized",
      "phoneCountryCode",
      "phoneDialCode",
      "phoneNational",
      "workerPhoneE164",
      "employeePhoneE164",
    ];

    return checks.some((key) => {
      const current = employee[key];
      const next = patch[key];
      if (current === undefined && next !== undefined) return true;
      if (typeof next === "boolean") return Boolean(current) !== next;
      return String(current ?? "") !== String(next ?? "");
    });
  }

  const workerBackfillPreview = useMemo(() => {
    const rows = workerRows || [];
    const candidates = rows.filter((row) => workerNeedsWhatsAppBackfill(row.emp));
    const withLegacyAccess = rows.filter((row) => workerHasActiveLegacyAccess(row.emp)).length;
    const withPhone = rows.filter((row) => workerPhoneStatusCopy(row.emp).tone !== "missing").length;
    const missingPhone = rows.filter((row) => workerPhoneStatusCopy(row.emp).tone === "missing").length;
    return {
      total: rows.length,
      candidates: candidates.length,
      withLegacyAccess,
      withPhone,
      missingPhone,
    };
  }, [workerRows]);

  const handleNormalizeWorkersWhatsAppIdentity = async () => {
    if (!businessId) {
      alert("No encontré el negocio para normalizar trabajadores.");
      return;
    }

    const rows = workerRows || [];
    if (!rows.length) {
      alert("Todavía no hay trabajadores para normalizar.");
      return;
    }

    const candidates = rows.filter((row) => workerNeedsWhatsAppBackfill(row.emp));
    if (!candidates.length) {
      setWorkerBackfillResult({ updated: 0, skipped: rows.length, failed: 0, at: new Date().toISOString() });
      alert("Tus trabajadores ya están normalizados para acceso seguro y WhatsApp próximamente.");
      return;
    }

    const ok = window.confirm(
      `Vamos a normalizar ${candidates.length} trabajador(es).\n\n` +
      "Esto NO quita accesos actuales por email o UID. Solo normaliza contacto, link seguro y estado de WhatsApp como Próximamente."
    );
    if (!ok) return;

    setNormalizingWorkers(true);
    setWorkerBackfillResult(null);

    let updated = 0;
    let failed = 0;
    const failures = [];

    try {
      for (const row of candidates) {
        const emp = row.emp || {};
        const employeeId = String(emp.id || emp.employeeId || "").trim();
        if (!employeeId) {
          failed += 1;
          failures.push("Trabajador sin ID");
          continue;
        }

        try {
          const patch = buildWorkerWhatsAppBackfillPatch(emp, { includeTimestamps: true });
          await workerSetDocByPath(`businesses/${businessId}/employees/${employeeId}`, {
            ...patch,
            updatedAt: workerPersistNow(),
          }, { merge: true });
          updated += 1;
        } catch (err) {
          failed += 1;
          failures.push(`${emp.name || employeeId}: ${err?.message || err}`);
          console.error("Worker access backfill failed", emp, err);
        }
      }

      setWorkerBackfillResult({
        updated,
        failed,
        skipped: Math.max(0, rows.length - candidates.length),
        failures: failures.slice(0, 5),
        at: new Date().toISOString(),
      });

      if (failed > 0) {
        alert(`Normalización completada con ${failed} error(es). Revisa la consola.`);
      } else {
        alert(`Listo: ${updated} trabajador(es) normalizados sin quitar accesos actuales.`);
      }
    } finally {
      setNormalizingWorkers(false);
    }
  };

  const filteredWorkerRows = useMemo(() => {
    const q = String(workerSearch || "").trim().toLowerCase();
    let rows = workerRows;
    if (workerFilter === "service") rows = rows.filter((row) => row.workerType === "service");
    if (workerFilter === "operational") rows = rows.filter((row) => row.workerType === "operational");
    if (workerFilter === "linked") rows = rows.filter((row) => workerInviteAccessState(row.emp).key === "linked");
    if (workerFilter === "pending_access") rows = rows.filter((row) => workerInviteAccessState(row.emp).key === "pending");
    if (workerFilter === "without_access") rows = rows.filter((row) => ["none", "expired"].includes(workerInviteAccessState(row.emp).key));

    if (q) {
      rows = rows.filter(({ emp, assignedServiceNames, workerType }) => {
        const haystack = [
          emp?.name,
          emp?.role,
          workerTypeCopy(workerType).badge,
          ...(Array.isArray(assignedServiceNames) ? assignedServiceNames : []),
        ].filter(Boolean).join(" ").toLowerCase();
        return haystack.includes(q);
      });
    }

    return rows;
  }, [workerRows, workerFilter, workerSearch]);

  const filteredWorkerServices = useMemo(() => {
    const q = String(workerServiceSearch || "").trim().toLowerCase();
    return (services || []).filter((service) => {
      const name = getServiceDisplayName(service).toLowerCase();
      return !q || name.includes(q);
    });
  }, [services, workerServiceSearch]);

  const selectedWorkerServiceCount = useMemo(() => {
    const selected = Array.isArray(editingEmployee?.services) ? editingEmployee.services : [];
    return selected.filter((sid) => (services || []).some((s) => String(s.id) === String(sid))).length;
  }, [editingEmployee?.services, services]);

  const selectedPhoneCountry = useMemo(
    () => detectPhoneCountry(editingEmployee?.phone),
    [editingEmployee?.phone]
  );

  const phoneNationalValue = useMemo(
    () => getPhoneNationalNumber(editingEmployee?.phone, selectedPhoneCountry),
    [editingEmployee?.phone, selectedPhoneCountry]
  );

  const updatePhoneCountry = (iso) => {
    const nextCountry = PHONE_COUNTRIES.find((c) => c.iso === iso) || PHONE_COUNTRIES[0];
    const national = getPhoneNationalNumber(editingEmployee?.phone, selectedPhoneCountry);
    updateField("phone", national ? `${nextCountry.dialCode} ${national}` : nextCountry.dialCode);
  };

  const updatePhoneNumber = (value) => {
    const clean = String(value || "").replace(/[^\d\s-]/g, "").replace(/\s+/g, " ").trimStart();
    updateField("phone", clean.trim() ? `${selectedPhoneCountry.dialCode} ${clean}` : "");
  };

  const updateCustomerVisibility = (visible) => {
    const cleanVisible = Boolean(visible) && normalizeWorkerType(editingEmployee) === "service";
    setEditingEmployee((prev) => ({
      ...prev,
      visibleToCustomers: cleanVisible,
      showOnBusinessPage: cleanVisible,
      hiddenFromPublicProfile: !cleanVisible,
      hiddenFromBooking: !cleanVisible,
      canBeBookedOnline: cleanVisible,
    }));
  };

  const editingAccessState = useMemo(() => workerInviteAccessState(editingEmployee || {}), [editingEmployee]);
  const editingHasProtectedLegacyAccess = useMemo(() => workerHasProtectedLegacyAccess(editingEmployee || {}), [editingEmployee]);
  const editingWorkerName = useMemo(() => {
    const name = String(editingEmployee?.name || "este trabajador").trim();
    return name || "este trabajador";
  }, [editingEmployee?.name]);

  const accessWizardMeta = useMemo(() => {
    const key = editingAccessState.key;
    const expiresLabel = editingAccessState.expiresAt ? formatWorkerInviteExpiry(editingAccessState.expiresAt) : "";

    if (key !== "linked" && editingHasProtectedLegacyAccess) {
      return {
        tone: "legacy",
        badge: "Protegido",
        title: `Sincroniza a ${editingWorkerName}`,
        subtitle: "Este trabajador conserva acceso anterior. Puedes conectarlo por UID sin quitarle el acceso actual.",
        primaryLabel: "Conectar cuenta trabajador",
        secondaryLabel: "",
        tinyNote: "Migración segura · sin perder acceso",
      };
    }

    if (key === "linked") {
      return {
        tone: "linked",
        badge: "Listo",
        title: `${editingWorkerName} ya tiene acceso`,
        subtitle: "Su cuenta está conectada con la empresa. Ya puedes ajustar permisos y agenda.",
        primaryLabel: "Ver permisos",
        secondaryLabel: "Cambiar acceso",
        tinyNote: "Conectado por cuenta segura",
      };
    }

    if (key === "pending") {
      return {
        tone: "pending",
        badge: "Esperando",
        title: `Acceso pendiente para ${editingWorkerName}`,
        subtitle: expiresLabel ? `Comparte el acceso. El link vence el ${expiresLabel}.` : "Elige cómo compartir el acceso para conectar su cuenta.",
        primaryLabel: "Conectar trabajador",
        secondaryLabel: "Crear nuevo link",
        tinyNote: "QR, link o WhatsApp · WhatsApp próximamente",
      };
    }

    if (key === "expired") {
      return {
        tone: "expired",
        badge: "Expirado",
        title: "El acceso venció",
        subtitle: "Elige un método para crear un acceso nuevo.",
        primaryLabel: "Conectar trabajador",
        secondaryLabel: "Revisar detalles",
        tinyNote: "QR, link o WhatsApp · WhatsApp próximamente",
      };
    }

    return {
      tone: "none",
      badge: "Sin acceso",
      title: `Conecta a ${editingWorkerName} con tu empresa`,
      subtitle: "Para darle permisos de agenda, primero conecta su cuenta a este perfil.",
      primaryLabel: "Conectar trabajador",
      secondaryLabel: "",
      tinyNote: "QR, link o WhatsApp · WhatsApp próximamente",
    };
  }, [editingAccessState.key, editingAccessState.expiresAt, editingHasProtectedLegacyAccess, editingWorkerName, inviteCopying]);

  const accessWizardSteps = useMemo(() => {
    const key = editingAccessState.key;
    if (key === "linked") {
      return [
        { number: "1", label: "Link creado", state: "done" },
        { number: "2", label: "Compartido", state: "done" },
        { number: "3", label: "Conectado", state: "done" },
      ];
    }
    if (key === "pending") {
      return [
        { number: "1", label: "Link creado", state: "done" },
        { number: "2", label: "Compartir", state: "active" },
        { number: "3", label: "Conectado", state: "waiting" },
      ];
    }
    if (key === "expired") {
      return [
        { number: "1", label: "Nuevo link", state: "active" },
        { number: "2", label: "Compartir", state: "waiting" },
        { number: "3", label: "Conectado", state: "waiting" },
      ];
    }
    return [
      { number: "1", label: "Crear link", state: "active" },
      { number: "2", label: "Compartir", state: "waiting" },
      { number: "3", label: "Conectado", state: "waiting" },
    ];
  }, [editingAccessState.key]);

  const accessShareRemainingMs = accessShareSessionExpiresAt ? accessShareSessionExpiresAt - accessShareNow : 0;
  const accessShareTimerLabel = accessShareSessionExpiresAt ? formatAccessCountdown(accessShareRemainingMs) : "10:00";
  const accessShareExpired = Boolean(accessShareSessionExpiresAt && accessShareRemainingMs <= 0);
  const accessShareStatus = accessShareStatusCopy(accessShareSyncStatus);



  return (
    <div className="workers-pro-shell space-y-4">

      <style>{`
        .workers-pro-shell,
        .space-y-4 {
          color: #0f172a;
        }

        .workers-pro-shell .bg-white,
        .space-y-4 .bg-white {
          border-radius: 28px !important;
          border-color: rgba(226,232,240,0.96) !important;
          box-shadow: 0 18px 48px rgba(15,23,42,0.07) !important;
          background: rgba(255,255,255,0.96) !important;
        }

        .workers-pro-shell input,
        .workers-pro-shell select,
        .workers-pro-shell textarea,
        .space-y-4 input,
        .space-y-4 select,
        .space-y-4 textarea {
          border-radius: 18px !important;
          border-color: #e2e8f0 !important;
          min-height: 48px;
          font-weight: 750;
        }

        .workers-pro-shell button,
        .space-y-4 button {
          border-radius: 16px !important;
          font-weight: 900 !important;
        }

        .workers-pro-shell .rounded-2xl,
        .space-y-4 .rounded-2xl {
          border-radius: 22px !important;
        }

        .workers-modal-overlay {
          scroll-padding-top: 96px;
        }

        .public-visibility-card {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          border: 1px solid rgba(226,232,240,.96);
          background: #ffffff;
          border-radius: 22px;
          padding: 15px;
          margin-top: 14px;
        }
        .public-visibility-card strong { display:block; color:#0f172a; font-size:14px; font-weight:950; }
        .public-visibility-card span { display:block; color:#64748b; font-size:12px; line-height:1.4; font-weight:750; margin-top:3px; }

        @media (max-width: 780px) {
          .workers-pro-shell .bg-white,
          .space-y-4 .bg-white {
            border-radius: 24px !important;
          }
        }


        /* =========================================================
           PHASE 31H — Workers Section Minimal Revamp
           Equipo: servicio vs operativo, lista compacta y acciones limpias.
           ========================================================= */
        .workers-modern-card {
          background: rgba(255,255,255,.96);
          border: 1px solid rgba(226,232,240,.96);
          border-radius: 28px;
          box-shadow: 0 18px 48px rgba(15,23,42,.065);
        }
        .workers-modern-hero {
          padding: 18px;
          display: grid;
          gap: 14px;
        }
        .workers-modern-hero-main {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 18px;
        }
        .workers-kicker {
          display: block;
          color: #00675f;
          font-size: 11px;
          font-weight: 950;
          letter-spacing: .16em;
          text-transform: uppercase;
          margin-bottom: 6px;
        }
        .workers-modern-hero h3 {
          margin: 0;
          color: #0f172a;
          font-size: 28px;
          line-height: 1;
          letter-spacing: -.055em;
          font-weight: 950;
        }
        .workers-modern-hero p {
          margin: 8px 0 0;
          color: #64748b;
          font-size: 13px;
          line-height: 1.4;
          font-weight: 750;
          max-width: 620px;
        }
        .workers-primary-action {
          min-height: 42px;
          border: 0;
          border-radius: 16px !important;
          background: #00675f;
          color: #fff;
          padding: 0 18px;
          font-size: 13px;
          font-weight: 950 !important;
          box-shadow: 0 12px 28px rgba(0,103,95,.18);
          white-space: nowrap;
        }
        .workers-stats-row {
          display: grid;
          grid-template-columns: repeat(6, minmax(0, 1fr)) minmax(180px, auto);
          gap: 8px;
        }
        .workers-stats-row > div {
          min-height: 44px;
          border: 1px solid #e2e8f0;
          border-radius: 18px;
          background: #fbfefd;
          padding: 8px 11px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .workers-stats-row strong {
          color: #00675f;
          font-size: 18px;
          font-weight: 950;
        }
        .workers-stats-row span {
          color: #64748b;
          font-size: 11px;
          font-weight: 850;
        }
        .workers-public-toggle {
          justify-content: space-between;
          background: #ffffff !important;
        }
        .workers-filter-tabs {
          height: 44px;
          display: inline-grid;
          grid-template-columns: repeat(6, auto);
          gap: 4px;
          padding: 4px;
          border: 1px solid #e2e8f0;
          border-radius: 999px;
          background: #f8fafc;
          justify-self: start;
        }
        .workers-filter-tabs button {
          border: 0;
          background: transparent;
          color: #64748b;
          padding: 0 14px;
          border-radius: 999px !important;
          font-size: 12px;
          font-weight: 950 !important;
          cursor: pointer;
        }
        .workers-filter-tabs button.active {
          background: #ffffff;
          color: #00675f;
          border: 1px solid rgba(0,103,95,.14);
          box-shadow: 0 8px 18px rgba(15,23,42,.045);
        }
        .workers-modern-list {
          display: grid;
          gap: 8px;
        }
        .workers-person-row {
          min-height: 76px;
          display: grid;
          grid-template-columns: 48px minmax(0,1fr) 110px 156px;
          gap: 12px;
          align-items: center;
          padding: 12px;
          border: 1px solid rgba(226,232,240,.96);
          border-radius: 24px;
          background: rgba(255,255,255,.97);
          box-shadow: 0 12px 30px rgba(15,23,42,.045);
        }
        .workers-person-row.is-disabled { opacity: .68; }
        .workers-avatar {
          width: 48px;
          height: 48px;
          border-radius: 18px;
          overflow: hidden;
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
        }
        .workers-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .workers-person-main { min-width: 0; }
        .workers-person-title {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
        }
        .workers-person-title strong {
          color: #0f172a;
          font-size: 14px;
          font-weight: 950;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .workers-person-title span {
          flex: 0 0 auto;
          border-radius: 999px;
          padding: 3px 8px;
          font-size: 9px;
          font-weight: 950;
          border: 1px solid;
        }
        .workers-person-title span.service {
          background: #f0fdfa;
          border-color: #99f6e4;
          color: #00796f;
        }
        .workers-person-title span.operational {
          background: #fffbeb;
          border-color: #fde68a;
          color: #b45309;
        }
        .workers-person-main p {
          margin: 3px 0 0;
          color: #64748b;
          font-size: 11px;
          font-weight: 800;
        }
        .workers-person-main small {
          display: block;
          margin-top: 3px;
          color: #475569;
          font-size: 11px;
          line-height: 1.3;
          font-weight: 750;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .workers-person-meta {
          display: grid;
          gap: 3px;
          justify-items: start;
        }
        .workers-person-meta span {
          color: #94a3b8;
          font-size: 10px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: .06em;
        }
        .workers-person-meta strong {
          color: #0f172a;
          font-size: 12px;
          font-weight: 950;
        }
        .workers-whatsapp-mini {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 22px;
          border-radius: 999px;
          padding: 0 8px;
          font-style: normal;
          font-size: 10px;
          font-weight: 950;
          white-space: nowrap;
          border: 1px solid #e2e8f0;
          background: #f8fafc;
          color: #64748b;
        }
        .workers-whatsapp-mini.verified {
          color: #00675f;
          background: #e8f8f5;
          border-color: rgba(0,103,95,.16);
        }
        .workers-whatsapp-mini.ready {
          color: #0f766e;
          background: #ecfeff;
          border-color: rgba(20,184,166,.18);
        }
        .workers-whatsapp-mini.pending {
          color: #92400e;
          background: #fffbeb;
          border-color: #fde68a;
        }
        .workers-whatsapp-status-card {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          margin-top: 8px;
          padding: 11px 12px;
          border-radius: 18px;
          border: 1px solid #e2e8f0;
          background: #f8fafc;
        }
        .workers-whatsapp-status-card strong {
          display: block;
          color: #0f172a;
          font-size: 12px;
          font-weight: 950;
        }
        .workers-whatsapp-status-card span {
          display: block;
          color: #64748b;
          font-size: 11px;
          font-weight: 750;
          line-height: 1.35;
          margin-top: 2px;
        }
        .workers-whatsapp-status-card.verified {
          background: #e8f8f5;
          border-color: rgba(0,103,95,.16);
        }
        .workers-whatsapp-status-card.ready {
          background: #ecfeff;
          border-color: rgba(20,184,166,.18);
        }
        .workers-whatsapp-status-card.pending {
          background: #fffbeb;
          border-color: #fde68a;
        }

        .workers-migration-summary {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          gap: 12px;
          align-items: center;
          padding: 13px 14px;
          border: 1px solid rgba(0,103,95,.12);
          border-radius: 22px;
          background:
            radial-gradient(circle at 0% 0%, rgba(0,103,95,.10), transparent 34%),
            linear-gradient(135deg, #ffffff 0%, #f5fffc 100%);
        }
        .workers-migration-summary strong {
          display: block;
          color: #0f172a;
          font-size: 13px;
          font-weight: 950;
        }
        .workers-migration-summary span {
          display: block;
          margin-top: 3px;
          color: #64748b;
          font-size: 12px;
          line-height: 1.4;
          font-weight: 760;
        }
        .workers-migration-summary em {
          font-style: normal;
          border-radius: 999px;
          padding: 6px 10px;
          background: #e8f8f5;
          color: #00675f;
          border: 1px solid rgba(0,103,95,.14);
          font-size: 11px;
          font-weight: 950;
          white-space: nowrap;
        }

        .workers-migration-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 8px;
          flex-wrap: wrap;
        }
        .workers-migration-secondary-action {
          border: 1px solid rgba(0,103,95,.22);
          background: #ffffff;
          color: #00675f;
          border-radius: 999px;
          min-height: 36px;
          padding: 0 14px;
          font-size: 12px;
          font-weight: 950;
          cursor: pointer;
          box-shadow: 0 8px 18px rgba(15,23,42,.06);
          transition: transform .16s ease, box-shadow .16s ease, background .16s ease;
        }
        .workers-migration-secondary-action:hover:not(:disabled) {
          background: #eefbf7;
          box-shadow: 0 12px 28px rgba(0,103,95,.11);
          transform: translateY(-1px);
        }
        .workers-migration-secondary-action:disabled {
          opacity: .55;
          cursor: not-allowed;
        }
        .workers-migration-backfill-result {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          margin-top: 10px;
          padding: 10px 12px;
          border: 1px solid rgba(0,103,95,.13);
          border-radius: 16px;
          background: rgba(236,253,245,.72);
          color: #0f172a;
          font-size: 12px;
          font-weight: 850;
        }
        .workers-migration-backfill-result strong {
          color: #00675f;
          font-weight: 950;
        }

        .workers-maintenance-details {
          justify-self: start;
          width: min(100%, 520px);
          border: 1px solid rgba(226,232,240,.92);
          border-radius: 20px;
          background: rgba(255,255,255,.74);
          box-shadow: 0 10px 26px rgba(15,23,42,.045);
          overflow: hidden;
        }
        .workers-maintenance-details summary {
          min-height: 42px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 0 14px;
          color: #475569;
          font-size: 12px;
          font-weight: 950;
          cursor: pointer;
          list-style: none;
        }
        .workers-maintenance-details summary::-webkit-details-marker { display: none; }
        .workers-maintenance-details summary:after {
          content: "⌄";
          color: #94a3b8;
          font-size: 14px;
          transform: translateY(-1px);
        }
        .workers-maintenance-details[open] summary:after {
          content: "⌃";
        }
        .workers-maintenance-body {
          display: grid;
          gap: 10px;
          padding: 12px 14px 14px;
          border-top: 1px solid rgba(226,232,240,.82);
        }
        .workers-maintenance-body p {
          margin: 0;
          color: #64748b;
          font-size: 12px;
          font-weight: 750;
          line-height: 1.4;
        }
        .workers-maintenance-body .workers-migration-actions {
          justify-content: flex-start;
        }
        .workers-access-tab {
          display: grid;
          gap: 14px;
          max-width: 900px;
          margin: 0 auto;
        }
        .workers-access-wizard {
          display: grid;
          gap: 16px;
        }
        .workers-access-simple-card {
          position: relative;
          display: grid;
          gap: 18px;
          border-radius: 28px;
          border: 1px solid rgba(0,103,95,.13);
          background:
            radial-gradient(circle at 92% 8%, rgba(0,103,95,.12), transparent 34%),
            linear-gradient(135deg,#ffffff 0%,#f6fffc 100%);
          padding: 22px;
          box-shadow: 0 20px 54px rgba(15,23,42,.075);
          overflow: hidden;
        }
        .workers-access-simple-card.is-pending {
          border-color: rgba(245,158,11,.36);
          background:
            radial-gradient(circle at 92% 8%, rgba(245,158,11,.14), transparent 34%),
            linear-gradient(135deg,#fffdf5 0%,#ffffff 100%);
        }
        .workers-access-simple-card.is-linked {
          border-color: rgba(16,185,129,.24);
          background:
            radial-gradient(circle at 92% 8%, rgba(16,185,129,.16), transparent 34%),
            linear-gradient(135deg,#effdf8 0%,#ffffff 100%);
        }
        .workers-access-simple-card.is-expired {
          border-color: rgba(251,146,60,.34);
          background:
            radial-gradient(circle at 92% 8%, rgba(251,146,60,.13), transparent 34%),
            linear-gradient(135deg,#fff7ed 0%,#ffffff 100%);
        }
        .workers-access-wizard-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
        }
        .workers-access-wizard-copy {
          display: grid;
          gap: 8px;
          min-width: min(420px, 100%);
          flex: 1 1 420px;
        }
        .workers-access-wizard-eyebrow {
          display: inline-flex;
          width: fit-content;
          align-items: center;
          gap: 8px;
          border: 1px solid rgba(0,103,95,.13);
          background: rgba(255,255,255,.78);
          border-radius: 999px;
          padding: 6px 10px;
          color: #00675f;
          font-size: 11px;
          font-weight: 950;
          letter-spacing: .08em;
          text-transform: uppercase;
        }
        .workers-access-wizard-eyebrow b {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 18px;
          height: 18px;
          border-radius: 999px;
          background: #00675f;
          color: #fff;
          font-size: 11px;
          letter-spacing: 0;
        }
        .workers-access-wizard-copy h3 {
          margin: 0;
          color: #0f172a;
          font-size: clamp(24px, 3vw, 34px);
          line-height: .98;
          font-weight: 950;
          letter-spacing: -.04em;
        }
        .workers-access-wizard-copy p {
          margin: 0;
          max-width: 560px;
          color: #64748b;
          font-size: 14px;
          line-height: 1.45;
          font-weight: 760;
        }
        .workers-access-wizard-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 10px;
          flex-wrap: wrap;
          flex: 0 1 340px;
        }
        .workers-access-wizard-primary,
        .workers-access-wizard-secondary {
          min-height: 52px;
          border-radius: 18px;
          padding: 0 20px;
          font-size: 14px;
          font-weight: 950;
          cursor: pointer;
          transition: transform .16s ease, box-shadow .16s ease, opacity .16s ease;
        }
        .workers-access-wizard-primary {
          border: 1px solid #00675f;
          background: #00675f;
          color: #fff;
          box-shadow: 0 18px 42px rgba(0,103,95,.18);
        }
        .workers-access-wizard-secondary {
          border: 1px solid rgba(226,232,240,.96);
          background: #fff;
          color: #334155;
        }
        .workers-access-wizard-primary:hover,
        .workers-access-wizard-secondary:hover {
          transform: translateY(-1px);
        }
        .workers-access-wizard-primary:disabled,
        .workers-access-wizard-secondary:disabled {
          opacity: .56;
          cursor: not-allowed;
          transform: none;
        }
        .workers-access-steps {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 10px;
        }
        .workers-access-step {
          position: relative;
          display: grid;
          gap: 8px;
          min-height: 98px;
          border-radius: 22px;
          border: 1px solid rgba(226,232,240,.86);
          background: rgba(255,255,255,.74);
          padding: 14px;
          align-content: center;
        }
        .workers-access-step.is-active {
          border-color: rgba(0,103,95,.28);
          background: #ffffff;
          box-shadow: 0 14px 34px rgba(15,23,42,.06);
        }
        .workers-access-step.is-done {
          border-color: rgba(16,185,129,.22);
          background: #ecfdf5;
        }
        .workers-access-step.is-waiting {
          color: #94a3b8;
        }
        .workers-access-step-number {
          width: 34px;
          height: 34px;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #f1f5f9;
          color: #64748b;
          font-weight: 950;
          font-size: 13px;
        }
        .workers-access-step.is-active .workers-access-step-number {
          background: #00675f;
          color: #fff;
        }
        .workers-access-step.is-done .workers-access-step-number {
          background: #10b981;
          color: #fff;
        }
        .workers-access-step-label {
          color: #0f172a;
          font-size: 13px;
          font-weight: 950;
          line-height: 1.15;
        }
        .workers-access-step.is-waiting .workers-access-step-label {
          color: #94a3b8;
        }
        .workers-access-mini-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          flex-wrap: wrap;
          border-radius: 18px;
          border: 1px solid rgba(0,103,95,.12);
          background: rgba(255,255,255,.72);
          padding: 10px 12px;
          color: #64748b;
          font-size: 12px;
          font-weight: 850;
        }
        .workers-access-mini-row strong {
          color: #00675f;
          font-weight: 950;
        }
        .workers-access-methods-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 12px;
        }
        .workers-access-method-card {
          display: grid;
          gap: 10px;
          align-content: start;
          min-height: 168px;
          border-radius: 24px;
          border: 1px solid rgba(226,232,240,.92);
          background: rgba(255,255,255,.84);
          padding: 16px;
          box-shadow: 0 12px 28px rgba(15,23,42,.045);
        }
        .workers-access-method-card.is-recommended {
          border-color: rgba(0,103,95,.22);
          background: linear-gradient(180deg,#ffffff,#f0fdfa);
        }
        .workers-access-method-card.is-disabled {
          opacity: .72;
          background: #f8fafc;
        }
        .workers-access-method-icon {
          width: 42px;
          height: 42px;
          border-radius: 16px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #ecfdf5;
          color: #00675f;
          font-size: 20px;
          font-weight: 950;
        }
        .workers-access-method-card h5 {
          margin: 0;
          color: #0f172a;
          font-size: 15px;
          font-weight: 950;
          letter-spacing: -.02em;
        }
        .workers-access-method-card p {
          margin: 0;
          color: #64748b;
          font-size: 12px;
          line-height: 1.35;
          font-weight: 760;
        }
        .workers-access-method-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 2px;
        }
        .workers-access-method-actions button {
          min-height: 36px;
          border-radius: 999px;
          border: 1px solid rgba(0,103,95,.16);
          background: #ffffff;
          color: #00675f;
          padding: 0 13px;
          font-size: 11px;
          font-weight: 950;
          cursor: pointer;
        }
        .workers-access-method-actions button.primary {
          background: #00675f;
          color: #fff;
          border-color: #00675f;
          box-shadow: 0 12px 26px rgba(0,103,95,.16);
        }
        .workers-access-method-actions button:disabled {
          cursor: not-allowed;
          opacity: .54;
          box-shadow: none;
        }
        .workers-access-pro-launcher {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 18px;
          flex-wrap: wrap;
          border-radius: 24px;
          border: 1px solid rgba(0,103,95,.14);
          background: rgba(255,255,255,.78);
          padding: 16px;
          box-shadow: 0 14px 34px rgba(15,23,42,.045);
        }
        .workers-access-pro-launcher-copy {
          display: grid;
          gap: 5px;
          min-width: min(440px,100%);
          flex: 1 1 440px;
        }
        .workers-access-pro-launcher-copy span {
          color: #00675f;
          font-size: 11px;
          font-weight: 950;
          letter-spacing: .08em;
          text-transform: uppercase;
        }
        .workers-access-pro-launcher-copy strong {
          color: #0f172a;
          font-size: 18px;
          font-weight: 950;
          letter-spacing: -.03em;
        }
        .workers-access-pro-launcher-copy p {
          margin: 0;
          color: #64748b;
          font-size: 12px;
          font-weight: 760;
          line-height: 1.4;
        }
        .workers-access-pro-launcher-btn {
          min-height: 48px;
          border-radius: 16px;
          border: 1px solid #00675f;
          background: #00675f;
          color: #fff;
          padding: 0 18px;
          font-size: 13px;
          font-weight: 950;
          box-shadow: 0 16px 34px rgba(0,103,95,.18);
          cursor: pointer;
        }

        .workers-connect-strip {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 18px;
          flex-wrap: wrap;
          border-radius: 24px;
          border: 1px solid rgba(226,232,240,.9);
          background: rgba(255,255,255,.92);
          padding: 16px;
          box-shadow: 0 16px 40px rgba(15,23,42,.06);
        }
        .workers-connect-strip.is-pending {
          border-color: rgba(239,68,68,.20);
          background:
            radial-gradient(circle at 92% 8%, rgba(239,68,68,.08), transparent 34%),
            #ffffff;
        }
        .workers-connect-strip.is-linked {
          border-color: rgba(16,185,129,.24);
          background:
            radial-gradient(circle at 92% 8%, rgba(16,185,129,.10), transparent 34%),
            #ffffff;
        }
        .workers-connect-strip.is-legacy {
          border-color: rgba(245,158,11,.28);
          background:
            radial-gradient(circle at 92% 8%, rgba(245,158,11,.12), transparent 36%),
            #fffdf7;
        }
        .workers-connect-main {
          display: flex;
          align-items: center;
          gap: 14px;
          min-width: min(420px, 100%);
          flex: 1 1 420px;
        }
        .workers-connect-icon {
          width: 48px;
          height: 48px;
          border-radius: 18px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          font-weight: 950;
          flex: 0 0 auto;
        }
        .workers-connect-strip.is-pending .workers-connect-icon {
          background: #fff1f2;
          color: #dc2626;
          border: 1px solid rgba(239,68,68,.18);
        }
        .workers-connect-strip.is-linked .workers-connect-icon {
          background: #ecfdf5;
          color: #059669;
          border: 1px solid rgba(16,185,129,.20);
        }
        .workers-connect-strip.is-legacy .workers-connect-icon {
          background: #fffbeb;
          color: #b45309;
          border: 1px solid rgba(245,158,11,.24);
        }
        .workers-connect-copy {
          display: grid;
          gap: 3px;
          min-width: 0;
        }
        .workers-connect-copy span {
          color: #00675f;
          font-size: 11px;
          font-weight: 950;
          letter-spacing: .08em;
          text-transform: uppercase;
        }
        .workers-connect-copy strong {
          color: #0f172a;
          font-size: 20px;
          font-weight: 950;
          letter-spacing: -.035em;
          line-height: 1.05;
        }
        .workers-connect-copy p {
          margin: 0;
          color: #64748b;
          font-size: 13px;
          font-weight: 760;
          line-height: 1.35;
        }
        .workers-connect-side {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 10px;
          flex-wrap: wrap;
        }
        .workers-connect-status {
          min-height: 48px;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          background: #f8fafc;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 8px 12px;
        }
        .workers-connect-status b {
          width: 26px;
          height: 26px;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          font-weight: 950;
          flex: 0 0 auto;
        }
        .workers-connect-status div {
          display: grid;
          gap: 1px;
        }
        .workers-connect-status strong {
          color: #0f172a;
          font-size: 12px;
          font-weight: 950;
          line-height: 1.1;
          white-space: nowrap;
        }
        .workers-connect-status span {
          color: #64748b;
          font-size: 11px;
          font-weight: 800;
          white-space: nowrap;
        }
        .workers-connect-status.is-pending {
          border-color: rgba(239,68,68,.18);
          background: #fff1f2;
        }
        .workers-connect-status.is-pending b {
          background: #dc2626;
          color: #fff;
        }
        .workers-connect-status.is-linked {
          border-color: rgba(16,185,129,.20);
          background: #ecfdf5;
        }
        .workers-connect-status.is-linked b {
          background: #059669;
          color: #fff;
        }
        .workers-connect-status.is-legacy {
          border-color: rgba(245,158,11,.24);
          background: #fffbeb;
        }
        .workers-connect-status.is-legacy b {
          background: #f59e0b;
          color: #fff;
        }
        .workers-connect-primary {
          min-height: 48px;
          border-radius: 16px;
          border: 1px solid #00675f;
          background: #00675f;
          color: #fff;
          padding: 0 18px;
          font-size: 13px;
          font-weight: 950;
          box-shadow: 0 16px 34px rgba(0,103,95,.18);
          cursor: pointer;
        }
        .workers-connect-primary:disabled {
          opacity: .58;
          cursor: not-allowed;
        }
        .workers-permissions-locked-note {
          border: 1px dashed rgba(148,163,184,.45);
          border-radius: 20px;
          background: rgba(248,250,252,.72);
          padding: 14px 16px;
          display: grid;
          gap: 3px;
        }
        .workers-permissions-locked-note strong {
          color: #0f172a;
          font-size: 14px;
          font-weight: 950;
        }
        .workers-permissions-locked-note span {
          color: #64748b;
          font-size: 12px;
          font-weight: 760;
          line-height: 1.35;
        }
        .workers-permissions-locked-note.is-legacy {
          border-color: rgba(245,158,11,.34);
          background:
            radial-gradient(circle at 96% 10%, rgba(245,158,11,.10), transparent 34%),
            #fffdf7;
        }
        .workers-permissions-locked-note.is-legacy strong {
          color: #92400e;
        }
        .workers-access-share-overlay {
          position: fixed;
          inset: 0;
          z-index: 2147483602;
          display: grid;
          place-items: center;
          padding: 24px;
          background: rgba(15,23,42,.54);
          backdrop-filter: blur(16px) saturate(1.05);
          -webkit-backdrop-filter: blur(16px) saturate(1.05);
        }

        .workers-access-share-shell {
          width: min(720px, calc(100vw - 32px));
          max-height: min(86vh, 760px);
          overflow: hidden;
          display: flex;
          flex-direction: column;
          border-radius: 32px;
          background:
            radial-gradient(circle at 18% 0%, rgba(0,103,95,.08), transparent 34%),
            linear-gradient(180deg, rgba(255,255,255,.99), rgba(255,255,255,.96));
          border: 1px solid rgba(226,232,240,.94);
          box-shadow: 0 36px 110px rgba(15,23,42,.30);
        }

        .workers-access-share-head {
          position: relative;
          padding: 30px 34px 18px;
          text-align: center;
          border-bottom: 1px solid rgba(226,232,240,.72);
        }

        .workers-access-share-head span {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 7px 12px;
          border-radius: 999px;
          background: rgba(0,103,95,.08);
          color: #00675f;
          border: 1px solid rgba(0,103,95,.13);
          font-size: 11px;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: .075em;
        }

        .workers-access-share-head strong {
          display: block;
          margin-top: 14px;
          color: #0f172a;
          font-size: clamp(25px, 3vw, 34px);
          font-weight: 950;
          letter-spacing: -0.04em;
          line-height: 1.04;
        }

        .workers-access-share-head p {
          max-width: 500px;
          margin: 10px auto 0;
          color: #64748b;
          font-size: 14px;
          font-weight: 760;
          line-height: 1.45;
        }

        .workers-access-share-close {
          position: absolute;
          top: 18px;
          right: 18px;
          width: 42px;
          height: 42px;
          border-radius: 999px;
          border: 1px solid rgba(203,213,225,.88);
          background: rgba(255,255,255,.92);
          color: #0f172a;
          font-size: 28px;
          line-height: 1;
          font-weight: 500;
          cursor: pointer;
          box-shadow: 0 10px 28px rgba(15,23,42,.08);
        }

        .workers-access-share-body {
          padding: 24px 34px 34px;
          overflow: auto;
        }

        .workers-access-share-progress {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-bottom: 18px;
        }

        .workers-access-share-progress-pill {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 34px;
          padding: 0 14px;
          border-radius: 999px;
          background: #e8f8f5;
          color: #00675f;
          border: 1px solid rgba(0,103,95,.14);
          font-size: 12px;
          font-weight: 950;
        }

        .workers-access-share-dots {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .workers-access-share-dots i {
          width: 9px;
          height: 9px;
          border-radius: 999px;
          background: #dbe5ee;
        }

        .workers-access-share-dots i.is-active { background: #00675f; }

        .workers-access-share-method-list {
          display: grid;
          gap: 12px;
          width: min(560px, 100%);
          margin: 0 auto;
        }

        .workers-access-share-option {
          width: 100%;
          min-height: 88px;
          border: 1px solid rgba(219,229,238,.98);
          border-radius: 22px;
          background: rgba(255,255,255,.96);
          display: grid;
          grid-template-columns: auto minmax(0,1fr) auto;
          align-items: center;
          gap: 16px;
          padding: 14px 16px;
          text-align: left;
          cursor: pointer;
          transition: transform .16s ease, border-color .16s ease, box-shadow .16s ease, background .16s ease;
        }

        .workers-access-share-option:hover {
          transform: translateY(-1px);
          border-color: rgba(0,103,95,.34);
          box-shadow: 0 18px 44px rgba(15,23,42,.08);
          background: #fff;
        }

        .workers-access-share-option.is-recommended {
          border-color: rgba(0,103,95,.22);
          background: linear-gradient(135deg, #f0fdfa, #ffffff 72%);
        }

        .workers-access-share-option.is-disabled {
          opacity: .55;
          cursor: not-allowed;
          background: #f8fafc;
        }

        .workers-access-share-option-icon {
          width: 56px;
          height: 56px;
          border-radius: 18px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #e8f8f5;
          color: #00675f;
          border: 1px solid rgba(0,103,95,.12);
          font-size: 27px;
          font-weight: 950;
          flex: 0 0 auto;
        }

        .workers-access-share-option-copy {
          min-width: 0;
          display: grid;
          gap: 4px;
        }

        .workers-access-share-option-title {
          display: flex;
          align-items: center;
          gap: 9px;
          flex-wrap: wrap;
        }

        .workers-access-share-option-title h4 {
          margin: 0;
          color: #0f172a;
          font-size: 16px;
          font-weight: 950;
          letter-spacing: -0.02em;
        }

        .workers-access-share-option-copy p {
          margin: 0;
          color: #64748b;
          font-size: 13px;
          font-weight: 760;
          line-height: 1.35;
        }

        .workers-access-share-option-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 24px;
          padding: 0 10px;
          border-radius: 999px;
          background: #e8f8f5;
          color: #00675f;
          border: 1px solid rgba(0,103,95,.13);
          font-size: 11px;
          font-weight: 950;
        }

        .workers-access-share-option-badge.is-muted {
          background: #f1f5f9;
          color: #94a3b8;
          border-color: #e2e8f0;
        }

        .workers-access-share-option-arrow {
          color: #0f172a;
          font-size: 26px;
          font-weight: 400;
        }

        .workers-access-share-bottom-note {
          width: min(560px, 100%);
          margin: 18px auto 0;
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 8px;
          min-height: 42px;
          border-radius: 16px;
          background: #f8fafc;
          color: #64748b;
          border: 1px solid #eef2f7;
          font-size: 12px;
          font-weight: 850;
        }

        .workers-access-share-active {
          display: grid;
          gap: 18px;
        }

        .workers-access-share-method-back {
          justify-self: flex-start;
          height: 38px;
          padding: 0 14px;
          border-radius: 999px;
          border: 1px solid #dbe5ee;
          background: #fff;
          color: #334155;
          font-size: 13px;
          font-weight: 900;
          cursor: pointer;
        }

        .workers-access-share-active-head {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 18px;
          padding: 4px 2px 2px;
        }

        .workers-access-share-active-head span {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: #00675f;
          font-size: 11px;
          font-weight: 950;
          letter-spacing: .06em;
          text-transform: uppercase;
        }

        .workers-access-share-active-head h3 {
          margin: 6px 0 0;
          color: #0f172a;
          font-size: clamp(22px, 3vw, 30px);
          font-weight: 950;
          letter-spacing: -0.035em;
        }

        .workers-access-share-active-head p {
          margin: 6px 0 0;
          color: #64748b;
          font-size: 13px;
          font-weight: 760;
        }

        .workers-access-share-timer-pill {
          min-width: 118px;
          height: 62px;
          border-radius: 20px;
          border: 1px solid rgba(0,103,95,.16);
          background: #f0fdfa;
          display: grid;
          place-items: center;
          color: #00675f;
          font-weight: 950;
          box-shadow: inset 0 1px 0 rgba(255,255,255,.75);
        }

        .workers-access-share-timer-pill small {
          display: block;
          margin-top: -8px;
          font-size: 10px;
          color: #64748b;
          letter-spacing: .03em;
          text-transform: uppercase;
        }

        .workers-access-share-timer-pill strong {
          display: block;
          font-size: 22px;
          letter-spacing: -0.03em;
        }

        .workers-access-share-workspace {
          display: grid;
          grid-template-columns: minmax(0, 1.1fr) minmax(250px, .9fr);
          gap: 16px;
          align-items: stretch;
        }

        .workers-access-share-mainpanel,
        .workers-access-share-sidepanel {
          border: 1px solid rgba(219,229,238,.98);
          border-radius: 24px;
          background: rgba(255,255,255,.96);
          box-shadow: 0 12px 34px rgba(15,23,42,.06);
        }

        .workers-access-share-mainpanel {
          padding: 18px;
          display: grid;
          gap: 14px;
        }

        .workers-access-share-sidepanel {
          padding: 18px;
          background: linear-gradient(135deg, #f0fdfa, #fff 72%);
        }

        .workers-access-share-qr-frame {
          min-height: 318px;
          border-radius: 24px;
          border: 1px dashed rgba(0,103,95,.22);
          background: #f8fafc;
          display: grid;
          place-items: center;
          padding: 22px;
        }

        .workers-access-share-qr-frame img {
          width: min(300px, 72vw);
          height: auto;
          display: block;
          border-radius: 18px;
          box-shadow: 0 18px 46px rgba(15,23,42,.14);
          background: #fff;
          padding: 12px;
        }

        .workers-access-share-link-box {
          display: grid;
          gap: 10px;
        }

        .workers-access-share-link-box label {
          color: #0f172a;
          font-size: 14px;
          font-weight: 950;
        }

        .workers-access-share-link-inputrow {
          display: grid;
          grid-template-columns: minmax(0,1fr) auto;
          gap: 10px;
          align-items: center;
          min-height: 56px;
          border-radius: 18px;
          border: 1px solid #dbe5ee;
          background: #fff;
          padding: 8px 8px 8px 14px;
        }

        .workers-access-share-link-value {
          min-width: 0;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
          color: #0f172a;
          font-size: 13px;
          font-weight: 850;
        }

        .workers-access-share-mini-copy {
          height: 40px;
          min-width: 42px;
          border-radius: 14px;
          border: 1px solid #dbe5ee;
          background: #f8fafc;
          color: #00675f;
          font-weight: 950;
          cursor: pointer;
        }

        .workers-access-share-side-title {
          margin: 0 0 14px;
          color: #0f172a;
          font-size: 16px;
          font-weight: 950;
          letter-spacing: -0.02em;
        }

        .workers-access-share-sync-card {
          display: grid;
          gap: 4px;
          padding: 14px;
          border-radius: 18px;
          background: rgba(255,255,255,.76);
          border: 1px solid rgba(219,229,238,.88);
          color: #0f172a;
        }

        .workers-access-share-sync-card strong {
          font-size: 14px;
          font-weight: 950;
        }

        .workers-access-share-sync-card span {
          color: #64748b;
          font-size: 12px;
          font-weight: 760;
          line-height: 1.35;
        }

        .workers-access-share-next-steps {
          margin-top: 14px;
          display: grid;
          gap: 12px;
        }

        .workers-access-share-next-step {
          display: grid;
          grid-template-columns: 26px minmax(0,1fr);
          gap: 10px;
          align-items: start;
          color: #334155;
          font-size: 12px;
          font-weight: 800;
        }

        .workers-access-share-next-step i {
          width: 26px;
          height: 26px;
          border-radius: 999px;
          background: #e8f8f5;
          color: #00675f;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-style: normal;
          font-size: 12px;
          font-weight: 950;
        }

        .workers-access-share-actions {
          display: flex;
          flex-wrap: wrap;
          justify-content: flex-end;
          gap: 10px;
          margin-top: 2px;
        }

        .workers-access-share-actions button {
          min-height: 48px;
          padding: 0 18px;
          border-radius: 16px;
          border: 1px solid #dbe5ee;
          background: #fff;
          color: #334155;
          font-size: 13px;
          font-weight: 950;
          cursor: pointer;
        }

        .workers-access-share-actions button.primary {
          border-color: #00675f;
          background: #00675f;
          color: white;
          box-shadow: 0 16px 36px rgba(0,103,95,.18);
        }

        .workers-access-share-actions button:disabled {
          opacity: .55;
          cursor: not-allowed;
        }

        @media (max-width: 760px) {
          .workers-access-share-overlay { padding: 12px; align-items: end; }
          .workers-access-share-shell {
            width: 100%;
            max-height: 92vh;
            border-radius: 28px 28px 0 0;
          }
          .workers-access-share-head { padding: 24px 22px 14px; }
          .workers-access-share-head strong { font-size: 24px; }
          .workers-access-share-body { padding: 18px 16px 24px; }
          .workers-access-share-option { min-height: 78px; grid-template-columns: auto minmax(0,1fr) auto; }
          .workers-access-share-workspace { grid-template-columns: 1fr; }
          .workers-access-share-active-head { align-items: flex-start; flex-direction: column; }
          .workers-access-share-timer-pill { width: 100%; }
          .workers-access-share-actions { display: grid; grid-template-columns: 1fr; }
          .workers-access-share-actions button { width: 100%; }
        }

        .workers-qr-modal-overlay {
          position: fixed;
          inset: 0;
          z-index: 2147483601;
          background: rgba(15,23,42,.54);
          backdrop-filter: blur(10px);
          display: grid;
          place-items: center;
          padding: 20px;
        }
        .workers-qr-modal-shell {
          width: min(440px, 100%);
          border-radius: 32px;
          border: 1px solid rgba(255,255,255,.78);
          background: #fff;
          box-shadow: 0 34px 90px rgba(15,23,42,.28);
          padding: 22px;
          display: grid;
          gap: 16px;
          text-align: center;
        }
        .workers-qr-modal-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          text-align: left;
        }
        .workers-qr-modal-head strong {
          display: block;
          color: #0f172a;
          font-size: 21px;
          font-weight: 950;
          letter-spacing: -.03em;
        }
        .workers-qr-modal-head span {
          display: block;
          margin-top: 4px;
          color: #64748b;
          font-size: 12px;
          font-weight: 760;
        }
        .workers-qr-close {
          width: 36px;
          height: 36px;
          border-radius: 999px;
          border: 1px solid #e2e8f0;
          background: #fff;
          color: #0f172a;
          font-weight: 950;
          cursor: pointer;
        }
        .workers-qr-box {
          min-height: 300px;
          border-radius: 28px;
          border: 1px solid #e2e8f0;
          background: #f8fafc;
          display: grid;
          place-items: center;
          padding: 18px;
        }
        .workers-qr-box img {
          width: min(300px, 100%);
          height: auto;
          border-radius: 18px;
          background: #fff;
          padding: 10px;
          box-shadow: 0 18px 42px rgba(15,23,42,.10);
        }
        .workers-qr-link-fallback {
          word-break: break-all;
          color: #00675f;
          font-size: 12px;
          font-weight: 850;
          text-align: left;
        }
        .workers-qr-actions {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }
        .workers-qr-actions button {
          min-height: 46px;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          background: #fff;
          color: #0f172a;
          font-weight: 950;
          cursor: pointer;
        }
        .workers-qr-actions button.primary {
          background: #00675f;
          border-color: #00675f;
          color: #fff;
        }
        .workers-access-tech-toggle {
          border: 1px solid #e2e8f0;
          background: #ffffff;
          border-radius: 22px;
          overflow: hidden;
        }
        .workers-access-tech-toggle > button {
          width: 100%;
          min-height: 54px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          border: 0;
          background: #ffffff;
          padding: 0 16px;
          color: #0f172a;
          font-weight: 950;
          cursor: pointer;
        }
        .workers-access-tech-grid {
          border-top: 1px solid #e2e8f0;
          padding: 14px;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 10px;
        }
        .workers-access-tech-grid > div {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 12px;
        }
        .workers-access-tech-grid span {
          display: block;
          color: #94a3b8;
          font-size: 10px;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: .08em;
        }
        .workers-access-tech-grid strong {
          display: block;
          margin-top: 4px;
          color: #0f172a;
          font-size: 12px;
          word-break: break-word;
        }
        .workers-connected-accounts-card {
          border: 1px solid rgba(0,103,95,.14);
          background:
            radial-gradient(circle at 92% 0%, rgba(0,188,170,.08), transparent 34%),
            #ffffff;
          border-radius: 24px;
          box-shadow: 0 16px 40px rgba(15,23,42,.05);
          overflow: hidden;
        }
        .workers-connected-accounts-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 14px;
          padding: 16px;
          border-bottom: 1px solid rgba(226,232,240,.85);
        }
        .workers-connected-accounts-head span {
          display: block;
          color: #00675f;
          font-size: 10px;
          font-weight: 950;
          letter-spacing: .08em;
          text-transform: uppercase;
        }
        .workers-connected-accounts-head strong {
          display: block;
          margin-top: 4px;
          color: #0f172a;
          font-size: 15px;
          font-weight: 950;
        }
        .workers-connected-accounts-head p {
          margin: 4px 0 0;
          color: #64748b;
          font-size: 12px;
          font-weight: 760;
          line-height: 1.4;
        }
        .workers-connected-refresh-btn {
          min-height: 36px;
          border-radius: 999px;
          border: 1px solid rgba(0,103,95,.18);
          background: #ffffff;
          color: #00675f;
          padding: 0 12px;
          font-size: 11px;
          font-weight: 950;
          cursor: pointer;
          white-space: nowrap;
        }
        .workers-connected-refresh-btn:disabled {
          opacity: .55;
          cursor: not-allowed;
        }
        .workers-connected-accounts-list {
          display: grid;
          gap: 10px;
          padding: 14px;
        }
        .workers-connected-account-row {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          gap: 12px;
          align-items: center;
          border: 1px solid rgba(226,232,240,.92);
          background: rgba(248,250,252,.74);
          border-radius: 18px;
          padding: 12px;
        }
        .workers-connected-account-main {
          min-width: 0;
          display: grid;
          gap: 7px;
        }
        .workers-connected-account-title {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          min-width: 0;
        }
        .workers-connected-account-title strong {
          color: #0f172a;
          font-size: 13px;
          font-weight: 950;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .workers-connected-account-badge {
          border-radius: 999px;
          padding: 4px 8px;
          background: #ecfdf5;
          color: #00675f;
          font-size: 10px;
          font-weight: 950;
          white-space: nowrap;
        }
        .workers-connected-account-badge.is-secondary {
          background: #f1f5f9;
          color: #475569;
        }
        .workers-connected-account-meta {
          display: flex;
          gap: 7px;
          flex-wrap: wrap;
          color: #64748b;
          font-size: 11px;
          font-weight: 780;
        }
        .workers-connected-account-meta span {
          border-radius: 999px;
          background: #ffffff;
          border: 1px solid rgba(226,232,240,.9);
          padding: 4px 8px;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .workers-connected-account-remove {
          min-height: 34px;
          border-radius: 999px;
          border: 1px solid rgba(239,68,68,.24);
          background: #fff7f7;
          color: #b91c1c;
          padding: 0 12px;
          font-size: 11px;
          font-weight: 950;
          cursor: pointer;
          white-space: nowrap;
        }
        .workers-connected-account-remove:disabled {
          opacity: .55;
          cursor: not-allowed;
        }
        .workers-connected-empty {
          padding: 16px;
          color: #64748b;
          font-size: 12px;
          font-weight: 780;
        }
        .workers-connected-error {
          margin: 0 14px 14px;
          border-radius: 16px;
          background: #fff7ed;
          border: 1px solid #fed7aa;
          color: #9a3412;
          padding: 10px 12px;
          font-size: 12px;
          font-weight: 850;
        }
        @media (max-width: 640px) {
          .workers-connected-accounts-head,
          .workers-connected-account-row {
            grid-template-columns: 1fr;
          }
          .workers-connected-account-remove,
          .workers-connected-refresh-btn {
            width: 100%;
          }
        }
        .workers-access-tab .workers-premium-field {
          display: grid;
          gap: 8px;
        }
        .workers-access-tab .workers-migration-card,
        .workers-access-tab .workers-invite-channel-card {
          margin: 0 !important;
        }
        .workers-person-access-line {
          display: inline-flex;
          align-items: center;
          width: fit-content;
          max-width: 100%;
          margin-top: 5px;
          border-radius: 999px;
          padding: 4px 8px;
          font-size: 10px;
          font-weight: 950;
          line-height: 1.1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          border: 1px solid #e2e8f0;
          background: #f8fafc;
          color: #64748b;
        }
        .workers-person-access-line.complete,
        .workers-person-access-line.ready,
        .workers-person-access-line.needs_confirm,
        .workers-person-access-line.pending_ready {
          border-color: rgba(0,103,95,.14);
          background: #e8f8f5;
          color: #00675f;
        }
        .workers-person-access-line.needs_phone,
        .workers-person-access-line.pending_missing,
        .workers-person-access-line.missing {
          border-color: #fde68a;
          background: #fffbeb;
          color: #92400e;
        }
        .workers-migration-card {
          grid-column: 1 / -1;
          display: grid;
          gap: 12px;
          padding: 14px;
          border-radius: 20px;
          border: 1px solid rgba(0,103,95,.13);
          background: linear-gradient(135deg, #ffffff 0%, #f6fffc 100%);
        }
        .workers-migration-card-head {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
          flex-wrap: wrap;
        }
        .workers-migration-card-head strong {
          display: block;
          color: #0f172a;
          font-size: 13px;
          font-weight: 950;
        }
        .workers-migration-card-head span {
          display: block;
          margin-top: 3px;
          color: #64748b;
          font-size: 12px;
          line-height: 1.4;
          font-weight: 760;
        }
        .workers-migration-pill {
          border-radius: 999px;
          padding: 6px 10px;
          background: #e8f8f5;
          color: #00675f;
          border: 1px solid rgba(0,103,95,.14);
          font-size: 11px;
          font-weight: 950;
          white-space: nowrap;
        }
        .workers-migration-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 8px;
        }
        .workers-migration-grid > div {
          border: 1px solid rgba(226,232,240,.9);
          background: rgba(255,255,255,.78);
          border-radius: 16px;
          padding: 10px;
          display: grid;
          gap: 2px;
        }
        .workers-migration-grid span {
          color: #94a3b8;
          font-size: 10px;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: .06em;
        }
        .workers-migration-grid strong {
          color: #0f172a;
          font-size: 12px;
          font-weight: 950;
        }
        .workers-standby-button {
          min-height: 36px;
          border-radius: 999px !important;
          border: 1px solid rgba(0,103,95,.16) !important;
          background: #ffffff !important;
          color: #00675f !important;
          padding: 0 13px !important;
          font-size: 11px !important;
          font-weight: 950 !important;
          opacity: .86;
        }

        .workers-invite-channel-card {
          grid-column: 1 / -1;
          display: grid;
          gap: 12px;
          padding: 14px;
          border-radius: 20px;
          border: 1px solid rgba(15,23,42,.08);
          background:
            radial-gradient(circle at 100% 0%, rgba(0,103,95,.09), transparent 32%),
            linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
          box-shadow: 0 12px 28px rgba(15,23,42,.045);
        }
        .workers-invite-channel-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }
        .workers-invite-channel-head strong {
          display: block;
          color: #0f172a;
          font-size: 13px;
          font-weight: 950;
        }
        .workers-invite-channel-head span {
          display: block;
          color: #64748b;
          font-size: 12px;
          font-weight: 760;
          line-height: 1.4;
          margin-top: 3px;
        }
        .workers-invite-channel-phase {
          border-radius: 999px;
          padding: 6px 10px;
          background: #f1f5f9;
          color: #475569 !important;
          border: 1px solid #e2e8f0;
          font-size: 10px !important;
          font-weight: 950 !important;
          margin-top: 0 !important;
        }
        .workers-invite-channel-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
        }
        .workers-invite-channel-option {
          border: 1px solid #e2e8f0;
          background: rgba(255,255,255,.82);
          border-radius: 18px;
          padding: 12px;
          display: grid;
          gap: 5px;
          align-content: start;
        }
        .workers-invite-channel-option.whatsapp {
          border-color: rgba(0,103,95,.14);
          background: linear-gradient(135deg, #f0fdfa 0%, #ffffff 100%);
        }
        .workers-invite-channel-option.whatsapp.missing {
          border-color: #fde68a;
          background: linear-gradient(135deg, #fffbeb 0%, #ffffff 100%);
        }
        .workers-invite-channel-option span {
          color: #94a3b8;
          font-size: 10px;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: .06em;
        }
        .workers-invite-channel-option strong {
          color: #0f172a;
          font-size: 13px;
          font-weight: 950;
        }
        .workers-invite-channel-option em {
          color: #64748b;
          font-size: 11px;
          line-height: 1.35;
          font-weight: 740;
          font-style: normal;
        }
        .workers-invite-channel-option button {
          justify-self: start;
          margin-top: 5px;
          min-height: 34px;
          border-radius: 999px;
          border: 1px solid rgba(0,103,95,.18);
          background: #ffffff;
          color: #00675f;
          padding: 0 12px;
          font-size: 11px;
          font-weight: 950;
          cursor: pointer;
        }
        .workers-invite-channel-option button:disabled {
          opacity: .5;
          cursor: not-allowed;
        }
        .workers-whatsapp-foundation-field .loop-phone-input {
          width: 100%;
        }
        .workers-add-phone-field .loop-phone-control {
          box-shadow: none;
        }
        .workers-person-actions {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 6px;
        }
        .workers-person-actions button {
          min-height: 34px;
          border-radius: 999px !important;
          font-size: 11px;
          font-weight: 950 !important;
          padding: 0 12px;
        }
        .workers-person-actions .edit {
          background: #ffffff;
          border: 1px solid #dbe7ef;
          color: #0f172a;
        }
        .workers-person-actions .delete {
          background: transparent;
          border: 0;
          color: #dc2626;
          padding: 0 4px;
        }

        .workers-person-actions-3a3 {
          grid-template-columns: repeat(2, minmax(0, 1fr)) auto auto;
          align-items: center;
        }
        .workers-person-actions .copy-link {
          background: #ffffff;
          border: 1px solid rgba(0,103,95,.18);
          color: #00675f;
        }
        .workers-person-actions .copy-link:disabled {
          opacity: .45;
          cursor: not-allowed;
        }
        .workers-person-actions .whatsapp-standby {
          background: #e8f8f5;
          border: 1px solid rgba(0,103,95,.14);
          color: #00675f;
        }
        .workers-person-actions .whatsapp-standby.missing {
          background: #fffbeb;
          border-color: #fde68a;
          color: #92400e;
        }
        .workers-person-actions .whatsapp-standby.verified {
          background: #dcfce7;
          border-color: #bbf7d0;
          color: #047857;
        }
        .workers-empty-state {
          border: 1px dashed #cbd5e1;
          background: #ffffff;
          border-radius: 24px;
          padding: 22px;
          display: grid;
          gap: 8px;
          color: #64748b;
          font-size: 13px;
          font-weight: 750;
        }
        .workers-empty-state strong {
          color: #0f172a;
          font-size: 16px;
          font-weight: 950;
        }
        .workers-empty-state button {
          justify-self: start;
          min-height: 38px;
          border: 0;
          background: #00675f;
          color: #fff;
          padding: 0 14px;
          border-radius: 999px !important;
        }
        .workers-add-type {
          border: 1px solid #e2e8f0;
          border-radius: 20px;
          padding: 12px;
          background: #f8fafc;
        }
        .workers-add-type label {
          display: block;
          color: #0f172a;
          font-size: 12px;
          font-weight: 950;
          margin-bottom: 8px;
        }
        .workers-add-type > div {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 6px;
        }
        .workers-add-type button {
          min-height: 38px;
          border: 1px solid #e2e8f0;
          background: #fff;
          color: #64748b;
          border-radius: 14px !important;
          font-size: 12px;
          font-weight: 950 !important;
        }
        .workers-add-type button.active {
          border-color: rgba(0,103,95,.22);
          background: #f0fdfa;
          color: #00675f;
        }
        .workers-add-type p {
          margin: 8px 0 0;
          color: #64748b;
          font-size: 11px;
          line-height: 1.35;
          font-weight: 750;
        }

        @media (max-width: 980px) {
          .workers-stats-row {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .workers-person-row {
            grid-template-columns: 48px minmax(0,1fr);
          }
          .workers-person-meta,
          .workers-person-actions {
            grid-column: 2;
          }
          .workers-person-actions {
            grid-template-columns: 1fr auto;
          }
        }
        @media (max-width: 640px) {
          .workers-modern-hero-main {
            display: grid;
          }
          .workers-primary-action {
            justify-self: start;
          }
          .workers-stats-row {
            grid-template-columns: 1fr;
          }
          .workers-filter-tabs {
            width: 100%;
            grid-template-columns: repeat(3, 1fr);
          }
          .workers-person-row {
            grid-template-columns: 42px minmax(0,1fr);
            padding: 10px;
          }
          .workers-avatar {
            width: 42px;
            height: 42px;
            border-radius: 15px;
          }
        }


        /* =========================================================
           PHASE 31J — Worker Compensation Minimal Polish
           Compact commission setup without oversized hero cards.
           ========================================================= */
        .workers-compensation-tab {
          min-height: 100%;
        }
        .workers-compensation-shell {
          display: grid;
          gap: 12px;
          max-width: 980px;
          margin: 0 auto;
        }
        .workers-compensation-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 16px 18px;
          border: 1px solid rgba(226,232,240,.96);
          background:
            radial-gradient(circle at 0% 0%, rgba(0,103,95,.055), transparent 32%),
            rgba(255,255,255,.96);
          border-radius: 24px;
          box-shadow: 0 12px 30px rgba(15,23,42,.04);
        }
        .workers-compensation-header-copy {
          display: grid;
          gap: 3px;
          min-width: 0;
        }
        .workers-compensation-kicker {
          color: #00675f;
          font-size: 10px;
          font-weight: 950;
          letter-spacing: .16em;
          text-transform: uppercase;
        }
        .workers-compensation-header h3 {
          margin: 0;
          color: #0f172a;
          font-size: clamp(20px, 1.8vw, 26px);
          line-height: 1;
          letter-spacing: -.045em;
          font-weight: 950;
        }
        .workers-compensation-header p {
          margin: 0;
          color: #64748b;
          font-size: 12.5px;
          line-height: 1.38;
          font-weight: 760;
        }
        .workers-compensation-status-pill {
          min-width: 190px;
          border: 1px solid rgba(0,103,95,.14);
          background: #f0fdfa;
          color: #00675f;
          border-radius: 18px;
          padding: 10px 12px;
          text-align: right;
          display: grid;
          gap: 2px;
        }
        .workers-compensation-status-pill span {
          color: #64748b;
          font-size: 10px;
          font-weight: 950;
          letter-spacing: .12em;
          text-transform: uppercase;
        }
        .workers-compensation-status-pill strong {
          color: #00675f;
          font-size: 13px;
          font-weight: 950;
          line-height: 1.12;
        }
        .workers-compensation-panel,
        .workers-compensation-rules-panel {
          border: 1px solid rgba(226,232,240,.96);
          background: rgba(255,255,255,.96);
          border-radius: 24px;
          box-shadow: 0 12px 30px rgba(15,23,42,.04);
        }
        .workers-compensation-panel {
          padding: 16px;
          display: grid;
          gap: 14px;
        }
        .workers-compensation-panel-head,
        .workers-compensation-rules-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
        }
        .workers-compensation-panel-head h4,
        .workers-compensation-rules-head h4 {
          margin: 0;
          color: #0f172a;
          font-size: 15px;
          line-height: 1.15;
          font-weight: 950;
          letter-spacing: -.02em;
        }
        .workers-compensation-panel-head p,
        .workers-compensation-rules-head p {
          margin: 4px 0 0;
          color: #64748b;
          font-size: 12px;
          line-height: 1.4;
          font-weight: 760;
        }
        .workers-compensation-segmented {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 6px;
          padding: 5px;
          border: 1px solid #e2e8f0;
          background: #f8fafc;
          border-radius: 18px;
        }
        .workers-compensation-segmented button {
          min-height: 44px;
          border: 0;
          border-radius: 14px;
          background: transparent;
          color: #64748b;
          font-size: 12px;
          font-weight: 950;
          cursor: pointer;
          transition: background .16s ease, color .16s ease, box-shadow .16s ease, transform .16s ease;
        }
        .workers-compensation-segmented button:active {
          transform: scale(.99);
        }
        .workers-compensation-segmented button.is-active {
          background: #ffffff;
          color: #00675f;
          box-shadow: 0 10px 24px rgba(15,23,42,.075);
        }
        .workers-compensation-form-grid {
          display: grid;
          grid-template-columns: minmax(260px, .72fr) minmax(0, 1fr);
          gap: 12px;
          align-items: stretch;
        }
        .workers-compensation-field,
        .workers-compensation-example-box,
        .workers-compensation-empty-note {
          border: 1px solid #e2e8f0;
          background: #ffffff;
          border-radius: 20px;
          padding: 14px;
        }
        .workers-compensation-field {
          display: grid;
          gap: 8px;
        }
        .workers-compensation-field span,
        .workers-compensation-example-box span,
        .workers-compensation-empty-note span {
          color: #64748b;
          font-size: 11px;
          font-weight: 900;
          letter-spacing: .02em;
        }
        .workers-compensation-amount {
          min-height: 54px;
          border: 1px solid #dbe5ee;
          border-radius: 16px;
          display: grid;
          grid-template-columns: auto minmax(0, 1fr) auto;
          align-items: center;
          gap: 8px;
          padding: 0 12px;
          background: #ffffff;
        }
        .workers-compensation-amount input {
          border: 0 !important;
          box-shadow: none !important;
          outline: 0 !important;
          min-height: 50px !important;
          border-radius: 0 !important;
          padding: 0 !important;
          color: #0f172a;
          font-size: 24px;
          line-height: 1;
          font-weight: 950;
          letter-spacing: -.04em;
          background: transparent !important;
          min-width: 0;
        }
        .workers-compensation-amount em {
          font-style: normal;
          color: #00675f;
          font-size: 18px;
          font-weight: 950;
        }
        .workers-compensation-example-box {
          display: grid;
          align-content: center;
          gap: 8px;
          background: linear-gradient(135deg, #ffffff 0%, #f8fffd 100%);
        }
        .workers-compensation-example-box strong {
          color: #0f172a;
          font-size: 14px;
          line-height: 1.32;
          font-weight: 900;
        }
        .workers-compensation-empty-note {
          display: grid;
          gap: 6px;
          background: #f8fafc;
        }
        .workers-compensation-empty-note strong {
          color: #0f172a;
          font-size: 14px;
          font-weight: 950;
        }
        .workers-compensation-empty-note p {
          margin: 0;
          color: #64748b;
          font-size: 12px;
          line-height: 1.42;
          font-weight: 760;
        }
        .workers-compensation-rules-panel {
          padding: 14px;
          display: grid;
          gap: 12px;
        }
        .workers-compensation-rules-list {
          display: grid;
          gap: 8px;
        }
        .workers-compensation-rule-row {
          width: 100%;
          min-height: 58px;
          border: 1px solid #e2e8f0;
          background: #ffffff;
          color: #0f172a;
          border-radius: 18px;
          padding: 12px;
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          align-items: center;
          gap: 12px;
          text-align: left;
          cursor: pointer;
          transition: border-color .16s ease, background .16s ease, transform .16s ease;
        }
        .workers-compensation-rule-row:active {
          transform: scale(.99);
        }
        .workers-compensation-rule-row.is-active {
          border-color: rgba(0,103,95,.22);
          background: #f0fdfa;
        }
        .workers-compensation-rule-copy {
          display: grid;
          gap: 2px;
          min-width: 0;
        }
        .workers-compensation-rule-copy strong {
          color: #0f172a;
          font-size: 13px;
          font-weight: 950;
          line-height: 1.15;
        }
        .workers-compensation-rule-copy span {
          color: #64748b;
          font-size: 11.5px;
          line-height: 1.35;
          font-weight: 760;
        }
        .workers-compensation-mini-switch {
          width: 44px;
          height: 26px;
          border-radius: 999px;
          background: #e2e8f0;
          border: 1px solid #cbd5e1;
          padding: 2px;
          display: inline-flex;
          justify-content: flex-start;
          transition: background .16s ease, border-color .16s ease;
        }
        .workers-compensation-mini-switch i {
          width: 20px;
          height: 20px;
          border-radius: 999px;
          background: #ffffff;
          box-shadow: 0 4px 10px rgba(15,23,42,.14);
          transition: transform .16s ease;
        }
        .workers-compensation-rule-row.is-active .workers-compensation-mini-switch {
          justify-content: flex-end;
          background: #00675f;
          border-color: #00675f;
        }
        .workers-compensation-base-options {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
        }
        .workers-compensation-base-options button {
          border: 1px solid #e2e8f0;
          background: #ffffff;
          color: #64748b;
          border-radius: 16px;
          min-height: 46px;
          padding: 0 12px;
          font-size: 12px;
          font-weight: 950;
          cursor: pointer;
          transition: background .16s ease, border-color .16s ease, color .16s ease;
        }
        .workers-compensation-base-options button.is-active {
          background: #f0fdfa;
          border-color: rgba(0,103,95,.22);
          color: #00675f;
        }
        .workers-compensation-footnote {
          color: #64748b;
          font-size: 11.5px;
          line-height: 1.4;
          font-weight: 760;
        }
        @media (max-width: 900px) {
          .workers-compensation-header,
          .workers-compensation-panel-head,
          .workers-compensation-rules-head {
            display: grid;
          }
          .workers-compensation-status-pill {
            min-width: 0;
            text-align: left;
          }
          .workers-compensation-form-grid,
          .workers-compensation-base-options {
            grid-template-columns: 1fr;
          }
          .workers-compensation-segmented {
            grid-template-columns: 1fr;
          }
        }

        /* =========================================================
           PHASE 31H.2 — Worker Full Page Editor
           El editor deja de sentirse como popup pequeño y pasa a subpágina amplia.
           ========================================================= */

        .workers-editor-overlay{
          padding: calc(env(safe-area-inset-top,0px) + 74px) 24px 24px !important;
          background: rgba(15,23,42,.52) !important;
          backdrop-filter: blur(8px);
        }
        .workers-editor-shell{
          width: min(1180px, calc(100vw - 48px)) !important;
          max-width: 1180px !important;
          height: calc(100vh - 110px) !important;
          max-height: calc(100vh - 110px) !important;
          border-radius: 30px !important;
          box-shadow: 0 30px 90px rgba(15,23,42,.28) !important;
          background:
            radial-gradient(circle at 10% 0%, rgba(0,103,95,.06), transparent 32%),
            #ffffff !important;
        }
        .workers-editor-head{
          padding: 18px 22px 12px !important;
          background: rgba(255,255,255,.96) !important;
          backdrop-filter: blur(16px);
        }
        .workers-editor-head > .flex:first-child{
          align-items: center !important;
        }
        .workers-back-button{
          min-height: 40px !important;
          border-radius: 999px !important;
          background: #f8fafc !important;
          border: 1px solid #e2e8f0 !important;
          color: #0f172a !important;
          padding: 0 14px !important;
        }
        .workers-editor-head .mt-3{
          margin-top: 14px !important;
          display: inline-flex !important;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 999px;
          padding: 4px;
          gap: 4px !important;
        }
        .workers-editor-head .mt-3 button{
          min-height: 34px;
          padding: 0 13px !important;
          font-size: 12px !important;
          border-radius: 999px !important;
          box-shadow: none !important;
        }
        .workers-editor-body{
          padding: 18px 22px 22px !important;
          background: linear-gradient(180deg,#ffffff 0%,#fbfefd 100%);
        }
        .workers-editor-footer{
          padding: 14px 22px !important;
          background: rgba(255,255,255,.96) !important;
          backdrop-filter: blur(16px);
        }
        .workers-editor-footer .grid{
          display: flex !important;
          justify-content: flex-end;
          gap: 10px !important;
        }
        .workers-editor-footer button{
          min-width: 160px;
          min-height: 44px;
          border-radius: 999px !important;
        }

        /* General tab premium */
        .workers-general-tab > .bg-white{
          box-shadow: none !important;
          border-radius: 26px !important;
        }
        .workers-general-tab .bg-white > .px-4.py-3{
          display: none !important;
        }
        .workers-general-tab .bg-white > .p-4{
          padding: 0 !important;
        }
        .workers-general-tab .p-4 > .flex{
          display: grid !important;
          grid-template-columns: 260px minmax(0,1fr) !important;
          gap: 18px !important;
          align-items: stretch !important;
        }
        .workers-photo-column{
          width: 100% !important;
          border: 1px solid #e2e8f0;
          border-radius: 24px;
          padding: 12px;
          background: linear-gradient(180deg,#ffffff,#fbfefd);
          display: grid;
          align-content: start;
        }
        .workers-photo-column .aspect-square{
          border-radius: 20px !important;
          box-shadow: 0 12px 28px rgba(15,23,42,.055);
        }
        .workers-photo-column button{
          min-height: 40px !important;
          border-radius: 999px !important;
          color: #00675f !important;
        }
        .workers-main-fields{
          display: grid !important;
          gap: 12px !important;
          align-content: start;
        }
        .workers-main-fields label{
          font-size: 12px !important;
          font-weight: 950 !important;
          color: #475569 !important;
        }
        .workers-main-fields input{
          min-height: 44px !important;
          border-radius: 16px !important;
          background: #ffffff !important;
        }
        .workers-main-fields > .rounded-2xl{
          order: -1;
          padding: 10px !important;
          border-radius: 20px !important;
          background: #f8fafc !important;
        }
        .workers-main-fields > .rounded-2xl label{
          display: none !important;
        }
        .workers-main-fields > .rounded-2xl .grid{
          display: inline-grid !important;
          grid-template-columns: 1fr 1fr !important;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 999px;
          padding: 4px;
          gap: 4px !important;
          width: min(460px, 100%);
        }
        .workers-main-fields > .rounded-2xl .grid button{
          border: 0 !important;
          border-radius: 999px !important;
          padding: 10px 12px !important;
          min-height: 42px;
          background: transparent !important;
        }
        .workers-main-fields > .rounded-2xl .grid button.border-emerald-300{
          background: #f0fdfa !important;
          color: #00675f !important;
          box-shadow: inset 0 0 0 1px rgba(0,103,95,.14);
        }
        .workers-main-fields > .rounded-2xl .grid button .text-sm{
          font-size: 12px !important;
        }
        .workers-main-fields > .rounded-2xl .grid button .text-xs{
          display: none !important;
        }

        /* Services / schedule / permissions tabs less bulky */
        .workers-editor-body .space-y-3,
        .workers-editor-body .space-y-4{
          gap: 10px !important;
        }
        .workers-editor-body .bg-white{
          box-shadow: 0 10px 28px rgba(15,23,42,.04) !important;
          border-radius: 24px !important;
        }
        .workers-editor-body .bg-white .p-4{
          padding: 14px !important;
        }
        .workers-editor-body .rounded-2xl{
          border-radius: 18px !important;
        }



        /* Inline subpage mode: replaces the cramped modal with an in-section editor page. */
        .workers-editor-page{
          width: 100%;
          min-height: calc(100vh - 188px);
          display: flex;
          align-items: stretch;
        }
        .workers-editor-page .workers-editor-shell{
          width: 100% !important;
          max-width: none !important;
          height: calc(100vh - 188px) !important;
          max-height: calc(100vh - 188px) !important;
          min-height: 620px;
          margin: 0 !important;
          border-radius: 28px !important;
          box-shadow: 0 18px 52px rgba(15,23,42,.08) !important;
          border: 1px solid rgba(226,232,240,.96) !important;
          background:
            radial-gradient(circle at 0% 0%, rgba(0,103,95,.07), transparent 30%),
            linear-gradient(180deg,#ffffff 0%,#fbfefd 100%) !important;
        }


        /* PHASE 31H.4 — Dedicated large worker popup via portal */
        .workers-editor-modal-overlay{
          position:fixed;
          inset:0;
          z-index:7000;
          display:flex;
          align-items:flex-start;
          justify-content:center;
          padding:calc(env(safe-area-inset-top,0px) + 96px) 24px 22px;
          background:rgba(15,23,42,.44);
          backdrop-filter:blur(10px);
          -webkit-backdrop-filter:blur(10px);
        }
        /* Keep parent cropper above this worker editor. The cropper lives in the parent page, so this section must not cover it. */
        body .image-cropper-overlay,
        body .cropper-overlay,
        body .cropper-modal,
        body .photo-cropper-overlay,
        body .logo-cropper-overlay,
        body .loop-cropper-overlay,
        body .loop-cropper-backdrop,
        body .loop-cropper-panel,
        body .react-easy-crop-modal,
        body .reactEasyCrop_Container,
        body [data-loop-cropper="true"]{
          z-index:2147483646 !important;
        }

        .workers-editor-modal-shell{
          width:min(1120px,calc(100vw - 48px)) !important;
          max-width:1120px !important;
          height:min(780px,calc(100vh - 132px)) !important;
          max-height:calc(100vh - 132px) !important;
          min-height:min(620px,calc(100vh - 132px)) !important;
          margin:0 !important;
          border-radius:30px !important;
          border:1px solid rgba(226,232,240,.98) !important;
          box-shadow:0 34px 90px rgba(15,23,42,.30) !important;
          background:linear-gradient(180deg,#ffffff 0%,#fbfefd 100%) !important;
        }
        .workers-editor-modal-shell .workers-editor-head{
          border-bottom:1px solid rgba(226,232,240,.78) !important;
        }
        .workers-editor-modal-shell .workers-editor-body{
          padding:16px 18px 18px !important;
        }
        .workers-editor-modal-shell .workers-editor-footer{
          border-top:1px solid rgba(226,232,240,.78) !important;
          padding:12px 18px !important;
        }
        .workers-editor-modal-shell .workers-editor-footer button{
          min-height:42px;
        }

        .workers-editor-title-row{
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:16px;
        }
        .workers-editor-title-left{
          min-width:0;
          display:flex;
          align-items:center;
          gap:14px;
        }
        .workers-editor-title-copy{
          min-width:0;
        }
        .workers-editor-kicker{
          color:#00675f;
          font-size:10px;
          font-weight:950;
          letter-spacing:.16em;
          text-transform:uppercase;
        }
        .workers-editor-name{
          color:#0f172a;
          font-size:24px;
          line-height:1.05;
          letter-spacing:-.045em;
          font-weight:950;
          white-space:nowrap;
          overflow:hidden;
          text-overflow:ellipsis;
        }
        .workers-editor-subtitle{
          margin-top:3px;
          color:#64748b;
          font-size:12px;
          font-weight:800;
          white-space:nowrap;
          overflow:hidden;
          text-overflow:ellipsis;
        }
        .workers-editor-actions{
          display:flex;
          align-items:center;
          justify-content:flex-end;
          gap:8px;
          flex:0 0 auto;
        }
        .workers-editor-type-badge{
          min-height:34px;
          display:inline-flex;
          align-items:center;
          padding:0 12px;
          border-radius:999px;
          border:1px solid rgba(0,103,95,.14);
          background:#f0fdfa;
          color:#00675f;
          font-size:11px;
          font-weight:950;
          white-space:nowrap;
        }
        .workers-editor-save-top{
          min-height:40px;
          border:0;
          border-radius:999px !important;
          background:#00675f;
          color:#fff;
          padding:0 16px;
          font-size:12px;
          box-shadow:0 12px 24px rgba(0,103,95,.16);
        }
        .workers-editor-cancel-top{
          min-height:40px;
          border:1px solid #e2e8f0;
          background:#fff;
          color:#334155;
          padding:0 14px;
          font-size:12px;
          border-radius:999px !important;
        }
        .workers-photo-status{
          margin-top:10px;
          display:flex;
          align-items:center;
          justify-content:center;
          min-height:34px;
          border-radius:999px;
          background:#f8fafc;
          border:1px solid #e2e8f0;
          color:#475569;
          font-size:11px;
          font-weight:900;
          text-align:center;
        }
        .workers-type-helper{
          margin-top:8px;
          color:#64748b;
          font-size:12px;
          line-height:1.45;
          font-weight:750;
        }
\n\n        /* PHASE 31H.3A — Minimal worker schedule */\n        .workers-schedule-tab{\n          display:grid;\n          gap:12px;\n        }\n        .workers-schedule-summary{\n          border:1px solid rgba(226,232,240,.96);\n          border-radius:26px;\n          background:linear-gradient(180deg,#ffffff 0%,#fbfefd 100%);\n          padding:16px;\n          box-shadow:0 12px 28px rgba(15,23,42,.04);\n        }\n        .workers-schedule-summary-top{\n          display:flex;\n          align-items:flex-start;\n          justify-content:space-between;\n          gap:16px;\n        }\n        .workers-schedule-summary h4{\n          margin:0;\n          color:#0f172a;\n          font-size:18px;\n          font-weight:950;\n          letter-spacing:-.035em;\n        }\n        .workers-schedule-summary p{\n          margin:5px 0 0;\n          color:#64748b;\n          font-size:12px;\n          line-height:1.4;\n          font-weight:800;\n        }\n        .workers-schedule-badge{\n          display:inline-flex;\n          align-items:center;\n          min-height:32px;\n          padding:0 12px;\n          border-radius:999px;\n          background:#f0fdfa;\n          border:1px solid rgba(0,103,95,.14);\n          color:#00675f;\n          font-size:11px;\n          font-weight:950;\n          white-space:nowrap;\n        }\n        .workers-schedule-actions{\n          margin-top:14px;\n          display:flex;\n          flex-wrap:wrap;\n          gap:8px;\n        }\n        .workers-schedule-actions button,\n        .workers-blocked-compact button{\n          min-height:36px;\n          padding:0 13px;\n          border-radius:999px !important;\n          border:1px solid #e2e8f0;\n          background:#fff;\n          color:#334155;\n          font-size:11px;\n          font-weight:950 !important;\n        }\n        .workers-schedule-actions button.primary-soft{\n          background:#f0fdfa;\n          border-color:rgba(0,103,95,.16);\n          color:#00675f;\n        }\n        .workers-schedule-list{\n          display:grid;\n          gap:7px;\n        }\n        .workers-day-row{\n          border:1px solid rgba(226,232,240,.96);\n          border-radius:22px;\n          background:#fff;\n          overflow:hidden;\n          box-shadow:0 8px 22px rgba(15,23,42,.035);\n        }\n        .workers-day-row.is-open{\n          border-color:rgba(0,103,95,.18);\n          background:linear-gradient(180deg,#ffffff 0%,#fbfefd 100%);\n        }\n        .workers-day-row-button{\n          width:100%;\n          min-height:58px;\n          padding:10px 13px;\n          display:grid;\n          grid-template-columns:46px minmax(0,1fr) auto 22px;\n          align-items:center;\n          gap:12px;\n          border:0;\n          background:transparent;\n          cursor:pointer;\n        }\n        .workers-day-label{\n          width:42px;\n          height:38px;\n          display:grid;\n          place-items:center;\n          border-radius:16px;\n          background:#f8fafc;\n          color:#0f172a;\n          font-size:12px;\n          font-weight:950;\n        }\n        .workers-day-copy{\n          min-width:0;\n          text-align:left;\n        }\n        .workers-day-copy strong{\n          display:block;\n          color:#0f172a;\n          font-size:13px;\n          font-weight:950;\n          white-space:nowrap;\n          overflow:hidden;\n          text-overflow:ellipsis;\n        }\n        .workers-day-copy span{\n          display:block;\n          margin-top:2px;\n          color:#64748b;\n          font-size:11px;\n          font-weight:800;\n          white-space:nowrap;\n          overflow:hidden;\n          text-overflow:ellipsis;\n        }\n        .workers-day-status{\n          text-align:right;\n          color:#94a3b8;\n          font-size:11px;\n          font-weight:900;\n          white-space:nowrap;\n        }\n        .workers-day-chevron{\n          color:#94a3b8;\n          font-size:13px;\n          font-weight:950;\n        }\n        .workers-day-editor{\n          border-top:1px solid #f1f5f9;\n          padding:12px 13px 13px;\n          display:grid;\n          gap:10px;\n        }\n        .workers-day-editor-top{\n          display:flex;\n          align-items:center;\n          justify-content:space-between;\n          gap:12px;\n          padding:10px;\n          border-radius:18px;\n          background:#f8fafc;\n          border:1px solid #e2e8f0;\n        }\n        .workers-day-editor-top strong{\n          display:block;\n          color:#0f172a;\n          font-size:12px;\n          font-weight:950;\n        }\n        .workers-day-editor-top span{\n          display:block;\n          margin-top:2px;\n          color:#64748b;\n          font-size:11px;\n          font-weight:750;\n        }\n        .workers-time-grid{\n          display:grid;\n          grid-template-columns:1fr 1fr auto;\n          gap:8px;\n          align-items:end;\n        }\n        .workers-time-grid.compact{\n          grid-template-columns:1fr 1fr auto;\n        }\n        .workers-time-field label{\n          display:block;\n          color:#64748b;\n          font-size:10px;\n          font-weight:950;\n          text-transform:uppercase;\n          letter-spacing:.06em;\n          margin-bottom:5px;\n        }\n        .workers-time-field input{\n          width:100%;\n          min-height:42px !important;\n          border-radius:15px !important;\n          padding:0 12px !important;\n          font-size:13px;\n        }\n        .workers-mini-action{\n          min-height:42px;\n          padding:0 12px;\n          border-radius:15px !important;\n          border:1px solid #e2e8f0;\n          background:#fff;\n          color:#334155;\n          font-size:11px;\n          font-weight:950 !important;\n          white-space:nowrap;\n        }\n        .workers-mini-action.danger{\n          color:#b91c1c;\n          background:#fff7f7;\n          border-color:#fecaca;\n        }\n        .workers-closed-note{\n          padding:13px;\n          border-radius:18px;\n          background:#f8fafc;\n          border:1px dashed #cbd5e1;\n          color:#64748b;\n          font-size:12px;\n          font-weight:800;\n          line-height:1.4;\n        }\n        .workers-blocked-compact{\n          margin-top:4px;\n          display:flex;\n          align-items:center;\n          justify-content:space-between;\n          gap:12px;\n          border:1px solid rgba(226,232,240,.96);\n          border-radius:22px;\n          background:#fff;\n          padding:13px;\n        }\n        .workers-blocked-compact strong{\n          display:block;\n          color:#0f172a;\n          font-size:13px;\n          font-weight:950;\n        }\n        .workers-blocked-compact span{\n          display:block;\n          margin-top:2px;\n          color:#64748b;\n          font-size:11px;\n          font-weight:800;\n        }\n\n

        /* PHASE 31H.5 — Schedule Option 1: Minimal Clean compact list */
        .workers-editor-modal-shell .workers-schedule-tab{
          gap:10px;
        }
        .workers-schedule-summary-clean{
          border-radius:18px !important;
          padding:14px 16px !important;
          box-shadow:none !important;
          background:#ffffff !important;
        }
        .workers-schedule-summary-clean .workers-schedule-summary-top{
          align-items:center;
        }
        .workers-schedule-summary-clean h4{
          font-size:15px !important;
          letter-spacing:-.025em !important;
        }
        .workers-schedule-summary-clean p{
          margin-top:5px !important;
          font-size:11px !important;
          color:#64748b !important;
          font-weight:800 !important;
        }
        .workers-company-hours-button{
          min-height:38px !important;
          padding:0 16px !important;
          border-radius:12px !important;
          border:0 !important;
          background:#00675f !important;
          color:#ffffff !important;
          font-size:11px !important;
          font-weight:950 !important;
          box-shadow:0 10px 24px rgba(0,103,95,.13) !important;
          white-space:nowrap;
        }
        .workers-company-hours-button:disabled{
          opacity:.45;
          cursor:not-allowed;
          box-shadow:none !important;
        }
        .workers-schedule-more-actions{
          margin-top:8px;
        }
        .workers-schedule-more-actions summary{
          display:inline-flex;
          align-items:center;
          min-height:28px;
          color:#64748b;
          font-size:11px;
          font-weight:900;
          cursor:pointer;
          list-style:none;
        }
        .workers-schedule-more-actions summary::-webkit-details-marker{display:none}
        .workers-schedule-more-actions summary::after{
          content:"+";
          margin-left:7px;
          color:#94a3b8;
          font-weight:950;
        }
        .workers-schedule-more-actions[open] summary::after{content:"−"}
        .workers-schedule-more-actions > div{
          display:flex;
          flex-wrap:wrap;
          gap:7px;
          margin-top:6px;
        }
        .workers-schedule-more-actions button{
          min-height:32px !important;
          padding:0 11px !important;
          border-radius:10px !important;
          border:1px solid #e2e8f0 !important;
          background:#fff !important;
          color:#334155 !important;
          font-size:10px !important;
          font-weight:950 !important;
        }
        .workers-schedule-list{
          gap:0 !important;
          border:1px solid rgba(226,232,240,.92);
          border-radius:18px;
          overflow:hidden;
          background:#ffffff;
          box-shadow:0 10px 26px rgba(15,23,42,.035);
        }
        .workers-day-row{
          border:0 !important;
          border-radius:0 !important;
          box-shadow:none !important;
          border-bottom:1px solid #eef2f7 !important;
          background:#fff !important;
        }
        .workers-day-row:last-child{
          border-bottom:0 !important;
        }
        .workers-day-row.is-open{
          background:#fbfefd !important;
          border-color:#eef2f7 !important;
        }
        .workers-day-row-button{
          min-height:47px !important;
          padding:8px 14px !important;
          grid-template-columns:26px minmax(0,1fr) 66px 18px !important;
          gap:10px !important;
        }
        .workers-day-icon{
          width:18px;
          height:18px;
          display:grid;
          place-items:center;
          color:#00675f;
          font-size:10px;
          line-height:1;
          opacity:.85;
        }
        .workers-day-copy{
          display:grid;
          grid-template-columns:120px minmax(0,1fr);
          align-items:center;
          gap:12px;
        }
        .workers-day-copy strong{
          font-size:12px !important;
          font-weight:950 !important;
        }
        .workers-day-copy span{
          margin-top:0 !important;
          font-size:11px !important;
          color:#475569 !important;
          font-weight:800 !important;
        }
        .workers-day-status{
          justify-self:end;
          min-width:38px;
          min-height:23px;
          padding:0 9px;
          display:inline-flex;
          align-items:center;
          justify-content:center;
          border-radius:999px;
          border:1px solid #eef2f7;
          background:#f8fafc;
          color:#64748b !important;
          font-size:10px !important;
          font-weight:950 !important;
        }
        .workers-day-chevron{
          color:#94a3b8 !important;
          font-size:12px !important;
        }
        .workers-day-editor{
          padding:12px 14px 14px !important;
          gap:10px !important;
          background:#fbfefd;
        }
        .workers-day-editor-top{
          padding:9px 11px !important;
          border-radius:13px !important;
          background:#ffffff !important;
        }
        .workers-day-editor-top strong{
          font-size:12px !important;
        }
        .workers-day-editor-top span{
          font-size:10px !important;
        }
        .workers-time-grid{
          gap:8px !important;
        }
        .workers-time-field input{
          min-height:38px !important;
          border-radius:12px !important;
        }
        .workers-mini-action{
          min-height:38px !important;
          border-radius:12px !important;
        }
        .workers-blocked-compact{
          margin-top:0 !important;
          border-radius:18px !important;
          padding:13px 14px !important;
          box-shadow:0 8px 22px rgba(15,23,42,.032);
        }



        /* PHASE 31H.3C — Minimal assigned services */
        .workers-services-tab{
          display:grid;
          gap:12px;
        }
        .workers-services-hero{
          display:flex;
          align-items:flex-start;
          justify-content:space-between;
          gap:16px;
          border:1px solid rgba(226,232,240,.96);
          border-radius:26px;
          background:linear-gradient(180deg,#ffffff 0%,#fbfefd 100%);
          padding:16px;
          box-shadow:0 12px 28px rgba(15,23,42,.04);
        }
        .workers-services-hero span{
          display:block;
          color:#00675f;
          font-size:10px;
          font-weight:950;
          letter-spacing:.16em;
          text-transform:uppercase;
          margin-bottom:5px;
        }
        .workers-services-hero h4{
          margin:0;
          color:#0f172a;
          font-size:18px;
          font-weight:950;
          letter-spacing:-.035em;
        }
        .workers-services-hero p{
          margin:5px 0 0;
          color:#64748b;
          font-size:12px;
          line-height:1.45;
          font-weight:800;
          max-width:680px;
        }
        .workers-services-count{
          min-width:118px;
          min-height:58px;
          border:1px solid rgba(0,103,95,.13);
          border-radius:20px;
          background:#f0fdfa;
          display:grid;
          place-items:center;
          align-content:center;
          color:#00675f;
          padding:8px 12px;
        }
        .workers-services-count strong{
          font-size:22px;
          line-height:1;
          font-weight:950;
        }
        .workers-services-count small{
          margin-top:3px;
          font-size:10px;
          font-weight:950;
          color:#00796f;
          white-space:nowrap;
        }
        .workers-services-toolbar{
          display:grid;
          grid-template-columns:minmax(0,1fr) auto auto;
          gap:8px;
          align-items:center;
        }
        .workers-services-search{
          width:100%;
          min-height:42px !important;
          border-radius:999px !important;
          border:1px solid #e2e8f0;
          background:#fff;
          padding:0 15px !important;
          font-size:13px;
          font-weight:800;
        }
        .workers-services-toolbar button{
          min-height:42px;
          padding:0 14px;
          border-radius:999px !important;
          border:1px solid #e2e8f0;
          background:#fff;
          color:#334155;
          font-size:11px;
          font-weight:950 !important;
          white-space:nowrap;
        }
        .workers-services-toolbar button.primary-soft{
          background:#f0fdfa;
          border-color:rgba(0,103,95,.16);
          color:#00675f;
        }
        .workers-services-list{
          display:grid;
          gap:7px;
        }
        .workers-service-row{
          min-height:46px;
          display:grid;
          grid-template-columns:minmax(0,1fr) auto;
          align-items:center;
          gap:10px;
          padding:8px 11px 8px 14px;
          border:1px solid rgba(226,232,240,.96);
          border-radius:16px;
          background:#f8fafc;
          box-shadow:none;
          cursor:pointer;
          transition:transform .14s ease, border-color .14s ease, background .14s ease, box-shadow .14s ease;
        }
        .workers-service-row:hover{
          transform:translateY(-1px);
          border-color:rgba(0,103,95,.20);
          box-shadow:0 8px 18px rgba(15,23,42,.04);
        }
        .workers-service-row.is-selected{
          border-color:rgba(0,103,95,.34);
          background:#ecfdf5;
        }
        .workers-service-row.is-muted{
          border-color:#e5e7eb;
          background:#f8fafc;
        }
        .workers-service-row-copy{
          min-width:0;
          display:flex;
          align-items:center;
          gap:8px;
        }
        .workers-service-row-copy strong{
          display:block;
          color:#0f172a;
          font-size:13px;
          font-weight:950;
          white-space:nowrap;
          overflow:hidden;
          text-overflow:ellipsis;
        }
        .workers-service-row.is-muted .workers-service-row-copy strong{
          color:#64748b;
        }
        .workers-service-row-copy span{
          display:none;
        }
        .workers-service-check{
          width:26px;
          height:26px;
          border-radius:999px;
          border:1px solid #cbd5e1;
          background:#ffffff;
          display:grid;
          place-items:center;
          color:#94a3b8;
          font-size:12px;
          font-weight:950;
        }
        .workers-service-row.is-selected .workers-service-check{
          background:#00675f;
          border-color:#00675f;
          color:#fff;
        }
        .workers-services-empty,
        .workers-services-operational-note{
          border:1px dashed #cbd5e1;
          border-radius:24px;
          background:#ffffff;
          padding:18px;
          color:#64748b;
          font-size:13px;
          font-weight:800;
          line-height:1.45;
        }
        .workers-services-empty strong,
        .workers-services-operational-note strong{
          display:block;
          color:#0f172a;
          font-size:15px;
          font-weight:950;
          margin-bottom:4px;
        }
        @media(max-width: 760px){
          .workers-services-hero{ display:grid; }
          .workers-services-count{ justify-self:start; }
          .workers-services-toolbar{ grid-template-columns:1fr; }
          .workers-services-toolbar button{ width:100%; }
        }

        /* PHASE 31K — Services + Compensation unified tab */
        .workers-services-comp-tab{
          display:grid;
          gap:12px;
          max-width:980px;
          margin:0 auto;
        }
        .workers-service-comp-overview{
          display:grid;
          grid-template-columns:minmax(220px,.75fr) minmax(0,1.55fr);
          gap:14px;
          align-items:stretch;
          border:1px solid rgba(226,232,240,.96);
          border-radius:26px;
          background:linear-gradient(180deg,#ffffff 0%,#fbfefd 100%);
          padding:14px;
          box-shadow:0 12px 28px rgba(15,23,42,.04);
        }
        .workers-service-comp-title{
          min-width:0;
        }
        .workers-service-comp-title span,
        .workers-service-comp-services-head span{
          display:block;
          color:#00675f;
          font-size:10px;
          font-weight:950;
          letter-spacing:.16em;
          text-transform:uppercase;
          margin-bottom:5px;
        }
        .workers-service-comp-title h4,
        .workers-service-comp-services-head h4{
          margin:0;
          color:#0f172a;
          font-size:18px;
          line-height:1.05;
          font-weight:950;
          letter-spacing:-.035em;
        }
        .workers-service-comp-title p,
        .workers-service-comp-services-head p{
          margin:5px 0 0;
          color:#64748b;
          font-size:12px;
          line-height:1.45;
          font-weight:800;
        }
        .workers-service-comp-summary-grid{
          display:grid;
          grid-template-columns:minmax(150px,1fr) minmax(220px,1.3fr) 92px;
          gap:8px;
          align-items:stretch;
        }
        .workers-service-comp-summary-card{
          min-height:64px;
          border:1px solid #e2e8f0;
          border-radius:18px;
          background:#fff;
          padding:10px 13px;
          display:grid;
          align-content:center;
          gap:2px;
          color:#0f172a;
        }
        .workers-service-comp-summary-card small{
          color:#94a3b8;
          font-size:10px;
          font-weight:950;
          letter-spacing:.08em;
          text-transform:uppercase;
        }
        .workers-service-comp-summary-card strong{
          color:#0f172a;
          font-size:15px;
          line-height:1.05;
          font-weight:950;
          letter-spacing:-.025em;
        }
        .workers-service-comp-summary-card span{
          color:#64748b;
          font-size:11px;
          line-height:1.2;
          font-weight:820;
        }
        .workers-service-comp-summary-card.is-compensation{
          background:#f0fdfa;
          border-color:rgba(0,103,95,.16);
        }
        .workers-service-comp-summary-card.is-compensation strong{
          color:#00675f;
          font-size:14px;
          line-height:1.15;
        }
        .workers-service-comp-edit-pay{
          min-height:64px;
          border:1px solid rgba(0,103,95,.12);
          border-radius:18px;
          background:#00675f;
          color:#fff;
          padding:0 14px;
          font-size:12px;
          font-weight:950;
          cursor:pointer;
          box-shadow:0 14px 28px rgba(0,103,95,.16);
        }
        .workers-service-comp-edit-pay:active{
          transform:scale(.99);
        }
        .workers-service-comp-services-card{
          border:1px solid rgba(226,232,240,.96);
          border-radius:24px;
          background:rgba(255,255,255,.96);
          padding:13px;
          box-shadow:0 12px 28px rgba(15,23,42,.04);
          display:grid;
          gap:10px;
        }
        .workers-service-comp-services-head{
          display:flex;
          justify-content:space-between;
          align-items:flex-start;
          gap:12px;
        }
        .workers-service-comp-count-pill{
          min-height:40px;
          border:1px solid rgba(0,103,95,.14);
          border-radius:999px;
          background:#f0fdfa;
          color:#00675f;
          padding:0 13px;
          display:inline-flex;
          align-items:center;
          justify-content:center;
          font-size:12px;
          font-weight:950;
          white-space:nowrap;
        }
        .workers-service-comp-toolbar{
          margin-top:2px;
        }
        .workers-service-comp-list{
          max-height:none;
          gap:6px;
        }
        .workers-service-comp-list .workers-service-row{
          min-height:44px;
          border-radius:15px;
          padding:7px 10px 7px 13px;
        }
        .workers-service-comp-list .workers-service-row.is-selected{
          background:#e8fff6;
          border-color:rgba(0,103,95,.38);
        }
        .workers-service-comp-list .workers-service-row.is-muted{
          background:#f8fafc;
          border-color:#e5e7eb;
        }
        .workers-comp-modal-overlay{
          position:fixed;
          inset:0;
          z-index:2147483000;
          display:grid;
          place-items:center;
          padding:24px;
          background:rgba(15,23,42,.38);
          backdrop-filter:blur(14px);
          -webkit-backdrop-filter:blur(14px);
        }
        .workers-comp-modal-shell{
          width:min(680px,100%);
          max-height:min(720px,calc(100vh - 48px));
          overflow:hidden;
          border:1px solid rgba(226,232,240,.96);
          border-radius:28px;
          background:#fff;
          box-shadow:0 32px 90px rgba(15,23,42,.26);
          display:flex;
          flex-direction:column;
        }
        .workers-comp-modal-head{
          padding:22px 24px 18px;
          border-bottom:1px solid #eef2f7;
          display:grid;
          grid-template-columns:minmax(0,1fr) auto;
          gap:14px;
          align-items:start;
        }
        .workers-comp-modal-head span{
          display:block;
          color:#00675f;
          font-size:10px;
          font-weight:950;
          letter-spacing:.16em;
          text-transform:uppercase;
          margin-bottom:6px;
        }
        .workers-comp-modal-head strong{
          display:block;
          color:#0f172a;
          font-size:24px;
          line-height:1;
          font-weight:950;
          letter-spacing:-.045em;
        }
        .workers-comp-modal-head p{
          margin:7px 0 0;
          color:#64748b;
          font-size:13px;
          line-height:1.42;
          font-weight:760;
        }
        .workers-comp-modal-close{
          width:42px;
          height:42px;
          border-radius:999px;
          border:1px solid #dbe5ee;
          background:#fff;
          color:#0f172a;
          font-size:22px;
          font-weight:700;
          cursor:pointer;
        }
        .workers-comp-modal-body{
          padding:18px 24px 22px;
          overflow:auto;
          display:grid;
          gap:14px;
        }

        .workers-comp-modal-body-clean{
          padding: 18px 28px 18px;
        }
        .workers-comp-modal-head-clean{
          border-bottom:0;
          padding:26px 28px 8px;
        }
        .workers-comp-modal-head-clean strong{
          font-size:28px;
          letter-spacing:-.055em;
        }
        .workers-comp-modal-head-clean p{
          max-width:460px;
          font-size:14px;
        }
        .workers-comp-clean-card{
          display:grid;
          gap:14px;
        }
        .workers-comp-clean-row{
          display:grid;
          grid-template-columns:minmax(0,1.08fr) minmax(0,.92fr);
          gap:12px;
          align-items:end;
        }
        .workers-comp-clean-field{
          display:grid;
          gap:8px;
        }
        .workers-comp-clean-field > span{
          color:#0f172a;
          font-size:13px;
          line-height:1;
          font-weight:900;
          letter-spacing:-.01em;
        }
        .workers-comp-clean-field select,
        .workers-comp-clean-amount-box{
          width:100%;
          min-height:56px;
          border:1px solid #dbe5ee;
          border-radius:16px;
          background:#fff;
          color:#0f172a;
          font-size:16px;
          font-weight:850;
          outline:none;
          box-shadow:none;
        }
        .workers-comp-clean-field select{
          appearance:auto;
          padding:0 16px;
          cursor:pointer;
        }
        .workers-comp-clean-amount-box{
          display:grid;
          grid-template-columns:auto minmax(0,1fr) auto;
          align-items:center;
          overflow:hidden;
        }
        .workers-comp-clean-amount-box input{
          width:100%;
          min-height:54px !important;
          border:0 !important;
          outline:0 !important;
          box-shadow:none !important;
          padding:0 14px !important;
          color:#0f172a;
          font-size:20px;
          font-weight:900;
          letter-spacing:-.03em;
          background:transparent !important;
        }
        .workers-comp-clean-amount-box em{
          min-width:52px;
          min-height:56px;
          display:grid;
          place-items:center;
          border-left:1px solid #e2e8f0;
          color:#00675f;
          font-size:18px;
          font-weight:950;
          font-style:normal;
          background:#f8fafc;
        }
        .workers-comp-clean-amount-box em:first-child{
          border-left:0;
          border-right:1px solid #e2e8f0;
        }
        .workers-comp-clean-amount.is-disabled{
          opacity:.58;
        }
        .workers-comp-clean-toggle{
          width:100%;
          min-height:52px;
          border:1px solid #e2e8f0;
          border-radius:16px;
          background:#fff;
          color:#0f172a;
          padding:0 14px 0 16px;
          display:grid;
          grid-template-columns:minmax(0,1fr) auto;
          align-items:center;
          gap:12px;
          text-align:left;
          cursor:pointer;
          transition:background .16s ease,border-color .16s ease;
        }
        .workers-comp-clean-toggle span{
          font-size:15px;
          font-weight:850;
        }
        .workers-comp-clean-toggle i{
          width:46px;
          height:28px;
          border-radius:999px;
          background:#e2e8f0;
          border:1px solid #cbd5e1;
          padding:2px;
          display:flex;
          justify-content:flex-start;
          transition:background .16s ease,border-color .16s ease;
        }
        .workers-comp-clean-toggle i:before{
          content:"";
          width:22px;
          height:22px;
          border-radius:999px;
          background:#fff;
          box-shadow:0 4px 10px rgba(15,23,42,.14);
          transition:transform .16s ease;
        }
        .workers-comp-clean-toggle.is-active{
          border-color:rgba(0,103,95,.20);
          background:#f0fdfa;
        }
        .workers-comp-clean-toggle.is-active i{
          background:#007467;
          border-color:#007467;
        }
        .workers-comp-clean-toggle.is-active i:before{
          transform:translateX(18px);
        }
        .workers-comp-clean-base{
          display:grid;
          grid-template-columns:1fr 1fr;
          gap:0;
          border:1px solid #e2e8f0;
          border-radius:16px;
          overflow:hidden;
          background:#fff;
        }
        .workers-comp-clean-base button{
          min-height:52px;
          border:0;
          border-right:1px solid #e2e8f0;
          background:#fff;
          color:#64748b;
          font-size:13px;
          font-weight:900;
          cursor:pointer;
        }
        .workers-comp-clean-base button:last-child{
          border-right:0;
        }
        .workers-comp-clean-base button.is-active{
          background:#f0fdfa;
          color:#00675f;
          box-shadow:inset 0 0 0 1px rgba(0,103,95,.18);
        }
        .workers-comp-clean-empty{
          min-height:48px;
          border:1px dashed #dbe5ee;
          border-radius:16px;
          display:flex;
          align-items:center;
          padding:0 14px;
          color:#64748b;
          font-size:13px;
          font-weight:800;
          background:#f8fafc;
        }
        .workers-comp-clean-summary{
          min-height:58px;
          border:1px solid #e2e8f0;
          border-radius:16px;
          padding:0 16px;
          display:flex;
          align-items:center;
          gap:12px;
          color:#64748b;
          background:#fff;
        }
        .workers-comp-clean-summary.is-active{
          border-color:rgba(0,103,95,.25);
          background:#f0fdfa;
          color:#0f172a;
        }
        .workers-comp-clean-summary span{
          width:28px;
          height:28px;
          border-radius:999px;
          display:grid;
          place-items:center;
          border:1px solid currentColor;
          color:#00675f;
          font-size:14px;
          font-weight:950;
          flex:0 0 auto;
        }
        .workers-comp-clean-summary strong{
          font-size:14px;
          line-height:1.25;
          font-weight:900;
        }
        .workers-comp-modal-actions-clean{
          border-top:0;
          padding:10px 28px 24px;
        }
        @media(max-width: 680px){
          .workers-comp-clean-row{
            grid-template-columns:1fr;
          }
          .workers-comp-modal-head-clean,
          .workers-comp-modal-body-clean,
          .workers-comp-modal-actions-clean{
            padding-left:18px;
            padding-right:18px;
          }
        }
        .workers-comp-modal-actions{
          border-top:1px solid #eef2f7;
          padding:14px 18px;
          display:flex;
          justify-content:flex-end;
          gap:10px;
          background:#fff;
        }
        .workers-comp-modal-actions button{
          min-height:46px;
          border-radius:999px;
          padding:0 20px;
          border:1px solid #e2e8f0;
          background:#fff;
          color:#0f172a;
          font-size:13px;
          font-weight:950;
          cursor:pointer;
        }
        .workers-comp-modal-actions button.primary{
          background:#00675f;
          border-color:#00675f;
          color:#fff;
          box-shadow:0 14px 26px rgba(0,103,95,.16);
        }
        @media(max-width: 900px){
          .workers-service-comp-overview,
          .workers-service-comp-services-head{
            display:grid;
            grid-template-columns:1fr;
          }
          .workers-service-comp-summary-grid{
            grid-template-columns:1fr;
          }
          .workers-service-comp-edit-pay{
            width:100%;
          }
        }
        @media(max-width: 560px){
          .workers-comp-modal-overlay{
            padding:0;
            place-items:end center;
          }
          .workers-comp-modal-shell{
            width:100%;
            max-height:calc(100vh - 20px);
            border-radius:28px 28px 0 0;
          }
          .workers-comp-modal-head,
          .workers-comp-modal-body{
            padding-left:16px;
            padding-right:16px;
          }
        }


        /* PHASE 31H.3D — Schedule visual cleanup + business schedule */
        .workers-schedule-tab{
          gap:10px;
        }
        .workers-schedule-summary{
          border-radius:20px;
          padding:14px 14px 12px;
          box-shadow:none;
          background:#ffffff;
        }
        .workers-schedule-summary h4{
          font-size:16px;
          letter-spacing:-.025em;
        }
        .workers-schedule-summary p{
          margin-top:4px;
          font-size:12px;
          font-weight:750;
        }
        .workers-schedule-badge{
          min-height:28px;
          padding:0 10px;
          background:#f8fafc;
          border-color:#e2e8f0;
          color:#475569;
          font-size:10px;
        }
        .workers-schedule-actions{
          margin-top:12px;
          gap:6px;
        }
        .workers-schedule-actions button,
        .workers-blocked-compact button{
          min-height:34px;
          border-radius:12px !important;
          background:#ffffff;
          box-shadow:none;
        }
        .workers-schedule-actions button.primary-soft{
          background:#00675f;
          border-color:#00675f;
          color:#ffffff;
        }
        .workers-schedule-actions button:disabled{
          opacity:.45;
          cursor:not-allowed;
        }
        .workers-schedule-list{
          gap:0;
          border:1px solid rgba(226,232,240,.96);
          border-radius:20px;
          overflow:hidden;
          background:#ffffff;
        }
        .workers-day-row{
          border:0;
          border-top:1px solid #f1f5f9;
          border-radius:0;
          box-shadow:none;
          background:#ffffff;
        }
        .workers-day-row:first-child{
          border-top:0;
        }
        .workers-day-row.is-open{
          border-color:#f1f5f9;
          background:#fbfefd;
        }
        .workers-day-row-button{
          min-height:52px;
          padding:9px 12px;
          display:grid;
          grid-template-columns:minmax(0,1fr) auto;
          align-items:center;
          gap:10px;
          border:0;
          background:transparent;
        }
        .workers-day-row-main{
          min-width:0;
          min-height:34px;
          padding:0 !important;
          display:grid;
          grid-template-columns:42px minmax(0,1fr);
          align-items:center;
          gap:10px;
          border:0 !important;
          background:transparent !important;
          text-align:left;
          cursor:pointer;
          box-shadow:none !important;
        }
        .workers-day-row-side{
          display:flex;
          align-items:center;
          justify-content:flex-end;
          gap:10px;
        }
        .workers-day-row-side .relative{
          width:42px !important;
          height:24px !important;
        }
        .workers-day-row-side .relative span{
          top:2px !important;
          width:20px !important;
          height:20px !important;
        }
        .workers-day-row-side .relative[aria-pressed="true"] span{
          left:20px !important;
        }
        .workers-day-inline-state{
          min-width:54px;
          color:#94a3b8;
          font-size:11px;
          font-weight:900;
          text-align:right;
          white-space:nowrap;
        }
        .workers-day-inline-state.is-active{
          color:#64748b;
        }
        .workers-day-chevron-button{
          width:26px;
          min-width:26px !important;
          height:26px;
          min-height:26px !important;
          padding:0 !important;
          display:grid;
          place-items:center;
          border:1px solid #e2e8f0 !important;
          background:#ffffff !important;
          color:#94a3b8 !important;
          border-radius:999px !important;
          font-size:12px !important;
          line-height:1;
          box-shadow:none !important;
        }
        .workers-day-label{
          width:auto;
          height:auto;
          display:block;
          background:transparent;
          color:#00675f;
          font-size:12px;
          text-align:left;
        }
        .workers-day-copy strong{
          font-size:13px;
        }
        .workers-day-copy span{
          color:#64748b;
          font-size:11px;
          font-weight:750;
        }
        .workers-day-status{
          color:#64748b;
          font-size:11px;
        }
        .workers-day-chevron{
          color:#94a3b8;
          font-size:12px;
        }
        .workers-day-editor{
          padding:10px 12px 12px;
          background:#fbfefd;
        }
        .workers-day-editor-top{
          padding:9px 10px;
          border-radius:14px;
          background:#ffffff;
        }
        .workers-time-grid,
        .workers-time-grid.compact{
          grid-template-columns: minmax(0,1fr) minmax(0,1fr) auto;
          gap:7px;
        }
        .workers-time-field input{
          min-height:38px !important;
          border-radius:12px !important;
          font-size:12px;
        }
        .workers-mini-action{
          min-height:38px;
          border-radius:12px !important;
        }
        .workers-blocked-compact{
          border-radius:18px;
          box-shadow:none;
          padding:12px;
        }
        @media(max-width: 640px){
          .workers-schedule-summary-top{
            display:grid;
            gap:8px;
          }
          .workers-schedule-badge{
            justify-self:start;
          }
          .workers-day-row-button{
            grid-template-columns:minmax(0,1fr) auto;
            padding:8px 10px;
          }
          .workers-day-row-main{
            grid-template-columns:34px minmax(0,1fr);
          }
          .workers-day-inline-state{
            display:none;
          }
          .workers-day-row-side{
            gap:7px;
          }
          .workers-time-grid,
          .workers-time-grid.compact{
            grid-template-columns:1fr 1fr;
          }
          .workers-time-grid .workers-mini-action,
          .workers-time-grid.compact .workers-mini-action{
            grid-column:1 / -1;
          }
        }


        /* PHASE 31H.3B SAFE — Permisos completos, solo revamp visual */
        .workers-permissions-full-tab{
          display:grid;
          gap:12px;
        }
        .workers-permissions-hero{
          display:flex;
          align-items:flex-start;
          justify-content:space-between;
          gap:16px;
          border:1px solid rgba(226,232,240,.96);
          border-radius:26px;
          background:linear-gradient(180deg,#ffffff 0%,#fbfefd 100%);
          padding:16px;
          box-shadow:0 12px 28px rgba(15,23,42,.04);
        }
        .workers-permissions-hero span{
          display:block;
          color:#00675f;
          font-size:10px;
          font-weight:950;
          letter-spacing:.16em;
          text-transform:uppercase;
          margin-bottom:5px;
        }
        .workers-permissions-hero h4{
          margin:0;
          color:#0f172a;
          font-size:18px;
          font-weight:950;
          letter-spacing:-.035em;
        }
        .workers-permissions-hero p{
          margin:5px 0 0;
          color:#64748b;
          font-size:12px;
          line-height:1.45;
          font-weight:800;
          max-width:680px;
        }
        .workers-permissions-hero-stat{
          min-width:104px;
          min-height:58px;
          border:1px solid rgba(0,103,95,.13);
          border-radius:20px;
          background:#f0fdfa;
          display:grid;
          place-items:center;
          align-content:center;
          color:#00675f;
          padding:8px 12px;
        }
        .workers-permissions-hero-stat strong{
          font-size:22px;
          line-height:1;
          font-weight:950;
        }
        .workers-permissions-hero-stat small{
          margin-top:3px;
          font-size:10px;
          font-weight:950;
          color:#00796f;
          white-space:nowrap;
        }
        .workers-permission-groups-grid{
          display:grid;
          grid-template-columns:repeat(2,minmax(0,1fr));
          gap:10px;
        }
        .workers-permission-group-card{
          border:1px solid rgba(226,232,240,.96);
          border-radius:24px;
          background:#ffffff;
          box-shadow:0 10px 26px rgba(15,23,42,.035);
          overflow:hidden;
        }
        .workers-permission-group-title{
          padding:13px 14px 10px;
          border-bottom:1px solid #f1f5f9;
          background:linear-gradient(180deg,#ffffff,#fbfefd);
        }
        .workers-permission-group-title h5{
          margin:0;
          color:#0f172a;
          font-size:14px;
          font-weight:950;
          letter-spacing:-.02em;
        }
        .workers-permission-group-title p{
          margin:4px 0 0;
          color:#64748b;
          font-size:11px;
          line-height:1.35;
          font-weight:750;
        }
        .workers-permission-rows{
          display:grid;
          gap:0;
        }
        .workers-permission-full-row{
          min-height:58px;
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:14px;
          padding:11px 14px;
          border-top:1px solid #f8fafc;
        }
        .workers-permission-full-row:first-child{
          border-top:0;
        }
        .workers-permission-full-row > div{
          min-width:0;
        }
        .workers-permission-full-row strong{
          display:block;
          color:#0f172a;
          font-size:13px;
          font-weight:950;
          line-height:1.2;
        }
        .workers-permission-full-row span{
          display:block;
          margin-top:3px;
          color:#94a3b8;
          font-size:10px;
          font-weight:900;
          text-transform:uppercase;
          letter-spacing:.06em;
        }

        /* LOOP WORKER PERMISSIONS REVAMP V99 — compact permission command center */
        .workers-permissions-accordion{
          overflow:hidden;
        }
        .workers-permissions-accordion-head{
          width:100%;
          border:0;
          background:transparent;
          cursor:pointer;
          text-align:left;
        }
        .workers-permissions-accordion-actions{
          display:flex;
          align-items:center;
          gap:10px;
          flex:0 0 auto;
        }
        .workers-permissions-accordion-chip{
          min-height:34px;
          display:inline-flex;
          align-items:center;
          justify-content:center;
          padding:0 12px;
          border-radius:999px;
          border:1px solid rgba(0,103,95,.14);
          background:#ffffff;
          color:#00675f;
          font-size:11px;
          font-weight:950;
          letter-spacing:0;
          text-transform:none;
          white-space:nowrap;
        }
        .workers-permissions-accordion-actions i,
        .workers-permission-group-summary i{
          width:28px;
          height:28px;
          border-radius:999px;
          display:inline-flex;
          align-items:center;
          justify-content:center;
          border:1px solid rgba(0,103,95,.12);
          background:#f0fdfa;
          color:#00675f;
          font-style:normal;
          font-weight:950;
        }
        .workers-permissions-accordion-body{
          border-top:1px solid rgba(226,232,240,.85);
        }
        .workers-connected-accounts-toolbar{
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:12px;
          padding:12px 14px 0;
        }
        .workers-connected-accounts-toolbar span{
          color:#64748b;
          font-size:12px;
          font-weight:850;
        }
        .workers-permissions-command-center{
          display:grid;
          grid-template-columns:minmax(180px,1fr) minmax(360px,1.3fr) auto;
          gap:12px;
          align-items:center;
          border:1px solid rgba(226,232,240,.96);
          border-radius:24px;
          background:linear-gradient(180deg,#ffffff 0%,#fbfefd 100%);
          padding:14px;
          box-shadow:0 10px 26px rgba(15,23,42,.035);
        }
        .workers-permissions-command-copy span{
          display:block;
          color:#00675f;
          font-size:10px;
          font-weight:950;
          letter-spacing:.16em;
          text-transform:uppercase;
          margin-bottom:4px;
        }
        .workers-permissions-command-copy h4{
          margin:0;
          color:#0f172a;
          font-size:18px;
          font-weight:950;
          letter-spacing:-.035em;
        }
        .workers-permissions-command-copy p{
          margin:4px 0 0;
          color:#64748b;
          font-size:12px;
          line-height:1.35;
          font-weight:800;
        }
        .workers-permissions-command-fields{
          display:grid;
          grid-template-columns:1fr 1fr;
          gap:10px;
        }
        .workers-permissions-mini-field{
          min-height:64px;
          border-radius:18px !important;
          background:#ffffff !important;
        }
        .workers-permissions-mini-field select{
          min-height:34px !important;
          font-size:13px !important;
        }
        .workers-permissions-command-stat{
          min-width:92px;
          min-height:60px;
        }
        .workers-permission-groups-grid-compact{
          align-items:start;
        }
        .workers-permission-group-accordion{
          border-radius:20px;
        }
        .workers-permission-group-summary{
          width:100%;
          min-height:64px;
          display:grid;
          grid-template-columns:minmax(0,1fr) auto auto;
          align-items:center;
          gap:10px;
          padding:12px 14px;
          border:0;
          background:linear-gradient(180deg,#ffffff,#fbfefd);
          cursor:pointer;
          text-align:left;
        }
        .workers-permission-group-summary h5{
          margin:0;
          color:#0f172a;
          font-size:14px;
          font-weight:950;
          letter-spacing:-.02em;
        }
        .workers-permission-group-summary p{
          margin:3px 0 0;
          color:#64748b;
          font-size:11px;
          line-height:1.3;
          font-weight:750;
        }
        .workers-permission-group-count{
          min-width:42px;
          min-height:28px;
          padding:0 9px;
          border-radius:999px;
          display:inline-flex;
          align-items:center;
          justify-content:center;
          background:#f0fdfa;
          color:#00675f;
          border:1px solid rgba(0,103,95,.12);
          font-size:11px;
          font-weight:950;
        }
        .workers-permission-group-preview{
          min-height:44px;
          display:flex;
          align-items:center;
          gap:7px;
          flex-wrap:wrap;
          padding:0 14px 12px;
          color:#64748b;
          font-size:11px;
          font-weight:850;
        }
        .workers-permission-group-preview span,
        .workers-permission-group-preview em{
          min-height:24px;
          display:inline-flex;
          align-items:center;
          border-radius:999px;
          padding:0 9px;
          background:#f8fafc;
          border:1px solid #e2e8f0;
          color:#64748b;
          font-style:normal;
        }
        .workers-permission-group-accordion.is-collapsed .workers-permission-group-summary{
          border-bottom:0;
        }
        .workers-permission-group-accordion.is-open .workers-permission-group-summary{
          border-bottom:1px solid #f1f5f9;
        }
        @media(max-width:920px){
          .workers-permissions-command-center{
            grid-template-columns:1fr;
          }
          .workers-permissions-command-fields{
            grid-template-columns:1fr;
          }
          .workers-permissions-command-stat{
            justify-self:start;
          }
          .workers-permission-groups-grid{
            grid-template-columns:1fr !important;
          }
        }
        @media(max-width:640px){
          .workers-connected-accounts-head{
            align-items:center;
            padding:12px;
          }
          .workers-connected-accounts-head p{
            display:none;
          }
          .workers-permissions-accordion-chip{
            display:none;
          }
          .workers-connected-account-row{
            grid-template-columns:1fr;
          }
          .workers-connected-account-remove{
            width:100%;
            min-height:38px;
          }
          .workers-permissions-command-center{
            padding:12px;
            border-radius:20px;
          }
          .workers-permission-full-row{
            min-height:52px;
            padding:9px 12px;
          }
          .workers-permission-group-summary{
            min-height:58px;
            padding:10px 12px;
          }
        }

        /* PHASE 31H.7 — General cleanup: more minimal + visibility control */
        .workers-editor-actions-minimal{
          min-width:auto;
        }
        .workers-editor-modal-shell .workers-editor-head{
          padding-top:16px !important;
          padding-bottom:10px !important;
        }
        .workers-editor-modal-shell .workers-editor-body{
          background:#fbfefd !important;
        }
        .workers-editor-modal-shell .workers-editor-type-badge{
          background:#ffffff;
          color:#00675f;
        }
        .workers-profile-form-premium{
          gap:10px;
        }
        .workers-profile-form-premium > div,
        .workers-profile-card-premium{
          box-shadow:none;
          border-color:#e6edf2;
        }
        .workers-profile-card-premium{
          padding:12px;
          gap:10px;
          border-radius:22px;
        }
        .workers-profile-photo-frame{
          border-radius:20px;
          box-shadow:none;
        }
        .workers-profile-photo-button{
          min-height:40px;
          border-radius:14px !important;
        }
        .workers-profile-visibility-card{
          padding:10px;
          border-radius:16px;
          background:#ffffff;
        }
        .workers-type-premium-card{
          padding:12px;
          gap:9px;
          border-radius:20px !important;
        }
        .workers-type-premium-head span,
        .workers-profile-card-title span{
          margin-bottom:2px;
          font-size:9px;
          letter-spacing:.14em;
        }
        .workers-type-premium-head strong,
        .workers-profile-card-title strong{
          font-size:13px;
        }
        .workers-type-premium-selector{
          border-radius:16px;
          gap:4px;
          padding:3px;
        }
        .workers-type-premium-selector button{
          min-height:38px;
          border-radius:13px !important;
        }
        .workers-type-premium-helper{
          font-size:10.5px;
          color:#718096;
        }
        .workers-visibility-toggle-row{
          margin-top:2px;
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:14px;
          padding:10px 11px;
          border-radius:16px;
          border:1px solid #e2e8f0;
          background:#ffffff;
        }
        .workers-visibility-toggle-row strong{
          display:block;
          color:#0f172a;
          font-size:12px;
          font-weight:950;
        }
        .workers-visibility-toggle-row span{
          display:block;
          margin-top:2px;
          color:#64748b;
          font-size:10.5px;
          line-height:1.3;
          font-weight:750;
        }
        .workers-profile-fields-card-minimal{
          padding:12px;
          border-radius:20px !important;
        }
        .workers-profile-card-title-minimal{
          margin-bottom:10px;
        }
        .workers-profile-fields-grid-clean{
          gap:10px;
        }
        .workers-premium-field{
          gap:5px;
        }
        .workers-premium-field > span{
          font-size:10.5px;
          color:#64748b;
        }
        .workers-premium-field input,
        .workers-phone-premium-input{
          min-height:44px !important;
          border-radius:14px !important;
          font-size:13px;
        }
        .workers-phone-field-clean em{
          display:none;
        }
        .workers-phone-premium-input{
          grid-template-columns:132px minmax(0,1fr);
        }
        .workers-phone-country-wrap{
          min-height:44px;
          background:#ffffff;
        }
        .workers-phone-premium-input input{
          min-height:44px !important;
        }
        @media(max-width: 900px){
          .workers-connect-strip,
          .workers-connect-side,
          .workers-connect-primary,
          .workers-connect-status {
            width: 100%;
          }
          .workers-connect-side {
            justify-content: stretch;
          }
          .workers-connect-status {
            justify-content: flex-start;
          }
          .workers-connect-primary {
            justify-content: center;
          }
          .workers-access-methods-grid {
            grid-template-columns: 1fr;
          }
          .workers-access-method-card {
            min-height: 0;
          }
          .workers-permissions-waiting-card {
            display: grid;
          }
          .workers-qr-actions {
            grid-template-columns: 1fr;
          }
          .workers-permission-groups-grid{
            grid-template-columns:1fr;
          }
          .workers-permissions-hero{
            display:grid;
          }
          .workers-permissions-hero-stat{
            justify-self:start;
          }
        }

        @media(max-width: 900px){
          .workers-editor-modal-overlay{
            padding:10px;
            align-items:stretch;
          }
          .workers-editor-modal-shell{
            width:calc(100vw - 20px) !important;
            height:calc(100dvh - 20px) !important;
            max-height:calc(100dvh - 20px) !important;
            min-height:0 !important;
            border-radius:24px !important;
          }
          .workers-editor-title-row{
            align-items:flex-start;
          }
          .workers-editor-actions{
            width:100%;
            justify-content:flex-start;
            flex-wrap:wrap;
          }
          .workers-editor-actions-minimal{
            width:auto;
          }

          .workers-editor-overlay{
            padding: calc(env(safe-area-inset-top,0px) + 68px) 12px 12px !important;
          }
          .workers-editor-shell{
            width: calc(100vw - 24px) !important;
            height: calc(100vh - 88px) !important;
            max-height: calc(100vh - 88px) !important;
            border-radius: 24px !important;
          }
          .workers-general-tab .p-4 > .flex{
            grid-template-columns: 1fr !important;
          }
          .workers-editor-footer .grid{
            display: grid !important;
            grid-template-columns: 1fr 1fr !important;
          }
          .workers-editor-footer button{
            min-width: 0;
          }
        }


        /* PHASE 31H.6 — Premium General Tab Revamp */
        .workers-general-premium-tab{
          display:block;
        }
        .workers-general-premium-shell{
          display:grid;
          grid-template-columns:270px minmax(0,1fr);
          gap:18px;
          align-items:start;
          padding:0;
        }
        .workers-profile-card-premium,
        .workers-profile-form-premium > div{
          border:1px solid rgba(226,232,240,.96);
          background:#ffffff;
          border-radius:24px;
          box-shadow:0 12px 30px rgba(15,23,42,.04);
        }
        .workers-profile-card-premium{
          padding:14px;
          display:grid;
          gap:12px;
          background:linear-gradient(180deg,#ffffff 0%,#fbfefd 100%);
        }
        .workers-profile-photo-frame{
          position:relative;
          width:100%;
          aspect-ratio:1 / 1;
          overflow:hidden;
          border-radius:22px;
          border:1px solid #e2e8f0;
          background:#f8fafc;
          box-shadow:0 14px 32px rgba(15,23,42,.065);
        }
        .workers-profile-photo-img{
          width:100%;
          height:100%;
          object-fit:contain;
          object-position:center center;
          display:block;
          background:#ffffff;
        }
        .workers-profile-photo-overlay{
          position:absolute;
          left:10px;
          right:10px;
          bottom:10px;
          min-height:38px;
          border:1px solid rgba(255,255,255,.62) !important;
          background:rgba(255,255,255,.88) !important;
          color:#00675f !important;
          border-radius:999px !important;
          font-size:11px !important;
          font-weight:950 !important;
          opacity:0;
          transform:translateY(6px);
          transition:opacity .16s ease, transform .16s ease;
          backdrop-filter:blur(12px);
        }
        .workers-profile-photo-frame:hover .workers-profile-photo-overlay{
          opacity:1;
          transform:translateY(0);
        }
        .workers-profile-photo-button{
          min-height:44px;
          border:1px solid #e2e8f0 !important;
          background:#ffffff !important;
          color:#00675f !important;
          border-radius:16px !important;
          font-size:12px !important;
          font-weight:950 !important;
        }
        .workers-profile-visibility-card{
          display:flex;
          gap:10px;
          align-items:flex-start;
          border:1px solid #e2e8f0;
          background:#f8fafc;
          border-radius:18px;
          padding:12px;
        }
        .workers-profile-visibility-card > span{
          width:9px;
          height:9px;
          border-radius:999px;
          flex:0 0 auto;
          margin-top:4px;
          background:#10b981;
          box-shadow:0 0 0 5px rgba(16,185,129,.10);
        }
        .workers-profile-visibility-card > span.internal{
          background:#f59e0b;
          box-shadow:0 0 0 5px rgba(245,158,11,.10);
        }
        .workers-profile-visibility-card strong{
          display:block;
          color:#0f172a;
          font-size:12px;
          font-weight:950;
        }
        .workers-profile-visibility-card p{
          margin:3px 0 0;
          color:#64748b;
          font-size:11px;
          line-height:1.35;
          font-weight:750;
        }
        .workers-profile-form-premium{
          display:grid;
          gap:12px;
          min-width:0;
        }
        .workers-profile-section-heading{
          padding:16px;
          background:radial-gradient(circle at 0% 0%,rgba(0,103,95,.075),transparent 34%),#ffffff !important;
        }
        .workers-profile-section-heading span,
        .workers-type-premium-head span,
        .workers-profile-card-title span{
          display:block;
          color:#00675f;
          font-size:10px;
          font-weight:950;
          letter-spacing:.16em;
          text-transform:uppercase;
          margin-bottom:5px;
        }
        .workers-profile-section-heading h4{
          margin:0;
          color:#0f172a;
          font-size:22px;
          line-height:1;
          letter-spacing:-.05em;
          font-weight:950;
        }
        .workers-profile-section-heading p{
          margin:7px 0 0;
          color:#64748b;
          font-size:12px;
          line-height:1.45;
          font-weight:800;
          max-width:680px;
        }
        .workers-type-premium-card,
        .workers-profile-fields-card{
          padding:14px;
        }
        .workers-type-premium-card{
          display:grid;
          gap:10px;
          background:linear-gradient(180deg,#ffffff,#fbfefd) !important;
        }
        .workers-type-premium-head{
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:10px;
        }
        .workers-type-premium-head strong,
        .workers-profile-card-title strong{
          color:#0f172a;
          font-size:14px;
          font-weight:950;
        }
        .workers-type-premium-selector{
          display:grid;
          grid-template-columns:1fr 1fr;
          gap:6px;
          padding:4px;
          border:1px solid #e2e8f0;
          background:#f8fafc;
          border-radius:18px;
        }
        .workers-type-premium-selector button{
          min-height:44px;
          border:0 !important;
          background:transparent !important;
          color:#64748b !important;
          border-radius:14px !important;
          display:flex;
          align-items:center;
          justify-content:center;
          gap:8px;
          font-size:12px !important;
          font-weight:950 !important;
          transition:background .16s ease, color .16s ease, box-shadow .16s ease;
        }
        .workers-type-premium-selector button i{
          font-style:normal;
          font-size:13px;
        }
        .workers-type-premium-selector button.is-active{
          background:#ffffff !important;
          color:#00675f !important;
          box-shadow:0 10px 22px rgba(15,23,42,.055), inset 0 0 0 1px rgba(0,103,95,.13);
        }
        .workers-type-premium-helper{
          margin:0;
          color:#64748b;
          font-size:11px;
          line-height:1.45;
          font-weight:800;
        }
        .workers-profile-card-title{
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:10px;
          margin-bottom:12px;
        }
        .workers-profile-fields-grid{
          display:grid;
          grid-template-columns:1fr 1fr;
          gap:12px;
        }
        .workers-premium-field{
          display:grid;
          gap:6px;
          min-width:0;
        }
        .workers-premium-field.span-2{
          grid-column:1 / -1;
        }
        .workers-premium-field > span{
          color:#475569;
          font-size:11px;
          font-weight:950;
        }
        .workers-premium-field input,
        .workers-phone-premium-input{
          width:100%;
          min-height:46px !important;
          border:1px solid #e2e8f0 !important;
          border-radius:16px !important;
          background:#ffffff !important;
          color:#0f172a;
          font-size:13px;
          font-weight:850;
          box-shadow:none !important;
        }
        .workers-premium-field input{
          padding:0 13px !important;
        }
        .workers-premium-field input:focus,
        .workers-phone-premium-input:focus-within{
          outline:none;
          border-color:rgba(0,103,95,.32) !important;
          box-shadow:0 0 0 4px rgba(0,103,95,.07) !important;
        }
        .workers-premium-field em{
          color:#94a3b8;
          font-size:10px;
          line-height:1.35;
          font-weight:800;
          font-style:normal;
        }
        .workers-phone-premium-input{
          display:grid;
          grid-template-columns:142px minmax(0,1fr);
          align-items:center;
          overflow:hidden;
        }
        .workers-phone-country-wrap{
          position:relative;
          height:100%;
          min-height:46px;
          display:flex;
          align-items:center;
          justify-content:center;
          border-right:1px solid #e2e8f0;
          background:#fbfefd;
        }
        .workers-phone-country-wrap select{
          position:absolute;
          inset:0;
          opacity:0;
          cursor:pointer;
          width:100%;
          min-height:46px !important;
        }
        .workers-phone-country-wrap strong{
          color:#0f172a;
          font-size:12px;
          font-weight:950;
          pointer-events:none;
          white-space:nowrap;
        }
        .workers-phone-country-wrap strong::after{
          content:"⌄";
          margin-left:7px;
          color:#94a3b8;
          font-size:11px;
        }
        .workers-phone-premium-input input{
          border:0 !important;
          border-radius:0 !important;
          min-height:46px !important;
          padding:0 14px !important;
          box-shadow:none !important;
        }
        .workers-contact-card-premium{
          background:linear-gradient(180deg,#ffffff,#fbfefd) !important;
        }
        @media(max-width: 900px){
          .workers-general-premium-shell{
            grid-template-columns:1fr;
          }
          .workers-profile-card-premium{
            grid-template-columns:120px minmax(0,1fr);
            align-items:center;
          }
          .workers-profile-photo-frame{
            grid-row:span 2;
          }
          .workers-profile-photo-button{
            align-self:end;
          }
          .workers-profile-visibility-card{
            align-self:start;
          }
        }
        @media(max-width: 620px){
          .workers-profile-card-premium,
          .workers-profile-fields-grid{
            grid-template-columns:1fr;
          }
          .workers-premium-field.span-2{
            grid-column:auto;
          }
          .workers-phone-premium-input{
            grid-template-columns:118px minmax(0,1fr);
          }
        }


        /* =========================================================
           PHASE 31I — Unified workers modals
           Add person and edit person now use full-page portal overlays.
           This avoids section-only dimming and keeps the editor below/above
           the app header consistently across web, tablet and native.
           ========================================================= */
        .workers-add-modal-overlay,
        .workers-editor-modal-overlay{
          position: fixed !important;
          inset: 0 !important;
          width: 100vw !important;
          height: 100dvh !important;
          z-index: 18000 !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          padding: max(18px, env(safe-area-inset-top, 0px)) 18px max(18px, env(safe-area-inset-bottom, 0px)) !important;
          background: rgba(15, 23, 42, 0.42) !important;
          backdrop-filter: blur(12px) saturate(118%) !important;
          -webkit-backdrop-filter: blur(12px) saturate(118%) !important;
          overflow: auto !important;
          overscroll-behavior: contain !important;
        }

        .workers-add-modal-overlay::before,
        .workers-editor-modal-overlay::before{
          content: "";
          position: fixed;
          inset: 0;
          pointer-events: none;
          background:
            radial-gradient(circle at 18% 0%, rgba(0,103,95,.10), transparent 30%),
            radial-gradient(circle at 88% 8%, rgba(20,184,166,.08), transparent 26%);
        }

        .workers-add-modal-shell{
          position: relative !important;
          z-index: 1 !important;
          width: min(460px, calc(100vw - 36px)) !important;
          max-width: 460px !important;
          margin: auto !important;
          border-radius: 28px !important;
          border: 1px solid rgba(226,232,240,.98) !important;
          background: linear-gradient(180deg, #ffffff 0%, #fbfefd 100%) !important;
          box-shadow: 0 28px 82px rgba(15,23,42,.24) !important;
          overflow: hidden !important;
        }

        .workers-add-modal-head{
          min-height: 64px;
          padding: 16px 18px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          border-bottom: 1px solid rgba(226,232,240,.82);
          background: rgba(255,255,255,.96);
        }
        .workers-add-modal-head strong{
          display: block;
          color: #0f172a;
          font-size: 18px;
          line-height: 1.1;
          font-weight: 950;
          letter-spacing: -.035em;
        }
        .workers-add-modal-head span{
          display: block;
          margin-top: 3px;
          color: #64748b;
          font-size: 12px;
          font-weight: 750;
        }
        .workers-add-modal-close{
          width: 38px !important;
          height: 38px !important;
          min-height: 38px !important;
          padding: 0 !important;
          border-radius: 999px !important;
          border: 1px solid #e2e8f0 !important;
          background: #ffffff !important;
          color: #475569 !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          box-shadow: none !important;
        }
        .workers-add-modal-body{
          padding: 16px 18px 14px;
          display: grid;
          gap: 13px;
        }
        .workers-add-modal-field label{
          display: block;
          margin-bottom: 6px;
          color: #475569;
          font-size: 12px;
          font-weight: 950;
        }
        .workers-add-modal-field input{
          width: 100%;
          min-height: 46px !important;
          padding: 0 14px !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 16px !important;
          background: #ffffff !important;
          color: #0f172a !important;
          font-size: 14px !important;
          font-weight: 800 !important;
          outline: none !important;
        }
        .workers-add-modal-actions{
          padding: 0 18px 18px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }
        .workers-add-modal-actions button{
          min-height: 46px !important;
          border-radius: 999px !important;
          font-size: 13px !important;
          font-weight: 950 !important;
        }
        .workers-add-modal-cancel{
          border: 1px solid #e2e8f0 !important;
          background: #ffffff !important;
          color: #334155 !important;
        }
        .workers-add-modal-submit{
          border: 0 !important;
          background: #00675f !important;
          color: #ffffff !important;
          box-shadow: 0 12px 28px rgba(0,103,95,.16) !important;
        }

        .workers-editor-modal-shell{
          position: relative !important;
          z-index: 1 !important;
          width: min(1120px, calc(100vw - 36px)) !important;
          height: min(820px, calc(100dvh - 36px)) !important;
          max-height: calc(100dvh - 36px) !important;
          min-height: min(620px, calc(100dvh - 36px)) !important;
          margin: auto !important;
        }

        .workers-editor-modal-shell .workers-editor-head{
          position: sticky !important;
          top: 0 !important;
          z-index: 4 !important;
          background: rgba(255,255,255,.98) !important;
          backdrop-filter: blur(16px) !important;
          -webkit-backdrop-filter: blur(16px) !important;
        }

        @media (max-width: 900px){
          .workers-add-modal-overlay,
          .workers-editor-modal-overlay{
            align-items: flex-start !important;
            padding: max(14px, env(safe-area-inset-top, 0px)) 10px max(14px, env(safe-area-inset-bottom, 0px)) !important;
          }
          .workers-add-modal-shell{
            width: min(460px, calc(100vw - 20px)) !important;
          }
          .workers-editor-modal-shell{
            width: calc(100vw - 20px) !important;
            height: calc(100dvh - 28px) !important;
            max-height: calc(100dvh - 28px) !important;
            min-height: 0 !important;
            border-radius: 24px !important;
          }
        }

        @media (max-width: 520px){
          .workers-add-modal-actions{
            grid-template-columns: 1fr;
          }
          .workers-add-type > div{
            grid-template-columns: 1fr;
          }
        }



        /* =========================================================
           LOOP MOBILE STAFF FIX — Equipo as real mobile feed
           - compact hero
           - compact stats
           - clean worker cards
           - add/edit modals become bottom sheets/fullscreen mobile
           ========================================================= */
        @media (max-width: 720px) {
          .workers-pro-shell {
            gap: 12px !important;
            padding-bottom: calc(84px + env(safe-area-inset-bottom, 0px));
          }

          .workers-modern-card,
          .workers-pro-shell .bg-white,
          .space-y-4 .bg-white {
            border-radius: 20px !important;
            box-shadow: 0 10px 28px rgba(15,23,42,.045) !important;
          }

          .workers-modern-hero {
            padding: 12px !important;
            gap: 10px !important;
          }
          .workers-modern-hero-main {
            display: grid !important;
            gap: 10px !important;
          }
          .workers-kicker {
            display: none !important;
          }
          .workers-modern-hero h3 {
            font-size: 22px !important;
            letter-spacing: -.045em !important;
          }
          .workers-modern-hero p {
            margin-top: 5px !important;
            font-size: 12px !important;
            line-height: 1.35 !important;
            max-width: 100% !important;
          }
          .workers-primary-action {
            width: 100% !important;
            min-height: 40px !important;
            justify-content: center !important;
            border-radius: 14px !important;
          }

          .workers-stats-row {
            display: grid !important;
            grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
            gap: 7px !important;
          }
          .workers-stats-row > div {
            min-height: 40px !important;
            border-radius: 14px !important;
            padding: 8px 7px !important;
            justify-content: center !important;
            text-align: center !important;
            display: grid !important;
            gap: 1px !important;
          }
          .workers-stats-row strong {
            font-size: 17px !important;
            line-height: 1 !important;
          }
          .workers-stats-row span {
            font-size: 9px !important;
            line-height: 1.05 !important;
            letter-spacing: 0 !important;
          }
          .workers-public-toggle {
            grid-column: 1 / -1 !important;
            grid-template-columns: minmax(0,1fr) auto !important;
            display: grid !important;
            align-items: center !important;
            text-align: left !important;
            min-height: 38px !important;
            padding: 8px 10px !important;
          }
          .workers-public-toggle span {
            font-size: 12px !important;
            color: #334155 !important;
          }

          .workers-migration-summary {
            grid-template-columns: 1fr !important;
            padding: 11px !important;
            border-radius: 16px !important;
          }
          .workers-migration-summary em {
            justify-self: start !important;
          }
          .workers-migration-actions {
            justify-content: flex-start !important;
          }
          .workers-migration-backfill-result {
            align-items: flex-start !important;
            flex-direction: column !important;
          }
          .workers-person-access-line {
            max-width: 100% !important;
            font-size: 9px !important;
            padding: 3px 7px !important;
          }
          .workers-migration-grid {
            grid-template-columns: 1fr !important;
          }

          .workers-invite-channel-grid {
            grid-template-columns: 1fr !important;
          }
          .workers-person-actions-3a3 {
            grid-template-columns: 1fr 1fr !important;
          }
          .workers-person-actions-3a3 .edit,
          .workers-person-actions-3a3 .delete {
            grid-column: auto !important;
          }

          .workers-filter-tabs {
            width: 100% !important;
            height: 40px !important;
            padding: 3px !important;
            gap: 3px !important;
            overflow: hidden !important;
          }
          .workers-filter-tabs button {
            padding: 0 6px !important;
            font-size: 11px !important;
          }

          .workers-modern-list {
            gap: 8px !important;
          }
          .workers-person-row {
            grid-template-columns: 42px minmax(0,1fr) auto !important;
            gap: 10px !important;
            padding: 10px !important;
            min-height: 72px !important;
            border-radius: 18px !important;
            box-shadow: 0 8px 22px rgba(15,23,42,.04) !important;
          }
          .workers-avatar {
            width: 42px !important;
            height: 42px !important;
            border-radius: 14px !important;
          }
          .workers-person-title {
            gap: 6px !important;
          }
          .workers-person-title strong {
            font-size: 13px !important;
          }
          .workers-person-title span {
            padding: 2px 6px !important;
            font-size: 8px !important;
          }
          .workers-person-main p {
            margin-top: 2px !important;
            font-size: 11px !important;
          }
          .workers-person-main small {
            margin-top: 2px !important;
            font-size: 10px !important;
            max-width: 100% !important;
          }
          .workers-person-meta {
            display: none !important;
          }
          .workers-person-actions {
            grid-column: auto !important;
            grid-template-columns: 1fr !important;
            justify-items: end !important;
            gap: 2px !important;
          }
          .workers-person-actions .edit {
            min-height: 32px !important;
            padding: 0 11px !important;
            font-size: 10px !important;
          }
          .workers-person-actions .delete {
            min-height: 26px !important;
            font-size: 10px !important;
          }

          .workers-empty-state {
            border-radius: 18px !important;
            padding: 16px !important;
          }
          .workers-empty-state strong {
            font-size: 15px !important;
          }
          .workers-empty-state span {
            font-size: 12px !important;
            line-height: 1.35 !important;
          }
          .workers-empty-state button {
            width: 100% !important;
          }

          .workers-add-modal-overlay {
            align-items: end !important;
            padding: 10px !important;
          }
          .workers-add-modal-shell {
            width: 100% !important;
            max-width: none !important;
            border-radius: 26px 26px 18px 18px !important;
            max-height: min(82dvh, 680px) !important;
            overflow: auto !important;
          }
          .workers-add-modal-head {
            padding: 16px 16px 12px !important;
          }
          .workers-add-modal-head strong {
            font-size: 18px !important;
          }
          .workers-add-modal-head span {
            font-size: 12px !important;
          }
          .workers-add-modal-body {
            padding: 14px 16px !important;
          }
          .workers-add-modal-actions {
            padding: 12px 16px calc(14px + env(safe-area-inset-bottom,0px)) !important;
            position: sticky !important;
            bottom: 0 !important;
            background: rgba(255,255,255,.96) !important;
            backdrop-filter: blur(14px) !important;
          }

          .workers-editor-modal-overlay {
            padding: 0 !important;
            align-items: stretch !important;
          }
          .workers-editor-modal-shell,
          .workers-editor-shell {
            width: 100% !important;
            height: 100dvh !important;
            max-height: 100dvh !important;
            border-radius: 0 !important;
            border: 0 !important;
          }
          .workers-editor-head {
            padding: calc(10px + env(safe-area-inset-top,0px)) 12px 8px !important;
          }
          .workers-editor-title-row,
          .workers-editor-title-left {
            gap: 8px !important;
          }
          .workers-back-button {
            min-height: 32px !important;
            padding: 0 10px !important;
            font-size: 11px !important;
            border-radius: 999px !important;
          }
          .workers-editor-kicker,
          .workers-editor-actions-minimal {
            display: none !important;
          }
          .workers-editor-name {
            font-size: 17px !important;
            line-height: 1.08 !important;
          }
          .workers-editor-subtitle {
            font-size: 11px !important;
            margin-top: 2px !important;
          }
          .workers-editor-head .mt-3 {
            margin-top: 8px !important;
            gap: 6px !important;
            padding-bottom: 2px !important;
            scrollbar-width: none !important;
          }
          .workers-editor-head .mt-3::-webkit-scrollbar { display: none !important; }
          .workers-editor-head .mt-3 button {
            min-height: 34px !important;
            padding: 0 12px !important;
            font-size: 11px !important;
            flex: 0 0 auto !important;
          }
          .workers-editor-body {
            padding: 12px !important;
            padding-bottom: calc(86px + env(safe-area-inset-bottom,0px)) !important;
          }
          .workers-editor-footer {
            padding: 10px 12px calc(12px + env(safe-area-inset-bottom,0px)) !important;
          }
          .workers-editor-footer button {
            min-height: 44px !important;
            font-size: 13px !important;
          }

          .workers-general-premium-shell,
          .workers-compensation-hero,
          .workers-compensation-grid,
          .workers-profile-fields-grid,
          .workers-profile-fields-grid-clean,
          .workers-permission-groups-grid {
            grid-template-columns: 1fr !important;
          }
          .workers-profile-card-premium,
          .workers-profile-fields-card,
          .workers-type-premium-card,
          .workers-permission-group-card,
          .workers-compensation-card,
          .workers-compensation-hero-copy,
          .workers-compensation-preview,
          .workers-services-hero,
          .workers-schedule-summary {
            border-radius: 18px !important;
            padding: 14px !important;
          }
          .workers-profile-card-premium {
            display: grid !important;
            grid-template-columns: 72px minmax(0,1fr) !important;
            align-items: center !important;
            gap: 12px !important;
          }
          .workers-profile-photo-frame {
            width: 72px !important;
            height: 72px !important;
            border-radius: 22px !important;
          }
          .workers-profile-photo-button,
          .workers-photo-status {
            grid-column: 2 !important;
            justify-self: start !important;
            margin: 0 !important;
          }
          .workers-profile-visibility-card {
            grid-column: 1 / -1 !important;
            padding: 10px !important;
            border-radius: 14px !important;
          }
          .workers-type-premium-selector {
            grid-template-columns: 1fr 1fr !important;
            gap: 6px !important;
          }
          .workers-type-premium-selector button {
            min-height: 42px !important;
          }
          .workers-visibility-toggle-row {
            padding: 10px !important;
            border-radius: 14px !important;
          }
          .workers-premium-field.span-2,
          .workers-compensation-card.span-2 {
            grid-column: auto !important;
          }
          .workers-phone-premium-input {
            grid-template-columns: 112px minmax(0,1fr) !important;
          }
          .workers-services-toolbar {
            grid-template-columns: 1fr !important;
          }
          .workers-services-list,
          .workers-schedule-list,
          .workers-permission-rows {
            gap: 8px !important;
          }
          .workers-service-row,
          .workers-day-row,
          .workers-permission-full-row,
          .workers-experience-price-row,
          .workers-visit-row {
            border-radius: 16px !important;
            padding: 11px !important;
          }
          .workers-time-grid,
          .workers-time-grid.compact,
          .workers-tier-row,
          .workers-experience-price-row {
            grid-template-columns: 1fr !important;
          }
        }

        /* =========================================================
           LOOP WORKERS HOTFIX 2 — Equipo & Accesos premium polish
           - Card hierarchy cleaner
           - Access-first filters
           - WhatsApp visibly Próximamente without looking broken
           ========================================================= */
        .workers-pro-shell {
          background: linear-gradient(180deg, #fffdf8 0%, #fbf7ef 100%) !important;
          padding: 2px 0 24px !important;
        }
        .workers-modern-card,
        .workers-person-row,
        .workers-empty-state {
          border-color: rgba(226,232,240,.82) !important;
          background: rgba(255,255,255,.92) !important;
          box-shadow: 0 18px 48px rgba(15,23,42,.055) !important;
        }
        .workers-modern-hero {
          padding: 20px !important;
          border-radius: 32px !important;
          background:
            radial-gradient(circle at 0% 0%, rgba(0,103,95,.10), transparent 32%),
            linear-gradient(135deg, rgba(255,255,255,.98), rgba(255,253,248,.94)) !important;
        }
        .workers-modern-hero-main {
          align-items: center !important;
        }
        .workers-modern-hero h3 {
          font-size: clamp(26px, 3vw, 34px) !important;
          letter-spacing: -.06em !important;
        }
        .workers-modern-hero p {
          max-width: 720px !important;
          font-size: 13.5px !important;
        }
        .workers-primary-action {
          min-height: 46px !important;
          padding: 0 20px !important;
          border-radius: 999px !important;
          background: #00675f !important;
          box-shadow: 0 16px 34px rgba(0,103,95,.18) !important;
        }
        .workers-stats-row {
          grid-template-columns: repeat(5, minmax(0, 1fr)) minmax(190px, auto) !important;
          gap: 10px !important;
        }
        .workers-stats-row > div {
          min-height: 58px !important;
          border-radius: 22px !important;
          background: rgba(255,255,255,.84) !important;
          align-items: center !important;
          padding: 12px 14px !important;
        }
        .workers-stats-row strong {
          font-size: 23px !important;
          letter-spacing: -.045em !important;
        }
        .workers-stats-row span {
          font-size: 10.5px !important;
          font-weight: 950 !important;
          text-transform: uppercase !important;
          letter-spacing: .08em !important;
          line-height: 1.1 !important;
        }
        .workers-public-toggle span {
          text-transform: none !important;
          letter-spacing: 0 !important;
          font-size: 12px !important;
          color: #334155 !important;
        }
        .workers-migration-summary {
          border-radius: 24px !important;
          padding: 14px 16px !important;
          background:
            radial-gradient(circle at 0% 0%, rgba(0,103,95,.11), transparent 34%),
            linear-gradient(135deg, #ffffff 0%, #f4fffb 100%) !important;
        }
        .workers-filter-tabs {
          width: 100% !important;
          max-width: 860px !important;
          height: auto !important;
          min-height: 46px !important;
          grid-template-columns: repeat(6, minmax(max-content, 1fr)) !important;
          overflow-x: auto !important;
          scrollbar-width: none !important;
          justify-self: start !important;
          border-radius: 999px !important;
          background: rgba(248,250,252,.92) !important;
        }
        .workers-filter-tabs::-webkit-scrollbar { display: none !important; }
        .workers-filter-tabs button {
          min-height: 36px !important;
          padding: 0 14px !important;
          white-space: nowrap !important;
        }
        .workers-modern-list {
          gap: 10px !important;
        }
        .workers-person-row {
          min-height: 88px !important;
          grid-template-columns: 56px minmax(230px, 1fr) minmax(150px, .58fr) minmax(250px, auto) !important;
          gap: 14px !important;
          padding: 15px !important;
          border-radius: 28px !important;
          transition: transform .16s ease, box-shadow .16s ease, border-color .16s ease !important;
        }
        .workers-person-row:hover {
          transform: translateY(-1px) !important;
          border-color: rgba(0,103,95,.16) !important;
          box-shadow: 0 22px 54px rgba(15,23,42,.07) !important;
        }
        .workers-avatar {
          width: 56px !important;
          height: 56px !important;
          border-radius: 20px !important;
        }
        .workers-person-title strong {
          font-size: 15px !important;
        }
        .workers-person-main p,
        .workers-person-main small {
          font-size: 11.5px !important;
        }
        .workers-person-access-line {
          margin-top: 7px !important;
          border-radius: 999px !important;
          background: #f8fafc !important;
          color: #475569 !important;
        }
        .workers-person-access-line.complete,
        .workers-person-access-line.pending_ready,
        .workers-person-access-line.ready {
          background: #ecfdf5 !important;
          color: #00675f !important;
          border-color: rgba(0,103,95,.15) !important;
        }
        .workers-person-access-line.missing,
        .workers-person-access-line.needs_confirm {
          background: #fffbeb !important;
          color: #92400e !important;
          border-color: #fde68a !important;
        }
        .workers-person-meta {
          border: 1px solid #e2e8f0 !important;
          background: #fbfefd !important;
          border-radius: 20px !important;
          padding: 10px !important;
        }
        .workers-whatsapp-mini {
          margin-top: 2px !important;
          background: #f8fafc !important;
          color: #64748b !important;
        }
        .workers-person-actions-3a3 {
          grid-template-columns: minmax(112px, 1fr) minmax(126px, 1fr) auto auto !important;
          gap: 7px !important;
        }
        .workers-person-actions button {
          min-height: 38px !important;
          box-shadow: none !important;
        }
        .workers-person-actions .copy-link {
          background: #00675f !important;
          border-color: #00675f !important;
          color: #ffffff !important;
        }
        .workers-person-actions .whatsapp-standby {
          background: #f8fafc !important;
          border-color: #e2e8f0 !important;
          color: #64748b !important;
        }
        .workers-person-actions .whatsapp-standby:after {
          content: "";
          display: inline-block;
          width: 6px;
        }
        .workers-person-actions .edit {
          border-color: #e2e8f0 !important;
        }
        .workers-person-actions .delete {
          opacity: .72 !important;
        }
        @media (max-width: 1100px) {
          .workers-stats-row {
            grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
          }
          .workers-public-toggle { grid-column: span 3 !important; }
          .workers-person-row {
            grid-template-columns: 52px minmax(0, 1fr) !important;
          }
          .workers-person-meta,
          .workers-person-actions {
            grid-column: 2 !important;
          }
          .workers-person-actions-3a3 {
            grid-template-columns: repeat(4, minmax(0, auto)) !important;
            justify-content: start !important;
          }
        }
        @media (max-width: 720px) {
          .workers-modern-hero {
            border-radius: 24px !important;
            padding: 14px !important;
          }
          .workers-stats-row {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
          .workers-public-toggle { grid-column: 1 / -1 !important; }
          .workers-filter-tabs {
            display: flex !important;
            width: 100% !important;
            max-width: 100% !important;
            overflow-x: auto !important;
            padding: 4px !important;
          }
          .workers-filter-tabs button {
            flex: 0 0 auto !important;
            padding: 0 12px !important;
          }
          .workers-person-row {
            grid-template-columns: 44px minmax(0, 1fr) !important;
            border-radius: 22px !important;
          }
          .workers-avatar {
            width: 44px !important;
            height: 44px !important;
            border-radius: 16px !important;
          }
          .workers-person-meta,
          .workers-person-actions {
            grid-column: 1 / -1 !important;
          }
          .workers-person-actions-3a3 {
            grid-template-columns: 1fr 1fr !important;
          }
          .workers-person-actions-3a3 .edit,
          .workers-person-actions-3a3 .delete {
            grid-column: auto !important;
          }
        }


      

        /* =========================================================
           LOOP WORKER POPUPS — TOP LAYER + HEADER GUARD HOTFIX
           The worker editor/add modals must sit above the global header,
           while their content starts below it. Nested pickers (PhoneKit,
           country selector, date blockers) use a higher layer so they are
           never clipped by the modal body/footer.
           ========================================================= */
        .workers-add-modal-overlay,
        .workers-editor-modal-overlay{
          z-index: 2147482400 !important;
          isolation: isolate !important;
          align-items: flex-start !important;
          justify-content: center !important;
          padding: calc(env(safe-area-inset-top, 0px) + 76px) 18px max(18px, env(safe-area-inset-bottom, 0px)) !important;
        }

        .workers-add-modal-shell,
        .workers-editor-modal-shell{
          margin: 0 auto !important;
          z-index: 1 !important;
        }

        .workers-editor-modal-shell{
          height: min(820px, calc(100dvh - 96px)) !important;
          max-height: calc(100dvh - 96px) !important;
          min-height: min(620px, calc(100dvh - 96px)) !important;
        }

        .workers-add-modal-shell{
          max-height: calc(100dvh - 96px) !important;
        }

        .workers-editor-body{
          overscroll-behavior: contain !important;
        }

        .workers-editor-head{
          z-index: 10 !important;
        }

        .workers-editor-modal-overlay .loop-phone-menu,
        .workers-add-modal-overlay .loop-phone-menu,
        body > .loop-phone-menu.is-portal{
          z-index: 2147483647 !important;
        }

        .workers-editor-modal-overlay .loop-phone-control,
        .workers-add-modal-overlay .loop-phone-control{
          overflow: visible !important;
        }

        .workers-editor-modal-overlay .loop-phone-input,
        .workers-add-modal-overlay .loop-phone-input{
          position: relative !important;
          z-index: 20 !important;
        }

        .workers-editor-modal-overlay .loop-phone-input:focus-within,
        .workers-add-modal-overlay .loop-phone-input:focus-within{
          z-index: 2147482500 !important;
        }

        @media (max-width: 900px){
          .workers-add-modal-overlay,
          .workers-editor-modal-overlay{
            padding: calc(env(safe-area-inset-top, 0px) + 66px) 10px max(12px, env(safe-area-inset-bottom, 0px)) !important;
          }
          .workers-editor-modal-shell{
            height: calc(100dvh - 84px) !important;
            max-height: calc(100dvh - 84px) !important;
            min-height: 0 !important;
          }
          .workers-add-modal-shell{
            max-height: calc(100dvh - 84px) !important;
          }
        }

        @media (max-width: 560px){
          .workers-add-modal-overlay,
          .workers-editor-modal-overlay{
            padding: calc(env(safe-area-inset-top, 0px) + 58px) 8px max(10px, env(safe-area-inset-bottom, 0px)) !important;
          }
          .workers-editor-modal-shell{
            width: calc(100vw - 16px) !important;
            height: calc(100dvh - 74px) !important;
            max-height: calc(100dvh - 74px) !important;
            border-radius: 22px !important;
          }
        }

        /* HOTFIX 18 — Premium compact services + compensation */
        .workers-services-comp-tab{
          display:grid;
          gap:24px;
          max-width:960px;
          margin:0 auto;
          padding:10px 0 0;
        }
        .workers-service-comp-premium-shell{
          display:grid;
          gap:18px;
          padding:0;
          border:0;
          background:transparent;
          box-shadow:none;
        }
        .workers-service-comp-premium-head h4{
          margin:0;
          color:#0f172a;
          font-size:20px;
          line-height:1.05;
          font-weight:950;
          letter-spacing:-.035em;
        }
        .workers-service-comp-summary-row{
          display:grid;
          grid-template-columns:minmax(0,1fr) minmax(0,1.05fr) minmax(170px,.72fr);
          gap:14px;
          align-items:stretch;
          max-width:760px;
        }
        .workers-service-comp-mini-card,
        .workers-service-comp-mini-action{
          min-height:58px;
          border:1px solid #e2e8f0;
          border-radius:18px;
          background:#fff;
          padding:0 16px;
          display:flex;
          align-items:center;
          gap:13px;
          text-align:left;
          box-shadow:0 12px 26px rgba(15,23,42,.035);
        }
        .workers-service-comp-mini-card.is-compensation{
          background:#f0fdfa;
          border-color:rgba(0,103,95,.16);
        }
        .workers-service-comp-mini-action{
          cursor:pointer;
          color:#0f172a;
          font:inherit;
          transition:transform .14s ease, border-color .14s ease, box-shadow .14s ease;
        }
        .workers-service-comp-mini-action:hover{
          transform:translateY(-1px);
          border-color:rgba(0,103,95,.18);
          box-shadow:0 14px 30px rgba(15,23,42,.055);
        }
        .workers-service-comp-mini-icon{
          width:34px;
          height:34px;
          border-radius:12px;
          flex:0 0 auto;
          display:grid;
          place-items:center;
          background:#ecfdf5;
          color:#00675f;
          font-size:15px;
          font-weight:950;
        }
        .workers-service-comp-mini-card div{
          min-width:0;
          display:grid;
          gap:2px;
        }
        .workers-service-comp-mini-card strong,
        .workers-service-comp-mini-action strong{
          color:#0f172a;
          font-size:14px;
          line-height:1.1;
          font-weight:950;
          letter-spacing:-.015em;
          white-space:nowrap;
          overflow:hidden;
          text-overflow:ellipsis;
        }
        .workers-service-comp-mini-card.is-compensation strong{
          color:#00675f;
        }
        .workers-service-comp-mini-card span:not(.workers-service-comp-mini-icon){
          color:#64748b;
          font-size:12px;
          line-height:1.1;
          font-weight:750;
          white-space:nowrap;
          overflow:hidden;
          text-overflow:ellipsis;
        }
        .workers-service-comp-inline-note{
          border:1px solid rgba(148,163,184,.22);
          background:#f8fafc;
          border-radius:18px;
          padding:11px 14px;
          display:flex;
          gap:8px;
          align-items:center;
          color:#64748b;
          font-size:12px;
          font-weight:800;
        }
        .workers-service-comp-inline-note strong{
          color:#0f172a;
          font-size:12px;
          font-weight:950;
        }
        .workers-service-chip-card{
          border-top:1px solid #e2e8f0;
          padding-top:20px;
          display:grid;
          gap:16px;
        }
        .workers-service-chip-head span{
          display:block;
          color:#00675f;
          font-size:11px;
          font-weight:950;
          letter-spacing:.18em;
          text-transform:uppercase;
        }
        .workers-service-chip-toolbar{
          display:grid;
          grid-template-columns:minmax(0,1fr) auto auto;
          gap:10px;
          align-items:center;
        }
        .workers-service-chip-search{
          min-height:44px;
          border:1px solid #e2e8f0;
          border-radius:999px;
          background:#fff;
          padding:0 16px;
          display:flex;
          align-items:center;
          gap:10px;
          color:#64748b;
          box-shadow:0 10px 24px rgba(15,23,42,.025);
        }
        .workers-service-chip-search span{
          font-size:20px;
          line-height:1;
          color:#64748b;
          transform:translateY(-1px);
        }
        .workers-service-chip-search input{
          width:100%;
          border:0;
          outline:0;
          background:transparent;
          color:#0f172a;
          font-size:14px;
          font-weight:800;
        }
        .workers-service-chip-search input::placeholder{
          color:#94a3b8;
        }
        .workers-service-chip-toolbar button{
          min-height:44px;
          border-radius:999px;
          border:1px solid #e2e8f0;
          background:#fff;
          color:#334155;
          padding:0 16px;
          font-size:12px;
          font-weight:950;
          white-space:nowrap;
          cursor:pointer;
        }
        .workers-service-chip-toolbar button.primary-soft{
          background:#ecfdf5;
          border-color:rgba(0,103,95,.16);
          color:#00675f;
        }
        .workers-service-chip-grid{
          display:flex;
          flex-wrap:wrap;
          gap:10px 12px;
          align-items:center;
        }
        .workers-service-chip{
          min-height:48px;
          min-width:158px;
          border-radius:16px;
          border:1px solid #e2e8f0;
          padding:0 17px;
          display:inline-flex;
          align-items:center;
          gap:12px;
          background:#fff;
          color:#334155;
          cursor:pointer;
          box-shadow:0 10px 24px rgba(15,23,42,.025);
          transition:transform .14s ease, border-color .14s ease, background .14s ease, box-shadow .14s ease;
        }
        .workers-service-chip:hover{
          transform:translateY(-1px);
          box-shadow:0 14px 28px rgba(15,23,42,.05);
        }
        .workers-service-chip.is-selected{
          background:#e6fff4;
          border-color:rgba(0,103,95,.24);
          color:#003f3a;
        }
        .workers-service-chip.is-muted{
          background:#f8fafc;
          border-color:#e5e7eb;
          color:#475569;
        }
        .workers-service-chip-icon{
          width:24px;
          height:24px;
          border-radius:999px;
          display:grid;
          place-items:center;
          flex:0 0 auto;
          border:1px solid #dbe4ef;
          background:#fff;
          color:#64748b;
          font-size:13px;
          font-weight:950;
        }
        .workers-service-chip.is-selected .workers-service-chip-icon{
          background:#00675f;
          border-color:#00675f;
          color:#fff;
        }
        .workers-service-chip strong{
          min-width:0;
          color:inherit;
          font-size:13px;
          line-height:1;
          font-weight:950;
          white-space:nowrap;
          overflow:hidden;
          text-overflow:ellipsis;
        }
        @media(max-width: 900px){
          .workers-service-comp-summary-row{
            grid-template-columns:1fr;
            max-width:none;
          }
          .workers-service-chip-toolbar{
            grid-template-columns:1fr;
          }
          .workers-service-chip-toolbar button{
            width:100%;
          }
          .workers-service-chip-grid{
            display:grid;
            grid-template-columns:1fr;
          }
          .workers-service-chip{
            width:100%;
            justify-content:flex-start;
          }
        }



        /* LOOP HOTFIX 20 — WorkerSection premium command center */
        .workers-team-panel{
          border-radius:32px !important;
          padding:22px !important;
          background:linear-gradient(135deg,rgba(255,255,255,.96),rgba(247,253,250,.92)) !important;
          border:1px solid rgba(215,226,232,.95) !important;
          box-shadow:0 22px 58px rgba(15,23,42,.055) !important;
          display:grid !important;
          gap:16px !important;
        }
        .workers-team-panel.is-embedded{
          padding:18px !important;
        }
        .workers-team-page-title{
          display:flex;
          align-items:flex-end;
          justify-content:space-between;
          gap:18px;
          padding:0 2px 4px;
        }
        .workers-team-page-title > div{min-width:0;}
        .workers-team-page-title .workers-kicker,
        .workers-team-panel .workers-kicker{
          display:block;
          letter-spacing:.22em;
          text-transform:uppercase;
          color:#00675f;
          font-size:11px;
          font-weight:950;
          margin-bottom:4px;
        }
        .workers-team-page-title h3{
          margin:0;
          color:#0f172a;
          font-size:clamp(28px,3vw,44px);
          line-height:.98;
          letter-spacing:-.055em;
          font-weight:950;
        }
        .workers-team-page-title p{
          margin:8px 0 0;
          max-width:720px;
          color:#64748b;
          font-size:13px;
          font-weight:800;
          line-height:1.35;
        }
        .workers-team-toolbar{
          display:grid;
          grid-template-columns:minmax(0,1fr) auto;
          align-items:center;
          gap:14px;
        }
        .workers-team-summary-chips{
          display:flex;
          flex-wrap:wrap;
          gap:10px;
          align-items:center;
          min-width:0;
        }
        .workers-team-chip{
          min-height:42px;
          border-radius:16px;
          border:1px solid rgba(226,232,240,.96);
          background:rgba(255,255,255,.88);
          display:inline-flex;
          align-items:center;
          gap:9px;
          padding:8px 12px;
          box-shadow:0 12px 28px rgba(15,23,42,.035);
          color:#334155;
          white-space:nowrap;
        }
        .workers-team-chip strong{
          color:#00675f;
          font-size:19px;
          line-height:1;
          font-weight:950;
          letter-spacing:-.04em;
        }
        .workers-team-chip span{
          color:#64748b;
          font-size:10px;
          line-height:1;
          font-weight:950;
          letter-spacing:.08em;
          text-transform:uppercase;
        }
        .workers-team-controls{
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:12px;
          flex-wrap:wrap;
        }
        .workers-team-public-toggle{
          border:1px solid rgba(226,232,240,.96);
          background:rgba(255,255,255,.82);
          border-radius:18px;
          padding:8px 10px 8px 14px;
          display:inline-flex;
          align-items:center;
          gap:14px;
          color:#0f172a;
          font-size:12px;
          font-weight:950;
          box-shadow:0 10px 24px rgba(15,23,42,.028);
        }
        .workers-team-actions-menu{
          width:min(100%,520px) !important;
          margin:0 !important;
          border-radius:18px !important;
          background:rgba(255,255,255,.78) !important;
        }
        .workers-team-actions-menu summary{
          min-height:44px !important;
          padding:0 16px !important;
        }
        .workers-filter-tabs-compact{
          width:min(100%,760px) !important;
          max-width:100% !important;
          margin:0 !important;
          padding:4px !important;
          min-height:44px !important;
          border-radius:999px !important;
          background:rgba(248,250,252,.86) !important;
          display:flex !important;
          gap:4px !important;
        }
        .workers-filter-tabs-compact button{
          min-height:34px !important;
          padding:0 16px !important;
          border-radius:999px !important;
          font-size:12px !important;
          letter-spacing:0 !important;
          text-transform:none !important;
        }
        .workers-modern-list{
          gap:12px !important;
        }
        .workers-person-row{
          min-height:88px !important;
          grid-template-columns:52px minmax(190px,1fr) minmax(210px,300px) auto !important;
          gap:14px !important;
          align-items:center !important;
          border-radius:26px !important;
          padding:14px 16px !important;
          background:rgba(255,255,255,.94) !important;
          box-shadow:0 18px 42px rgba(15,23,42,.045) !important;
        }
        .workers-person-row:hover{
          transform:translateY(-1px);
          box-shadow:0 22px 48px rgba(15,23,42,.065) !important;
        }
        .workers-avatar,
        .workers-avatar img{
          width:48px !important;
          height:48px !important;
          border-radius:16px !important;
        }
        .workers-person-main{
          gap:3px !important;
        }
        .workers-person-title strong{
          font-size:15px !important;
          letter-spacing:-.02em;
        }
        .workers-person-title span{
          min-height:22px !important;
          padding:0 9px !important;
          font-size:9px !important;
        }
        .workers-person-main p{
          margin:0 !important;
          font-size:12px !important;
          color:#64748b !important;
          font-weight:850 !important;
        }
        .workers-person-main small{
          color:#334155 !important;
          font-size:12px !important;
          font-weight:850 !important;
          white-space:nowrap;
          overflow:hidden;
          text-overflow:ellipsis;
          max-width:520px;
        }
        .workers-person-access-line{
          margin-top:4px !important;
          width:max-content !important;
          max-width:100% !important;
          min-height:22px !important;
          border-radius:999px !important;
          padding:0 10px !important;
          display:inline-flex !important;
          align-items:center !important;
          font-size:10px !important;
          font-weight:950 !important;
          letter-spacing:0 !important;
        }
        .workers-person-status{
          border:1px solid rgba(226,232,240,.96);
          background:#f8fafc;
          border-radius:18px;
          min-height:58px;
          padding:10px 12px;
          display:grid;
          grid-template-columns:auto minmax(0,1fr);
          gap:3px 10px;
          align-items:center;
        }
        .workers-person-status i{
          width:24px;
          height:24px;
          border-radius:999px;
          display:grid;
          place-items:center;
          font-style:normal;
          font-size:12px;
          font-weight:950;
          grid-row:1 / span 2;
        }
        .workers-person-status strong{
          color:#0f172a;
          font-size:12px;
          line-height:1.05;
          font-weight:950;
        }
        .workers-person-status span{
          color:#64748b;
          font-size:10px;
          line-height:1.05;
          font-weight:900;
        }
        .workers-person-status.linked{
          border-color:rgba(16,185,129,.24);
          background:#ecfdf5;
        }
        .workers-person-status.linked i{
          background:#00675f;
          color:white;
        }
        .workers-person-status.pending{
          border-color:rgba(245,158,11,.26);
          background:#fffbeb;
        }
        .workers-person-status.pending i{
          background:#f59e0b;
          color:white;
        }
        .workers-person-status.legacy{
          border-color:rgba(245,158,11,.22);
          background:#fff7ed;
        }
        .workers-person-status.legacy i{
          background:#d97706;
          color:white;
        }
        .workers-person-status.missing{
          border-color:rgba(226,232,240,.96);
          background:#f8fafc;
        }
        .workers-person-status.missing i{
          background:#e2e8f0;
          color:#64748b;
        }
        .workers-person-actions-3a3{
          display:flex !important;
          align-items:center !important;
          justify-content:flex-end !important;
          gap:8px !important;
          width:auto !important;
        }
        .workers-person-actions button{
          min-height:38px !important;
          border-radius:999px !important;
          padding:0 14px !important;
          font-size:12px !important;
          box-shadow:none !important;
        }
        .workers-person-actions .connect-link{
          min-width:112px;
          background:#00675f !important;
          border-color:#00675f !important;
          color:#fff !important;
        }
        .workers-person-actions .edit{
          background:#fff !important;
          border:1px solid #e2e8f0 !important;
          color:#0f172a !important;
        }
        .workers-person-actions .delete{
          background:transparent !important;
          border:0 !important;
          color:#ef4444 !important;
          padding-inline:8px !important;
        }
        .workers-primary-action{
          min-height:48px !important;
          border-radius:16px !important;
          padding:0 20px !important;
          box-shadow:0 20px 42px rgba(0,103,95,.16) !important;
        }
        @media(max-width:1100px){
          .workers-person-row{
            grid-template-columns:50px minmax(0,1fr) !important;
          }
          .workers-person-status,
          .workers-person-actions-3a3{
            grid-column:1 / -1;
            width:100% !important;
          }
          .workers-person-actions-3a3{
            justify-content:flex-start !important;
            flex-wrap:wrap;
          }
        }
        @media(max-width:760px){
          .workers-team-panel{
            border-radius:22px !important;
            padding:14px !important;
          }
          .workers-team-toolbar{
            grid-template-columns:1fr;
          }
          .workers-team-summary-chips{
            display:grid;
            grid-template-columns:repeat(2,minmax(0,1fr));
          }
          .workers-team-chip{
            width:100%;
            justify-content:flex-start;
          }
          .workers-team-controls{
            align-items:stretch;
          }
          .workers-team-public-toggle,
          .workers-team-actions-menu,
          .workers-primary-action{
            width:100%;
          }
          .workers-filter-tabs-compact{
            overflow:auto !important;
            justify-content:flex-start !important;
          }
          .workers-filter-tabs-compact button{
            flex:0 0 auto;
          }
          .workers-person-row{
            padding:12px !important;
            border-radius:20px !important;
          }
        }

      

        /* HOTFIX 21 — General tab minimal identity polish */
        .workers-general-minimal-v21 .workers-general-premium-shell-v21{
          grid-template-columns:270px minmax(0,1fr) !important;
          gap:18px !important;
        }
        .workers-profile-photo-only-v21{
          align-self:start !important;
        }
        .workers-general-form-v21{
          display:block !important;
        }
        .workers-general-card-v21{
          padding:16px !important;
          border-radius:24px !important;
          background:linear-gradient(180deg,#ffffff 0%,#fbfefd 100%) !important;
          box-shadow:0 14px 34px rgba(15,23,42,.045) !important;
        }
        .workers-general-topbar-v21{
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:16px;
          padding:0 0 14px;
          border-bottom:1px solid rgba(226,232,240,.78);
          margin-bottom:14px;
        }
        .workers-general-topbar-v21 > div:first-child span{
          display:block;
          color:#00675f;
          font-size:10px;
          font-weight:950;
          letter-spacing:.16em;
          text-transform:uppercase;
          margin-bottom:4px;
        }
        .workers-general-topbar-v21 > div:first-child strong{
          display:block;
          color:#0f172a;
          font-size:20px;
          line-height:1;
          letter-spacing:-.045em;
          font-weight:950;
        }
        .workers-general-controls-v21{
          display:flex;
          align-items:center;
          justify-content:flex-end;
          gap:10px;
          flex:0 0 auto;
        }
        .workers-mini-select-v21,
        .workers-mini-visibility-v21{
          min-height:46px;
          border:1px solid #e2e8f0;
          background:#ffffff;
          border-radius:16px;
          display:flex;
          align-items:center;
          gap:10px;
          padding:7px 10px 7px 12px;
        }
        .workers-mini-select-v21 span,
        .workers-mini-visibility-v21 span{
          color:#64748b;
          font-size:10px;
          font-weight:950;
          letter-spacing:.08em;
          text-transform:uppercase;
          white-space:nowrap;
        }
        .workers-mini-select-v21 select{
          min-width:128px;
          height:32px;
          border:0 !important;
          outline:0 !important;
          background:transparent !important;
          color:#0f172a;
          font-size:13px;
          font-weight:950;
          cursor:pointer;
          padding-right:2px;
        }
        .workers-mini-visibility-v21{
          padding-right:8px;
        }
        .workers-mini-visibility-v21 > div{
          display:flex;
          align-items:center;
          gap:6px;
        }
        .workers-mini-visibility-v21 i{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          width:17px;
          height:17px;
          margin-left:6px;
          border-radius:999px;
          border:1px solid rgba(0,103,95,.18);
          background:#f2fbf8;
          color:#00675f;
          font-size:10px;
          font-weight:950;
          font-style:normal;
          text-transform:none;
          letter-spacing:0;
          cursor:help;
        }
        .workers-general-fields-v21{
          display:grid !important;
          grid-template-columns:repeat(2,minmax(0,1fr)) !important;
          gap:12px !important;
        }
        .workers-general-fields-v21 .workers-premium-field{
          min-width:0;
        }
        .workers-general-fields-v21 .workers-premium-field > span,
        .workers-phone-clean-field-v21 .loop-phone-label{
          color:#64748b !important;
          font-size:10.5px !important;
          font-weight:950 !important;
          letter-spacing:.02em !important;
        }
        .workers-general-fields-v21 input,
        .workers-phone-clean-field-v21 .loop-phone-control,
        .workers-phone-clean-field-v21 .workers-phone-premium-input{
          min-height:48px !important;
          border-radius:16px !important;
        }
        .workers-phone-clean-field-v21 .loop-phone-hint,
        .workers-phone-clean-field-v21 em,
        .workers-general-minimal-v21 .workers-whatsapp-status-card{
          display:none !important;
        }
        @media(max-width: 900px){
          .workers-general-minimal-v21 .workers-general-premium-shell-v21{
            grid-template-columns:1fr !important;
          }
          .workers-general-topbar-v21{
            display:grid;
            gap:12px;
          }
          .workers-general-controls-v21{
            justify-content:stretch;
            width:100%;
          }
          .workers-mini-select-v21,
          .workers-mini-visibility-v21{
            flex:1 1 0;
          }
        }
        @media(max-width: 620px){
          .workers-general-fields-v21{
            grid-template-columns:1fr !important;
          }
          .workers-general-controls-v21{
            display:grid;
            grid-template-columns:1fr;
          }
          .workers-mini-select-v21,
          .workers-mini-visibility-v21{
            width:100%;
            justify-content:space-between;
          }
        }


        /* HOTFIX 26 — Team dashboard premium compact revamp */
        .workers-team-panel-v26{
          border-radius:28px !important;
          padding:28px !important;
          gap:22px !important;
          background:linear-gradient(135deg,#ffffff 0%,#fffdf8 56%,#f4fbf8 100%) !important;
          border:1px solid rgba(215,226,232,.92) !important;
        }
        .workers-team-panel-v26 .workers-kicker{
          margin-bottom:6px !important;
        }
        .workers-team-v26-head{
          display:grid;
          grid-template-columns:minmax(0,1fr) auto;
          gap:18px;
          align-items:start;
        }
        .workers-team-v26-head h3{
          margin:0 !important;
          color:#0f172a;
          font-size:clamp(28px,3vw,42px);
          line-height:.98;
          letter-spacing:-.055em;
          font-weight:950;
        }
        .workers-team-v26-head p{
          margin:8px 0 0 !important;
          color:#64748b;
          font-size:13px;
          line-height:1.35;
          font-weight:800;
        }
        .workers-primary-action-v26{
          min-height:56px !important;
          padding:0 24px !important;
          display:inline-flex !important;
          align-items:center !important;
          gap:10px !important;
          border-radius:18px !important;
          font-size:15px !important;
          font-weight:950 !important;
        }
        .workers-primary-action-v26 span{
          display:inline-grid;
          place-items:center;
          font-size:24px;
          line-height:1;
          font-weight:500;
        }
        .workers-team-summary-v26{
          display:flex;
          flex-wrap:wrap;
          gap:12px;
          align-items:center;
        }
        .workers-stat-v26{
          min-height:58px;
          min-width:144px;
          border:1px solid rgba(226,232,240,.95);
          border-radius:18px;
          background:rgba(255,255,255,.9);
          box-shadow:0 14px 34px rgba(15,23,42,.04);
          display:grid;
          grid-template-columns:30px auto;
          grid-template-rows:auto auto;
          column-gap:10px;
          align-items:center;
          padding:10px 14px;
        }
        .workers-stat-v26 i{
          grid-row:1 / span 2;
          width:30px;
          height:30px;
          border-radius:999px;
          display:grid;
          place-items:center;
          font-style:normal;
          font-size:15px;
          background:#f0fdfa;
          color:#00675f;
        }
        .workers-stat-v26.pending i{background:#fff7ed;color:#d97706;}
        .workers-stat-v26.missing i{background:#f1f5f9;color:#64748b;}
        .workers-stat-v26.service i{background:#ecfdf5;color:#00675f;}
        .workers-stat-v26.operational i{background:#f0fdfa;color:#0f766e;}
        .workers-stat-v26 strong{
          color:#00675f;
          font-size:18px;
          line-height:1;
          font-weight:950;
          letter-spacing:-.04em;
        }
        .workers-stat-v26 span{
          color:#64748b;
          font-size:11px;
          line-height:1;
          font-weight:850;
          text-transform:lowercase;
        }
        .workers-team-commandbar-v26{
          min-height:66px;
          border:1px solid rgba(226,232,240,.95);
          border-radius:22px;
          background:rgba(255,255,255,.82);
          display:grid;
          grid-template-columns:minmax(220px,300px) minmax(0,1fr) auto auto;
          gap:12px;
          align-items:center;
          padding:12px;
          box-shadow:0 16px 38px rgba(15,23,42,.035);
        }
        .workers-team-search-v26{
          min-height:42px;
          border:1px solid rgba(226,232,240,.98);
          border-radius:16px;
          background:#fff;
          display:flex;
          align-items:center;
          gap:10px;
          padding:0 14px;
          color:#64748b;
        }
        .workers-team-search-v26 span{
          font-size:22px;
          line-height:1;
          transform:translateY(-1px);
        }
        .workers-team-search-v26 input{
          border:0 !important;
          outline:0 !important;
          background:transparent !important;
          width:100%;
          min-width:0;
          color:#0f172a;
          font-size:13px;
          font-weight:800;
        }
        .workers-team-search-v26 input::placeholder{color:#94a3b8;font-weight:800;}
        .workers-filter-tabs-v26{
          width:auto !important;
          min-width:0 !important;
          justify-content:flex-start !important;
          overflow:auto !important;
          scrollbar-width:none;
        }
        .workers-filter-tabs-v26::-webkit-scrollbar{display:none;}
        .workers-filter-tabs-v26 button{
          flex:0 0 auto;
        }
        .workers-team-public-v26{
          min-height:42px !important;
          box-shadow:none !important;
          background:#fff !important;
          white-space:nowrap;
        }
        .workers-team-actions-v26{
          width:190px !important;
          flex:0 0 auto;
          background:#fff !important;
          box-shadow:none !important;
        }
        .workers-list-header-v26{
          display:grid;
          grid-template-columns:52px minmax(230px,1fr) minmax(130px,190px) minmax(190px,260px) auto;
          gap:14px;
          padding:4px 20px 8px;
          color:#64748b;
          font-size:12px;
          font-weight:850;
        }
        .workers-list-header-v26 span:nth-child(1){grid-column:2;}
        .workers-modern-list-v26{
          gap:10px !important;
        }
        .workers-modern-list-v26 .workers-person-row{
          min-height:76px !important;
          grid-template-columns:52px minmax(230px,1fr) minmax(130px,190px) minmax(190px,260px) auto !important;
          gap:14px !important;
          border-radius:22px !important;
          padding:12px 16px !important;
          box-shadow:0 10px 28px rgba(15,23,42,.035) !important;
        }
        .workers-modern-list-v26 .workers-person-main small{
          display:none !important;
        }
        .workers-person-services-v26{
          display:flex;
          align-items:center;
          justify-content:flex-start;
          min-width:0;
        }
        .workers-person-services-v26 span{
          min-height:32px;
          padding:0 13px;
          border-radius:999px;
          border:1px solid rgba(0,103,95,.16);
          background:#f1fbf8;
          color:#00675f;
          display:inline-flex;
          align-items:center;
          justify-content:center;
          font-size:12px;
          font-weight:950;
          white-space:nowrap;
        }
        .workers-modern-list-v26 .workers-person-status{
          min-height:48px !important;
          border-radius:16px !important;
          padding:8px 12px !important;
        }
        .workers-modern-list-v26 .workers-person-status i{
          width:24px !important;
          height:24px !important;
        }
        .workers-modern-list-v26 .workers-person-actions-v26{
          gap:10px !important;
          justify-content:flex-end !important;
          position:relative;
        }
        .workers-person-actions-v26 .edit{
          min-width:92px !important;
          background:#fff !important;
          border:1px solid #e2e8f0 !important;
          color:#0f172a !important;
        }
        .workers-row-menu-v26{
          position:relative;
        }
        .workers-row-menu-v26 summary{
          list-style:none;
          width:42px;
          height:38px;
          border-radius:999px;
          border:1px solid #e2e8f0;
          background:#fff;
          color:#0f172a;
          display:grid;
          place-items:center;
          cursor:pointer;
          font-size:13px;
          font-weight:950;
          letter-spacing:1px;
          box-shadow:0 8px 18px rgba(15,23,42,.035);
        }
        .workers-row-menu-v26 summary::-webkit-details-marker{display:none;}
        .workers-row-menu-v26[open] summary{
          border-color:rgba(0,103,95,.22);
          box-shadow:0 12px 24px rgba(15,23,42,.07);
        }
        .workers-row-menu-v26 > div{
          position:absolute;
          right:0;
          top:calc(100% + 8px);
          z-index:50;
          width:190px;
          padding:8px;
          border:1px solid #e2e8f0;
          background:#fff;
          border-radius:16px;
          box-shadow:0 22px 55px rgba(15,23,42,.16);
        }
        .workers-row-menu-v26 button{
          width:100%;
          min-height:38px !important;
          border-radius:12px !important;
          border:0 !important;
          background:#fff !important;
          color:#0f172a !important;
          display:flex;
          align-items:center;
          justify-content:flex-start;
          padding:0 12px !important;
          font-size:12px !important;
          font-weight:900 !important;
          cursor:pointer;
        }
        .workers-row-menu-v26 button:hover{background:#f8fafc !important;}
        .workers-row-menu-v26 button.danger{color:#dc2626 !important;}
        .workers-row-menu-v26 button.danger:hover{background:#fef2f2 !important;}
        @media(max-width:1180px){
          .workers-team-commandbar-v26{
            grid-template-columns:1fr;
          }
          .workers-team-public-v26,
          .workers-team-actions-v26{
            width:100% !important;
            justify-content:space-between;
          }
          .workers-list-header-v26{display:none;}
          .workers-modern-list-v26 .workers-person-row{
            grid-template-columns:50px minmax(0,1fr) !important;
          }
          .workers-person-services-v26,
          .workers-modern-list-v26 .workers-person-status,
          .workers-modern-list-v26 .workers-person-actions-v26{
            grid-column:1 / -1;
          }
          .workers-modern-list-v26 .workers-person-actions-v26{
            justify-content:flex-start !important;
          }
        }
        @media(max-width:760px){
          .workers-team-panel-v26{
            padding:16px !important;
            border-radius:22px !important;
          }
          .workers-team-v26-head{
            grid-template-columns:1fr;
          }
          .workers-primary-action-v26{
            width:100%;
            justify-content:center;
          }
          .workers-team-summary-v26{
            display:grid;
            grid-template-columns:repeat(2,minmax(0,1fr));
          }
          .workers-stat-v26{
            min-width:0;
          }
          .workers-team-commandbar-v26{
            border-radius:18px;
            padding:10px;
          }
        }


        /* HOTFIX 27 — Team dashboard ultra compact polish */
        .workers-team-panel-v26{
          padding:16px 20px 18px !important;
          gap:12px !important;
          border-radius:24px !important;
          background:linear-gradient(135deg,#ffffff 0%,#fffdf8 64%,#f6fbf8 100%) !important;
        }
        .workers-team-v26-head{
          align-items:center !important;
          gap:14px !important;
        }
        .workers-team-v26-head h3{
          font-size:clamp(24px,2.2vw,34px) !important;
          letter-spacing:-.045em !important;
        }
        .workers-team-v26-head .workers-kicker{
          margin-bottom:4px !important;
          font-size:10px !important;
        }
        .workers-team-v26-head p.workers-team-meta-v27{
          margin:7px 0 0 !important;
          display:flex !important;
          align-items:center !important;
          flex-wrap:wrap !important;
          gap:6px !important;
          color:#64748b !important;
          font-size:11px !important;
          line-height:1 !important;
          font-weight:850 !important;
        }
        .workers-team-meta-v27 span{
          min-height:26px;
          display:inline-flex;
          align-items:center;
          gap:5px;
          padding:0 10px;
          border-radius:999px;
          border:1px solid rgba(226,232,240,.88);
          background:rgba(255,255,255,.78);
          white-space:nowrap;
        }
        .workers-team-meta-v27 b{
          color:#00675f;
          font-size:13px;
          font-weight:950;
          letter-spacing:-.03em;
        }
        .workers-team-summary-v26{
          display:none !important;
        }
        .workers-primary-action-v26{
          min-height:44px !important;
          padding:0 18px !important;
          border-radius:15px !important;
          font-size:13px !important;
          box-shadow:0 18px 36px rgba(0,103,95,.14) !important;
        }
        .workers-primary-action-v26 span{
          font-size:18px !important;
          transform:none !important;
        }
        .workers-team-commandbar-v26{
          min-height:50px !important;
          grid-template-columns:minmax(190px,280px) minmax(0,1fr) auto auto !important;
          gap:8px !important;
          padding:8px !important;
          border-radius:18px !important;
          box-shadow:0 10px 24px rgba(15,23,42,.025) !important;
        }
        .workers-team-search-v26{
          min-height:36px !important;
          border-radius:13px !important;
          padding:0 10px !important;
          gap:7px !important;
        }
        .workers-team-search-v26 span{
          font-size:16px !important;
        }
        .workers-team-search-v26 input{
          font-size:12px !important;
          font-weight:850 !important;
        }
        .workers-filter-tabs-v26{
          gap:5px !important;
        }
        .workers-filter-tabs-v26 button{
          min-height:34px !important;
          padding:0 12px !important;
          border-radius:999px !important;
          font-size:11px !important;
          font-weight:900 !important;
        }
        .workers-team-public-v26{
          min-height:36px !important;
          border-radius:14px !important;
          padding:0 10px !important;
          gap:8px !important;
        }
        .workers-team-public-v26 span{
          font-size:11px !important;
          font-weight:900 !important;
        }
        .workers-team-public-v26 .relative{
          transform:scale(.86);
          transform-origin:right center;
        }
        .workers-team-actions-v26{
          width:132px !important;
          min-height:36px !important;
          border-radius:14px !important;
        }
        .workers-team-actions-v26 > summary{
          min-height:36px !important;
          padding:0 12px !important;
          font-size:11px !important;
          font-weight:900 !important;
        }
        .workers-list-header-v26{
          grid-template-columns:42px minmax(230px,1fr) 118px 180px 118px !important;
          gap:9px !important;
          padding:2px 16px 5px !important;
          margin-top:6px !important;
          color:#94a3b8 !important;
          font-size:9.5px !important;
          font-weight:950 !important;
          text-transform:uppercase;
          letter-spacing:.085em;
        }
        .workers-list-header-v26 span:nth-child(1){grid-column:2;}
        .workers-modern-list-v26{
          gap:7px !important;
        }
        .workers-modern-list-v26 .workers-person-row{
          min-height:60px !important;
          grid-template-columns:42px minmax(230px,1fr) 118px 180px 118px !important;
          gap:9px !important;
          align-items:center !important;
          border-radius:18px !important;
          padding:8px 14px !important;
          box-shadow:0 7px 18px rgba(15,23,42,.025) !important;
        }
        .workers-modern-list-v26 .workers-avatar{
          width:42px !important;
          height:42px !important;
          border-radius:14px !important;
        }
        .workers-modern-list-v26 .workers-avatar img{
          width:100% !important;
          height:100% !important;
          object-fit:cover !important;
        }
        .workers-modern-list-v26 .workers-person-main{
          min-width:0 !important;
        }
        .workers-modern-list-v26 .workers-person-title{
          gap:7px !important;
        }
        .workers-modern-list-v26 .workers-person-title strong{
          font-size:13.5px !important;
          line-height:1.05 !important;
          letter-spacing:-.025em !important;
        }
        .workers-modern-list-v26 .workers-person-title span{
          min-height:20px !important;
          padding:0 7px !important;
          font-size:9.5px !important;
          font-weight:950 !important;
        }
        .workers-modern-list-v26 .workers-person-main p{
          margin-top:3px !important;
          font-size:11px !important;
          line-height:1.1 !important;
          font-weight:850 !important;
          color:#64748b !important;
        }
        .workers-modern-list-v26 .workers-person-main small{
          display:none !important;
        }
        .workers-person-services-v26 span{
          min-height:28px !important;
          padding:0 10px !important;
          font-size:11px !important;
          font-weight:950 !important;
        }
        .workers-modern-list-v26 .workers-person-status{
          min-height:38px !important;
          max-width:180px !important;
          border-radius:14px !important;
          padding:6px 9px !important;
          gap:7px !important;
        }
        .workers-modern-list-v26 .workers-person-status i{
          width:22px !important;
          height:22px !important;
          font-size:10px !important;
          flex:0 0 22px !important;
        }
        .workers-modern-list-v26 .workers-person-status strong{
          font-size:11.5px !important;
          line-height:1.05 !important;
        }
        .workers-modern-list-v26 .workers-person-status span{
          font-size:9.5px !important;
          line-height:1.05 !important;
        }
        .workers-modern-list-v26 .workers-person-actions-v26{
          gap:7px !important;
        }
        .workers-person-actions-v26 .edit{
          min-width:72px !important;
          min-height:34px !important;
          padding:0 12px !important;
          border-radius:999px !important;
          font-size:11px !important;
          font-weight:950 !important;
        }
        .workers-row-menu-v26 summary{
          width:36px !important;
          height:34px !important;
          font-size:11px !important;
          letter-spacing:.5px !important;
        }

        /* HOTFIX 30 — Equipo right-control rail + public visibility column */
        .workers-team-panel-v26{
          padding:18px 20px !important;
          border-radius:24px !important;
          gap:0 !important;
        }
        .workers-team-shell-v30{
          display:grid;
          grid-template-columns:minmax(0,1fr) minmax(300px,420px);
          gap:18px;
          align-items:stretch;
        }
        .workers-team-main-v30{
          min-width:0;
          display:flex;
          flex-direction:column;
          justify-content:center;
        }
        .workers-team-main-v30 h3{
          margin:0 !important;
          color:#0f172a;
          font-size:clamp(28px,2.7vw,38px);
          line-height:.96;
          letter-spacing:-.055em;
          font-weight:950;
        }
        .workers-team-main-v30 .workers-kicker{
          margin-bottom:5px !important;
          font-size:10px !important;
        }
        .workers-team-meta-v30{
          margin-top:10px !important;
          max-width:760px;
        }
        .workers-team-side-panel-v30{
          min-width:0;
          align-self:stretch;
          display:grid;
          grid-template-columns:1fr;
          gap:8px;
          padding:10px;
          border-radius:20px;
          border:1px solid rgba(226,232,240,.9);
          background:rgba(255,255,255,.7);
          box-shadow:0 16px 34px rgba(15,23,42,.035);
        }
        .workers-team-side-top-v30{
          display:flex;
          justify-content:flex-end;
        }
        .workers-primary-action-v30{
          width:100%;
          justify-content:center !important;
          min-height:42px !important;
          border-radius:14px !important;
        }
        .workers-team-search-v30{
          width:100%;
          min-height:38px !important;
        }
        .workers-filter-tabs-v30{
          display:grid !important;
          grid-template-columns:repeat(3,minmax(0,1fr));
          gap:6px !important;
          padding:0 !important;
          border:none !important;
          background:transparent !important;
          box-shadow:none !important;
        }
        .workers-filter-tabs-v30 button{
          min-width:0 !important;
          min-height:30px !important;
          padding:0 8px !important;
          font-size:10px !important;
          border-radius:999px !important;
        }
        .workers-team-side-bottom-v30{
          display:grid;
          grid-template-columns:minmax(0,1fr) 126px;
          gap:8px;
          align-items:center;
        }
        .workers-team-public-v30,
        .workers-team-actions-v30{
          width:100% !important;
          min-height:36px !important;
        }
        .workers-team-actions-v30 > summary{
          justify-content:space-between !important;
        }
        .workers-list-header-v30{
          grid-template-columns:42px minmax(170px,1.15fr) 104px 112px 168px 98px !important;
          gap:8px !important;
          padding:8px 14px 7px !important;
          margin-top:8px !important;
        }
        .workers-list-header-v30 span:nth-child(1){grid-column:2 !important;}
        .workers-modern-list-v26 .workers-person-row{
          grid-template-columns:42px minmax(170px,1.15fr) 104px 112px 168px 98px !important;
          gap:8px !important;
          min-height:58px !important;
          padding:7px 12px !important;
          border-radius:17px !important;
        }
        .workers-modern-list-v26 .workers-person-services-v26{
          justify-content:flex-start !important;
        }
        .workers-person-public-v30{
          min-height:30px;
          padding:0 10px;
          border-radius:999px;
          border:1px solid rgba(226,232,240,.95);
          display:inline-flex;
          align-items:center;
          justify-content:center;
          gap:6px;
          justify-self:start;
          font-size:10.5px;
          font-weight:950;
          white-space:nowrap;
        }
        .workers-person-public-v30 i{
          width:16px;
          height:16px;
          border-radius:999px;
          display:grid;
          place-items:center;
          font-style:normal;
          font-size:10px;
          font-weight:950;
        }
        .workers-person-public-v30.is-public{
          color:#00675f;
          background:#effdf8;
          border-color:rgba(20,184,166,.28);
        }
        .workers-person-public-v30.is-public i{
          background:#ccfbef;
        }
        .workers-person-public-v30.is-private{
          color:#64748b;
          background:#f8fafc;
          border-color:#e2e8f0;
        }
        .workers-person-public-v30.is-private i{
          background:#e2e8f0;
        }
        .workers-modern-list-v26 .workers-person-status{
          max-width:168px !important;
        }
        .workers-modern-list-v26 .workers-person-actions-v26{
          justify-content:flex-end !important;
        }
        @media(max-width:1180px){
          .workers-team-shell-v30{
            grid-template-columns:1fr !important;
          }
          .workers-team-side-panel-v30{
            grid-template-columns:1fr !important;
          }
          .workers-filter-tabs-v30{
            grid-template-columns:repeat(2,minmax(0,1fr)) !important;
          }
          .workers-team-side-bottom-v30{
            grid-template-columns:1fr !important;
          }
          .workers-team-commandbar-v26{
            grid-template-columns:1fr !important;
          }
          .workers-list-header-v26{display:none !important;}
          .workers-modern-list-v26 .workers-person-row{
            grid-template-columns:42px minmax(0,1fr) !important;
            min-height:72px !important;
          }
          .workers-person-services-v26,
          .workers-person-public-v30,
          .workers-modern-list-v26 .workers-person-status,
          .workers-modern-list-v26 .workers-person-actions-v26{
            grid-column:2 / -1 !important;
          }
          .workers-modern-list-v26 .workers-person-status{
            max-width:100% !important;
          }
        }
        @media(max-width:760px){
          .workers-team-panel-v26{
            padding:14px !important;
          }
          .workers-team-v26-head{
            grid-template-columns:1fr !important;
            align-items:stretch !important;
          }
          .workers-team-meta-v27 span{
            min-height:24px;
            font-size:10.5px;
          }
          .workers-primary-action-v26{
            width:100% !important;
            justify-content:center !important;
          }
        }



        /* HOTFIX 31 — Keep controls beside the workers list, not inside the header */
        .workers-team-panel-v26{
          padding:18px 22px !important;
          min-height:140px !important;
        }
        .workers-team-shell-v30{
          grid-template-columns:1fr !important;
          gap:0 !important;
        }
        .workers-team-main-v30{
          justify-content:flex-start !important;
        }
        .workers-team-main-v30 h3{
          font-size:clamp(28px,2.4vw,36px) !important;
        }
        .workers-team-meta-v30{
          margin-top:8px !important;
        }
        .workers-team-content-v31{
          display:grid;
          grid-template-columns:minmax(0,1fr) minmax(300px,420px);
          gap:16px;
          align-items:start;
          margin-top:10px;
        }
        .workers-team-list-area-v31{
          min-width:0;
        }
        .workers-team-content-v31 .workers-team-side-panel-v30{
          position:sticky;
          top:88px;
          align-self:start;
          width:100%;
          background:rgba(255,255,255,.92);
          box-shadow:0 18px 44px rgba(15,23,42,.075);
        }
        .workers-team-content-v31 .workers-primary-action-v30{
          min-height:44px !important;
        }
        .workers-team-content-v31 .workers-team-search-v30{
          min-height:42px !important;
        }
        .workers-team-content-v31 .workers-filter-tabs-v30{
          grid-template-columns:repeat(3,minmax(0,1fr)) !important;
        }
        .workers-team-content-v31 .workers-team-side-bottom-v30{
          grid-template-columns:minmax(0,1fr) 126px !important;
        }
        .workers-team-list-area-v31 .workers-list-header-v30{
          margin-top:0 !important;
        }
        @media(max-width:1180px){
          .workers-team-content-v31{
            grid-template-columns:1fr !important;
          }
          .workers-team-content-v31 .workers-team-side-panel-v30{
            position:static;
            order:-1;
          }
        }


        /* HOTFIX 32 — Premium left control rail + safer responsive worker rows */
        .workers-team-panel-v26{
          min-height:124px !important;
          padding:18px 22px !important;
          border-radius:26px !important;
          background:linear-gradient(135deg,#ffffff 0%,#fffdf8 58%,#f5fbf8 100%) !important;
        }
        .workers-team-content-v31{
          display:grid !important;
          grid-template-columns:minmax(260px,330px) minmax(0,1fr) !important;
          gap:18px !important;
          align-items:start !important;
          margin-top:14px !important;
        }
        .workers-team-list-area-v31{
          grid-column:2 !important;
          grid-row:1 !important;
          min-width:0 !important;
          overflow:visible !important;
        }
        .workers-team-content-v31 .workers-team-side-panel-v30{
          grid-column:1 !important;
          grid-row:1 !important;
          position:sticky !important;
          top:94px !important;
          width:100% !important;
          align-self:start !important;
          padding:12px !important;
          border-radius:24px !important;
          background:linear-gradient(180deg,rgba(255,255,255,.98),rgba(249,253,251,.95)) !important;
          border:1px solid rgba(216,226,232,.95) !important;
          box-shadow:0 24px 58px rgba(15,23,42,.10) !important;
          z-index:4 !important;
        }
        .workers-team-content-v31 .workers-primary-action-v30{
          min-height:48px !important;
          border-radius:16px !important;
          font-size:13px !important;
          letter-spacing:-.01em !important;
        }
        .workers-team-content-v31 .workers-team-search-v30{
          min-height:44px !important;
          border-radius:16px !important;
          background:#fff !important;
          box-shadow:inset 0 0 0 1px rgba(226,232,240,.72) !important;
        }
        .workers-team-content-v31 .workers-filter-tabs-v30{
          display:grid !important;
          grid-template-columns:repeat(2,minmax(0,1fr)) !important;
          gap:7px !important;
          background:transparent !important;
        }
        .workers-team-content-v31 .workers-filter-tabs-v30 button{
          min-height:32px !important;
          padding:0 8px !important;
          font-size:10.5px !important;
        }
        .workers-team-content-v31 .workers-team-side-bottom-v30{
          display:grid !important;
          grid-template-columns:1fr !important;
          gap:8px !important;
        }
        .workers-team-content-v31 .workers-team-public-v30,
        .workers-team-content-v31 .workers-team-actions-v30{
          min-height:40px !important;
          border-radius:15px !important;
          background:#fff !important;
        }

        .workers-team-list-area-v31 .workers-list-header-v30{
          grid-template-columns:42px minmax(158px,1fr) 92px 106px 150px 92px !important;
          gap:7px !important;
          padding:0 12px 8px !important;
          margin-top:0 !important;
          font-size:9.5px !important;
          color:#94a3b8 !important;
        }
        .workers-modern-list-v26{
          gap:8px !important;
        }
        .workers-modern-list-v26 .workers-person-row{
          grid-template-columns:42px minmax(158px,1fr) 92px 106px 150px 92px !important;
          gap:7px !important;
          min-height:70px !important;
          padding:10px 12px !important;
          border-radius:20px !important;
          background:rgba(255,255,255,.97) !important;
          border:1px solid rgba(226,232,240,.88) !important;
          box-shadow:0 14px 34px rgba(15,23,42,.045) !important;
        }
        .workers-modern-list-v26 .workers-person-row:hover{
          transform:translateY(-1px) !important;
          border-color:rgba(0,103,95,.14) !important;
          box-shadow:0 18px 42px rgba(15,23,42,.07) !important;
        }
        .workers-modern-list-v26 .workers-avatar,
        .workers-modern-list-v26 .workers-avatar img{
          width:42px !important;
          height:42px !important;
          border-radius:14px !important;
        }
        .workers-modern-list-v26 .workers-person-main{
          min-width:0 !important;
        }
        .workers-modern-list-v26 .workers-person-title{
          gap:7px !important;
          align-items:center !important;
        }
        .workers-modern-list-v26 .workers-person-title strong{
          font-size:14px !important;
          line-height:1.05 !important;
          max-width:170px !important;
          overflow:hidden !important;
          text-overflow:ellipsis !important;
          white-space:nowrap !important;
        }
        .workers-modern-list-v26 .workers-person-title span{
          min-height:21px !important;
          padding:0 8px !important;
          font-size:9px !important;
        }
        .workers-modern-list-v26 .workers-person-main p{
          margin-top:2px !important;
          font-size:11.5px !important;
          color:#64748b !important;
        }
        .workers-modern-list-v26 .workers-person-services-v26 span,
        .workers-modern-list-v26 .workers-person-public-v30{
          min-height:30px !important;
          padding:0 10px !important;
          font-size:10.5px !important;
        }
        .workers-modern-list-v26 .workers-person-status{
          max-width:150px !important;
          min-height:44px !important;
          border-radius:15px !important;
          padding:7px 10px !important;
          gap:2px 7px !important;
        }
        .workers-modern-list-v26 .workers-person-status i{
          width:22px !important;
          height:22px !important;
          font-size:10px !important;
        }
        .workers-modern-list-v26 .workers-person-status strong{
          font-size:11.5px !important;
        }
        .workers-modern-list-v26 .workers-person-status span{
          font-size:9.5px !important;
        }
        .workers-modern-list-v26 .workers-person-actions-v26{
          gap:7px !important;
          justify-content:flex-end !important;
        }
        .workers-person-actions-v26 .edit{
          min-width:68px !important;
          min-height:34px !important;
          border-radius:999px !important;
        }
        .workers-row-menu-v26 summary{
          width:34px !important;
          height:34px !important;
        }
        @media(max-width:1120px){
          .workers-team-content-v31{
            grid-template-columns:1fr !important;
          }
          .workers-team-list-area-v31{
            grid-column:1 !important;
            grid-row:2 !important;
          }
          .workers-team-content-v31 .workers-team-side-panel-v30{
            grid-column:1 !important;
            grid-row:1 !important;
            position:relative !important;
            top:auto !important;
            z-index:1 !important;
          }
          .workers-team-content-v31 .workers-filter-tabs-v30{
            grid-template-columns:repeat(3,minmax(0,1fr)) !important;
          }
        }
        @media(max-width:860px){
          .workers-team-list-area-v31 .workers-list-header-v30{
            display:none !important;
          }
          .workers-modern-list-v26 .workers-person-row{
            grid-template-columns:42px minmax(0,1fr) !important;
            min-height:84px !important;
            padding:12px !important;
          }
          .workers-person-services-v26,
          .workers-person-public-v30,
          .workers-modern-list-v26 .workers-person-status,
          .workers-modern-list-v26 .workers-person-actions-v26{
            grid-column:2 / -1 !important;
          }
          .workers-modern-list-v26 .workers-person-status{
            max-width:100% !important;
          }
          .workers-modern-list-v26 .workers-person-actions-v26{
            justify-content:flex-start !important;
          }
        }


        /* HOTFIX 33 — Filter rail density + safe status/actions columns */
        .workers-team-content-v31{
          grid-template-columns:minmax(246px,300px) minmax(0,1fr) !important;
          gap:16px !important;
        }
        .workers-team-content-v31 .workers-team-side-panel-v30{
          padding:12px !important;
          overflow:visible !important;
        }
        .workers-team-content-v31 .workers-primary-action-v30{
          min-height:46px !important;
        }
        .workers-team-content-v31 .workers-team-search-v30{
          min-height:42px !important;
        }

        /* Filtros: chips reales, no grid. Evita letras cortadas y huecos raros. */
        .workers-team-content-v31 .workers-filter-tabs-v30{
          display:flex !important;
          flex-wrap:wrap !important;
          align-items:center !important;
          justify-content:flex-start !important;
          gap:6px !important;
          width:100% !important;
          overflow:visible !important;
          padding:0 !important;
          background:transparent !important;
        }
        .workers-team-content-v31 .workers-filter-tabs-v30 button{
          flex:0 0 auto !important;
          width:auto !important;
          min-width:0 !important;
          max-width:none !important;
          min-height:29px !important;
          padding:0 10px !important;
          font-size:10px !important;
          line-height:1 !important;
          white-space:nowrap !important;
          overflow:visible !important;
          text-overflow:clip !important;
          letter-spacing:-.01em !important;
        }
        .workers-team-content-v31 .workers-team-side-bottom-v30{
          gap:8px !important;
        }
        .workers-team-content-v31 .workers-team-public-v30,
        .workers-team-content-v31 .workers-team-actions-v30{
          min-height:38px !important;
        }

        /* Tabla/lista: columnas seguras para que Estado y Acciones no se pisen. */
        .workers-team-list-area-v31{
          min-width:0 !important;
          overflow:visible !important;
        }
        .workers-team-list-area-v31 .workers-list-header-v30{
          grid-template-columns:44px minmax(150px,1fr) minmax(88px,108px) minmax(102px,120px) minmax(168px,188px) 122px !important;
          gap:10px !important;
          padding:0 14px 8px !important;
        }
        .workers-modern-list-v26 .workers-person-row{
          grid-template-columns:44px minmax(150px,1fr) minmax(88px,108px) minmax(102px,120px) minmax(168px,188px) 122px !important;
          gap:10px !important;
          min-height:76px !important;
          padding:12px 14px !important;
          align-items:center !important;
          overflow:visible !important;
        }
        .workers-modern-list-v26 .workers-person-main,
        .workers-modern-list-v26 .workers-person-services-v26,
        .workers-modern-list-v26 .workers-person-public-v30,
        .workers-modern-list-v26 .workers-person-status,
        .workers-modern-list-v26 .workers-person-actions-v26{
          min-width:0 !important;
          box-sizing:border-box !important;
        }
        .workers-modern-list-v26 .workers-person-status{
          width:100% !important;
          max-width:none !important;
          justify-self:stretch !important;
          overflow:hidden !important;
          padding:8px 10px !important;
        }
        .workers-modern-list-v26 .workers-person-status strong,
        .workers-modern-list-v26 .workers-person-status span{
          min-width:0 !important;
          overflow:hidden !important;
          text-overflow:ellipsis !important;
          white-space:nowrap !important;
        }
        .workers-modern-list-v26 .workers-person-actions-v26{
          width:100% !important;
          min-width:122px !important;
          justify-content:flex-end !important;
          gap:8px !important;
          position:relative !important;
          z-index:2 !important;
        }
        .workers-person-actions-v26 .edit{
          min-width:70px !important;
          padding:0 13px !important;
        }
        .workers-row-menu-v26 summary{
          flex:0 0 auto !important;
        }

        @media(max-width:1340px){
          .workers-team-content-v31{
            grid-template-columns:minmax(236px,280px) minmax(0,1fr) !important;
            gap:14px !important;
          }
          .workers-team-list-area-v31 .workers-list-header-v30,
          .workers-modern-list-v26 .workers-person-row{
            grid-template-columns:44px minmax(130px,1fr) 92px 104px minmax(154px,174px) 118px !important;
            gap:8px !important;
          }
          .workers-team-content-v31 .workers-filter-tabs-v30 button{
            padding:0 8px !important;
            font-size:9.5px !important;
          }
        }

        @media(max-width:1180px){
          .workers-team-content-v31{
            grid-template-columns:1fr !important;
          }
          .workers-team-content-v31 .workers-team-side-panel-v30{
            position:relative !important;
            top:auto !important;
            z-index:1 !important;
          }
          .workers-team-content-v31 .workers-filter-tabs-v30{
            display:flex !important;
            flex-wrap:wrap !important;
          }
          .workers-team-content-v31 .workers-team-side-bottom-v30{
            grid-template-columns:minmax(0,1fr) minmax(126px,160px) !important;
          }
        }


        /* HOTFIX 34 — Responsive row fit: keep status/actions aligned at tablet widths */
        @media (min-width:861px) and (max-width:1180px){
          .workers-team-list-area-v31 .workers-list-header-v30{
            display:grid !important;
            grid-template-columns:44px minmax(150px,1fr) minmax(88px,104px) minmax(98px,114px) minmax(150px,170px) 116px !important;
            gap:8px !important;
            padding:0 12px 8px !important;
          }
          .workers-team-list-area-v31 .workers-list-header-v30 span:nth-child(1){grid-column:2 !important;}
          .workers-modern-list-v26 .workers-person-row{
            grid-template-columns:44px minmax(150px,1fr) minmax(88px,104px) minmax(98px,114px) minmax(150px,170px) 116px !important;
            gap:8px !important;
            min-height:72px !important;
            padding:11px 12px !important;
            align-items:center !important;
          }
          .workers-modern-list-v26 .workers-person-services-v26,
          .workers-modern-list-v26 .workers-person-public-v30,
          .workers-modern-list-v26 .workers-person-status,
          .workers-modern-list-v26 .workers-person-actions-v26{
            grid-column:auto !important;
            grid-row:auto !important;
          }
          .workers-modern-list-v26 .workers-person-status{
            width:100% !important;
            max-width:none !important;
            min-width:0 !important;
            min-height:42px !important;
            padding:7px 9px !important;
          }
          .workers-modern-list-v26 .workers-person-status i{
            width:22px !important;
            height:22px !important;
            flex:0 0 22px !important;
          }
          .workers-modern-list-v26 .workers-person-status strong{
            font-size:11px !important;
          }
          .workers-modern-list-v26 .workers-person-status span{
            font-size:9px !important;
          }
          .workers-modern-list-v26 .workers-person-actions-v26{
            width:100% !important;
            min-width:116px !important;
            justify-content:flex-end !important;
            align-self:center !important;
          }
          .workers-person-actions-v26 .edit{
            min-width:66px !important;
            min-height:32px !important;
            padding:0 12px !important;
          }
          .workers-row-menu-v26 summary{
            width:32px !important;
            height:32px !important;
          }
        }

        @media (max-width:860px){
          .workers-modern-list-v26 .workers-person-row{
            display:grid !important;
            grid-template-columns:44px minmax(0,1fr) auto !important;
            gap:8px 10px !important;
            min-height:auto !important;
            padding:12px !important;
            align-items:center !important;
          }
          .workers-modern-list-v26 .workers-avatar{
            grid-column:1 !important;
            grid-row:1 / span 2 !important;
            align-self:start !important;
          }
          .workers-modern-list-v26 .workers-person-main{
            grid-column:2 !important;
            grid-row:1 !important;
          }
          .workers-modern-list-v26 .workers-person-services-v26{
            grid-column:2 !important;
            grid-row:2 !important;
            justify-content:flex-start !important;
            align-self:start !important;
          }
          .workers-modern-list-v26 .workers-person-public-v30{
            grid-column:3 !important;
            grid-row:2 !important;
            justify-self:end !important;
            align-self:start !important;
          }
          .workers-modern-list-v26 .workers-person-status{
            grid-column:1 / -1 !important;
            grid-row:3 !important;
            width:100% !important;
            max-width:100% !important;
            min-height:42px !important;
            margin-top:4px !important;
          }
          .workers-modern-list-v26 .workers-person-actions-v26{
            grid-column:3 !important;
            grid-row:1 !important;
            width:auto !important;
            min-width:0 !important;
            justify-content:flex-end !important;
            align-self:start !important;
            gap:6px !important;
          }
          .workers-person-actions-v26 .edit{
            min-width:64px !important;
            min-height:32px !important;
          }
          .workers-row-menu-v26 summary{
            width:32px !important;
            height:32px !important;
          }
        }

        @media (max-width:560px){
          .workers-modern-list-v26 .workers-person-row{
            grid-template-columns:42px minmax(0,1fr) !important;
          }
          .workers-modern-list-v26 .workers-person-actions-v26{
            grid-column:1 / -1 !important;
            grid-row:4 !important;
            justify-content:flex-end !important;
            margin-top:2px !important;
          }
          .workers-modern-list-v26 .workers-person-public-v30{
            grid-column:2 !important;
            grid-row:3 !important;
            justify-self:start !important;
            margin-top:4px !important;
          }
          .workers-modern-list-v26 .workers-person-status{
            grid-column:1 / -1 !important;
            grid-row:5 !important;
          }
        }


        /* HOTFIX 35 — Compact card breakpoint: actions top-right, status below and fit-content */
        @media (max-width: 860px){
          .workers-modern-list-v26 .workers-person-row{
            display:grid !important;
            grid-template-columns:44px minmax(0,1fr) auto !important;
            grid-template-rows:auto auto auto auto !important;
            gap:6px 10px !important;
            min-height:0 !important;
            padding:12px !important;
            align-items:start !important;
          }
          .workers-modern-list-v26 .workers-avatar{
            grid-column:1 !important;
            grid-row:1 / span 2 !important;
            width:42px !important;
            height:42px !important;
            border-radius:14px !important;
            align-self:start !important;
          }
          .workers-modern-list-v26 .workers-person-main{
            grid-column:2 !important;
            grid-row:1 !important;
            min-width:0 !important;
            padding-right:4px !important;
          }
          .workers-modern-list-v26 .workers-person-title{
            gap:6px !important;
            flex-wrap:wrap !important;
          }
          .workers-modern-list-v26 .workers-person-title strong{
            font-size:13px !important;
            line-height:1.05 !important;
          }
          .workers-modern-list-v26 .workers-person-main p{
            font-size:11px !important;
            line-height:1.1 !important;
            margin-top:2px !important;
          }
          .workers-modern-list-v26 .workers-person-main small{
            display:none !important;
          }
          .workers-modern-list-v26 .workers-person-actions-v26{
            grid-column:3 !important;
            grid-row:1 !important;
            justify-self:end !important;
            align-self:start !important;
            width:auto !important;
            min-width:0 !important;
            display:flex !important;
            justify-content:flex-end !important;
            align-items:center !important;
            gap:6px !important;
            margin:0 !important;
          }
          .workers-person-actions-v26 .edit{
            min-width:60px !important;
            min-height:31px !important;
            height:31px !important;
            padding:0 11px !important;
            font-size:11px !important;
            border-radius:999px !important;
          }
          .workers-row-menu-v26 summary{
            width:31px !important;
            height:31px !important;
            min-width:31px !important;
            border-radius:999px !important;
          }
          .workers-modern-list-v26 .workers-person-services-v26{
            grid-column:2 !important;
            grid-row:2 !important;
            justify-self:start !important;
            align-self:start !important;
            margin-top:3px !important;
          }
          .workers-modern-list-v26 .workers-person-public-v30{
            grid-column:2 !important;
            grid-row:3 !important;
            justify-self:start !important;
            align-self:start !important;
            margin-top:2px !important;
          }
          .workers-modern-list-v26 .workers-person-services-v26 span,
          .workers-modern-list-v26 .workers-person-public-v30{
            min-height:28px !important;
            height:28px !important;
            padding:0 10px !important;
            font-size:10.5px !important;
          }
          .workers-modern-list-v26 .workers-person-status{
            grid-column:2 / -1 !important;
            grid-row:4 !important;
            justify-self:start !important;
            align-self:start !important;
            width:auto !important;
            max-width:min(100%, 280px) !important;
            min-width:0 !important;
            min-height:34px !important;
            margin-top:4px !important;
            padding:6px 10px !important;
            border-radius:16px !important;
            display:grid !important;
            grid-template-columns:22px auto !important;
            grid-template-rows:auto auto !important;
            column-gap:8px !important;
            row-gap:0 !important;
            align-items:center !important;
          }
          .workers-modern-list-v26 .workers-person-status i{
            grid-column:1 !important;
            grid-row:1 / span 2 !important;
            width:22px !important;
            height:22px !important;
            flex:0 0 22px !important;
            font-size:10px !important;
          }
          .workers-modern-list-v26 .workers-person-status strong{
            grid-column:2 !important;
            grid-row:1 !important;
            font-size:11px !important;
            line-height:1.05 !important;
            white-space:nowrap !important;
          }
          .workers-modern-list-v26 .workers-person-status span{
            grid-column:2 !important;
            grid-row:2 !important;
            font-size:9px !important;
            line-height:1.05 !important;
            white-space:nowrap !important;
          }
        }

        @media (max-width: 560px){
          .workers-modern-list-v26 .workers-person-row{
            grid-template-columns:42px minmax(0,1fr) auto !important;
            padding:11px !important;
            gap:6px 9px !important;
          }
          .workers-modern-list-v26 .workers-avatar{
            width:40px !important;
            height:40px !important;
          }
          .workers-modern-list-v26 .workers-person-actions-v26{
            grid-column:3 !important;
            grid-row:1 !important;
            justify-self:end !important;
            margin-top:0 !important;
          }
          .workers-modern-list-v26 .workers-person-public-v30{
            grid-column:2 !important;
            grid-row:3 !important;
          }
          .workers-modern-list-v26 .workers-person-status{
            grid-column:2 / -1 !important;
            grid-row:4 !important;
          }
        }

        @media (max-width: 420px){
          .workers-person-actions-v26 .edit{
            min-width:54px !important;
            padding:0 9px !important;
          }
          .workers-row-menu-v26 summary{
            width:29px !important;
            height:29px !important;
            min-width:29px !important;
          }
        }


        /* HOTFIX 36 — Container-width worker list + no native hover tooltip issue
           The video shows a narrow page/zoom where the viewport can still be "desktop"
           but the workers list area itself is too narrow. Use container queries so the
           row becomes a compact card based on available component width, not just window width. */
        .workers-team-list-area-v31{
          container-type:inline-size !important;
        }

        @container (max-width: 760px){
          .workers-team-list-area-v31 .workers-list-header-v30{
            display:none !important;
          }

          .workers-modern-list-v26 .workers-person-row{
            display:grid !important;
            grid-template-columns:42px minmax(0,1fr) auto !important;
            grid-template-rows:auto auto auto auto !important;
            gap:6px 9px !important;
            min-height:0 !important;
            padding:11px !important;
            align-items:start !important;
          }

          .workers-modern-list-v26 .workers-avatar{
            grid-column:1 !important;
            grid-row:1 / span 2 !important;
            width:40px !important;
            height:40px !important;
            border-radius:14px !important;
            align-self:start !important;
          }

          .workers-modern-list-v26 .workers-person-main{
            grid-column:2 !important;
            grid-row:1 !important;
            min-width:0 !important;
            padding-right:4px !important;
          }

          .workers-modern-list-v26 .workers-person-title{
            gap:6px !important;
            flex-wrap:wrap !important;
          }

          .workers-modern-list-v26 .workers-person-title strong{
            max-width:100% !important;
            font-size:13px !important;
            line-height:1.05 !important;
            white-space:nowrap !important;
            overflow:hidden !important;
            text-overflow:ellipsis !important;
          }

          .workers-modern-list-v26 .workers-person-main p{
            font-size:11px !important;
            line-height:1.1 !important;
            margin-top:2px !important;
          }

          .workers-modern-list-v26 .workers-person-main small{
            display:none !important;
          }

          .workers-modern-list-v26 .workers-person-actions-v26{
            grid-column:3 !important;
            grid-row:1 !important;
            justify-self:end !important;
            align-self:start !important;
            width:auto !important;
            min-width:0 !important;
            display:flex !important;
            justify-content:flex-end !important;
            align-items:center !important;
            gap:6px !important;
            margin:0 !important;
          }

          .workers-person-actions-v26 .edit{
            min-width:54px !important;
            min-height:29px !important;
            height:29px !important;
            padding:0 9px !important;
            font-size:11px !important;
            border-radius:999px !important;
          }

          .workers-row-menu-v26 summary{
            width:29px !important;
            height:29px !important;
            min-width:29px !important;
            border-radius:999px !important;
          }

          .workers-modern-list-v26 .workers-person-services-v26{
            grid-column:2 !important;
            grid-row:2 !important;
            justify-self:start !important;
            align-self:start !important;
            margin-top:3px !important;
          }

          .workers-modern-list-v26 .workers-person-public-v30{
            grid-column:2 !important;
            grid-row:3 !important;
            justify-self:start !important;
            align-self:start !important;
            margin-top:2px !important;
          }

          .workers-modern-list-v26 .workers-person-services-v26 span,
          .workers-modern-list-v26 .workers-person-public-v30{
            min-height:28px !important;
            height:28px !important;
            padding:0 10px !important;
            font-size:10.5px !important;
          }

          .workers-modern-list-v26 .workers-person-status{
            grid-column:2 / -1 !important;
            grid-row:4 !important;
            justify-self:start !important;
            align-self:start !important;
            width:auto !important;
            max-width:min(100%, 280px) !important;
            min-width:0 !important;
            min-height:34px !important;
            margin-top:4px !important;
            padding:6px 10px !important;
            border-radius:16px !important;
            display:grid !important;
            grid-template-columns:22px auto !important;
            grid-template-rows:auto auto !important;
            column-gap:8px !important;
            row-gap:0 !important;
            align-items:center !important;
          }

          .workers-modern-list-v26 .workers-person-status i{
            grid-column:1 !important;
            grid-row:1 / span 2 !important;
            width:22px !important;
            height:22px !important;
            flex:0 0 22px !important;
            font-size:10px !important;
          }

          .workers-modern-list-v26 .workers-person-status strong{
            grid-column:2 !important;
            grid-row:1 !important;
            font-size:11px !important;
            line-height:1.05 !important;
            white-space:nowrap !important;
          }

          .workers-modern-list-v26 .workers-person-status span{
            grid-column:2 !important;
            grid-row:2 !important;
            font-size:9px !important;
            line-height:1.05 !important;
            white-space:nowrap !important;
          }
        }


`}</style>

      {true && (
        <>
      {/* Top controls */}
      <section className={["workers-modern-card", "workers-modern-hero", "workers-team-panel", "workers-team-panel-v26", !showPageHeader ? "is-embedded" : ""].filter(Boolean).join(" ")}>
        <div className="workers-team-shell-v30">
          <div className="workers-team-main-v30">
            {showPageHeader ? <span className="workers-kicker">{t("Equipo")}</span> : null}
            {showPageHeader ? <h3>{t(pageTitle)}</h3> : null}
            <p className="workers-team-meta-v27 workers-team-meta-v30" aria-label={t("Resumen rápido del equipo")}>
              <span><b>{workerStats.total}</b>{" " + t("personas")}</span>
              <span><b>{workerStats.pendingAccess}</b>{" " + t("pendientes")}</span>
              <span><b>{workerStats.noAccess + workerStats.expiredAccess}</b>{" " + t("sin acceso")}</span>
              <span><b>{workerStats.service}</b>{" " + t("servicio")}</span>
              <span><b>{workerStats.operational}</b>{" " + t("operativo")}</span>
            </p>
          </div>


        </div>
      </section>

      <div className="workers-team-content-v31">
        <div className="workers-team-list-area-v31">
      {/* List */}
      {filteredWorkerRows.length > 0 ? (
        <div className="workers-list-header-v26 workers-list-header-v30" aria-hidden="true">
          <span>{t("Persona")}</span>
          <span>{t("Servicios")}</span>
          <span>{t("Vista pública")}</span>
          <span>{t("Estado")}</span>
          <span>{t("Acciones")}</span>
        </div>
      ) : null}
      <div className="workers-modern-list workers-modern-list-v26">
        {employees.length === 0 ? (
          <div className="workers-empty-state">
            <strong>{t("No hay personas agregadas.")}</strong>
            <span>{t("Agrega tu primer trabajador para comenzar a organizar servicios, horarios y permisos.")}</span>
            <button type="button" onClick={handleAddWorkerClick}>{t("Agregar persona")}</button>
          </div>
        ) : filteredWorkerRows.length === 0 ? (
          <div className="workers-empty-state">
            <strong>{t("No hay personas en este filtro.")}</strong>
            <span>{t("Prueba con Todos o cambia el tipo desde el perfil de cada persona.")}</span>
          </div>
        ) : (
          filteredWorkerRows.map(({ emp, workerType, assignedServiceNames }) => {
            const typeInfo = workerTypeCopy(workerType);
            const disabled = emp.disabled;
            const accessState = workerInviteAccessState(emp);
            const migrationStatus = workerMigrationStatusCopy(emp);
            const legacyAccess = workerHasProtectedLegacyAccess(emp);
            const rowAccessTone = accessState.key === "linked"
              ? "linked"
              : accessState.key === "pending"
                ? "pending"
                : legacyAccess
                  ? "legacy"
                  : "missing";
            const rowAccessIcon = rowAccessTone === "linked" ? "✓" : rowAccessTone === "pending" ? "2" : rowAccessTone === "legacy" ? "!" : "×";
            const rowAccessLabel = rowAccessTone === "linked"
              ? "Conectado"
              : rowAccessTone === "pending"
                ? "Pendiente"
                : rowAccessTone === "legacy"
                  ? "Acceso anterior"
                  : "Sin acceso";
            const rowAccessHelper = rowAccessTone === "linked"
              ? "Cuenta conectada"
              : rowAccessTone === "pending"
                ? "Esperando conexión"
                : rowAccessTone === "legacy"
                  ? "Protegido"
                  : "Conectar cuenta";
            const rowConnectLabel = accessState.key === "pending" ? "Copiar link" : accessState.key === "expired" ? "Nuevo link" : "Conectar";
            const rowPublicVisible = isWorkerVisibleToCustomers(emp);

            return (
              <article
                key={emp.id}
                className={[
                  "workers-person-row",
                  disabled ? "is-disabled" : "",
                  workerType === "operational" ? "is-operational" : "is-service",
                ].join(" ")}
              >
                <div className="workers-avatar">
                  <img
                    src={emp.photoUrl || defaultWorker}
                    alt={emp.name}
                    style={{ filter: disabled ? "grayscale(100%) opacity(0.72)" : "none" }}
                  />
                </div>

                <div className="workers-person-main">
                  <div className="workers-person-title">
                    <strong>{emp.name || "Sin nombre"}</strong>
                    <span className={workerType === "operational" ? "operational" : "service"}>{typeInfo.badge}</span>
                  </div>
                  <p>{emp.role || typeInfo.title}</p>
                  <small>
                    {assignedServiceNames.length > 0
                      ? assignedServiceNames.join(", ")
                      : workerType === "operational"
                        ? "Solo gestión interna"
                        : "Sin servicios asignados"}
                  </small>
                </div>

                <div className="workers-person-services-v26">
                  <span>{assignedServiceNames.length || 0} {assignedServiceNames.length === 1 ? t("servicio") : t("servicios")}</span>
                </div>

                <div
                  className={`workers-person-public-v30 ${rowPublicVisible ? "is-public" : "is-private"}`}
                  aria-label={rowPublicVisible ? "Visible en reservas y perfil público" : "Oculto para clientes"}
                >
                  <i aria-hidden="true">{rowPublicVisible ? "✓" : "–"}</i>
                  <span>{rowPublicVisible ? "Público" : t("Oculto")}</span>
                </div>

                <div
                  className={`workers-person-status ${rowAccessTone}`}
                  aria-label={`${rowAccessLabel}. ${rowAccessHelper}. ${migrationStatus.helper || ""}`.trim()}
                >
                  <i>{rowAccessIcon}</i>
                  <strong>{rowAccessLabel}</strong>
                  <span>{rowAccessHelper}</span>
                </div>

                <div className="workers-person-actions workers-person-actions-3a3 workers-person-actions-v26">
                  <button type="button" className="edit" onClick={() => {setAgendaSearch(""); openEdit(emp);}}>{t("Gestionar")}</button>
                  <details className="workers-row-menu-v26">
                    <summary aria-label={`Más acciones para ${emp.name || "trabajador"}`}>•••</summary>
                    <div>
                      <button type="button" className="danger" onClick={() => onDeleteEmployee(emp.id)}>{t("Eliminar persona")}</button>
                    </div>
                  </details>
                </div>
              </article>
            );
          })
        )}
      </div>


        </div>

          <aside className="workers-team-side-panel-v30" aria-label={t("Controles del equipo")}>
            <div className="workers-team-side-top-v30">
              <button
                onClick={handleAddWorkerClick}
                className="workers-primary-action workers-primary-action-v26 workers-primary-action-v30"
              >
                <span aria-hidden="true">+</span>{t("Agregar persona")}</button>
            </div>

            <label className="workers-team-search-v26 workers-team-search-v30">
              <span aria-hidden="true">⌕</span>
              <input
                type="search"
                value={workerSearch}
                onChange={(e) => setWorkerSearch(e.target.value)}
                placeholder={t("Buscar persona...")}
              />
            </label>

            <div className="workers-filter-tabs workers-filter-tabs-compact workers-filter-tabs-v26 workers-filter-tabs-v30">
              <button type="button" className={workerFilter === "all" ? "active" : ""} onClick={() => setWorkerFilter("all")}>{t("Todos")}</button>
              <button type="button" className={workerFilter === "linked" ? "active" : ""} onClick={() => setWorkerFilter("linked")}>{t("Con acceso")}</button>
              <button type="button" className={workerFilter === "pending_access" ? "active" : ""} onClick={() => setWorkerFilter("pending_access")}>
                Pendientes
              </button>
              <button type="button" className={workerFilter === "without_access" ? "active" : ""} onClick={() => setWorkerFilter("without_access")}>{t("Sin acceso")}</button>
              <button type="button" className={workerFilter === "service" ? "active" : ""} onClick={() => setWorkerFilter("service")}>
                Servicio
              </button>
              <button type="button" className={workerFilter === "operational" ? "active" : ""} onClick={() => setWorkerFilter("operational")}>{t("Operativo")}</button>
            </div>

            <div className="workers-team-side-bottom-v30">
              <div className="workers-team-public-toggle workers-team-public-v26 workers-team-public-v30">
                <span>{t("Equipo público")}</span>
                <NiceSwitch
                  checked={showTeamOnBusinessPage}
                  disabled={togglingShowTeam}
                  onChange={toggleShowTeamOnBusinessPage}
                  ariaLabel="Mostrar equipo en perfil público"
                />
              </div>

              <details className="workers-maintenance-details workers-team-actions-menu workers-team-actions-v26 workers-team-actions-v30">
                <summary>{t("Avanzado")}</summary>
                <div className="workers-maintenance-body">
                  <p>{t("Revisa accesos existentes o normaliza contactos sin cambiar la forma de entrada del trabajador.")}</p>
                  <div className="workers-migration-actions">
                    <em>{workerStats.linked}{" " + t("vinculados ·") + " "}{workerStats.pendingAccess}{" " + t("pendientes ·") + " "}{workerStats.whatsappMissing}{" " + t("sin WhatsApp")}</em>
                    <button
                      type="button"
                      className="workers-migration-secondary-action"
                      onClick={handleNormalizeWorkersWhatsAppIdentity}
                      disabled={normalizingWorkers || workerBackfillPreview.total === 0}
                      title={t("Repara acceso seguro y contacto sin quitar accesos actuales")}
                    >
                      {normalizingWorkers ? "Reparando..." : workerBackfillPreview.candidates > 0 ? `Reparar ${workerBackfillPreview.candidates}` : "Revisado"}
                    </button>
                  </div>
                  {workerBackfillResult ? (
                    <div className="workers-migration-backfill-result">
                      <span><strong>{workerBackfillResult.updated}</strong>{" " + t("actualizado(s) ·") + " "}{workerBackfillResult.skipped}{" " + t("sin cambios")}</span>
                      {workerBackfillResult.failed ? <span>{workerBackfillResult.failed}{" " + t("error(es)")}</span> : <span>{t("Accesos actuales protegidos")}</span>}
                    </div>
                  ) : null}
                </div>
              </details>
            </div>
          </aside>
      </div>

      {/* Add worker modal (portal) */}

      {workerPhotoCropFile && createPortal(
        <div
          className="loop-worker-photo-cropper-layer"
          data-loop-cropper="true"
          role="dialog"
          aria-modal="true"
          aria-label={t("Ajustar foto del trabajador")}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeWorkerPhotoCropper();
          }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2147483647,
            background: "rgba(15, 23, 42, 0.62)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "calc(env(safe-area-inset-top, 0px) + 22px) 18px calc(env(safe-area-inset-bottom, 0px) + 22px)",
            overflow: "auto",
          }}
        >
          <div
            className="loop-worker-photo-cropper-panel"
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              width: "min(920px, calc(100vw - 28px))",
              maxHeight: "min(820px, calc(100vh - 44px))",
              overflow: "auto",
              background: "#fff",
              borderRadius: 28,
              border: "1px solid rgba(226,232,240,.92)",
              boxShadow: "0 30px 90px rgba(15,23,42,.36)",
              padding: 18,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 11, letterSpacing: ".16em", textTransform: "uppercase", fontWeight: 950, color: "#00675f" }}>{t("Foto del trabajador")}</div>
                <h3 style={{ margin: "4px 0 0", fontSize: 22, lineHeight: 1.05, fontWeight: 950, color: "#0f172a" }}>{t("Ajustar imagen")}</h3>
              </div>
              <button
                type="button"
                onClick={closeWorkerPhotoCropper}
                aria-label={t("Cerrar recorte")}
                style={{ width: 42, height: 42, borderRadius: 999, border: "1px solid #dbe5ee", background: "#fff", color: "#0f172a", fontSize: 24, lineHeight: "38px", fontWeight: 800 }}
              >
                ×
              </button>
            </div>
            <ImageCropper
              file={workerPhotoCropFile}
              aspect={1}
              outputWidth={900}
              outputHeight={900}
              backgroundColor="#ffffff"
              // Keep worker preview faithful to the adjusted image. The profile card also uses object-fit: contain,
              // so saving does not apply a second crop/zoom after the user confirms the cropper.
              fitMode="contain"
              onCancel={closeWorkerPhotoCropper}
              onComplete={handleWorkerPhotoCropComplete}
            />
            {workerPhotoStatus ? (
              <div style={{ marginTop: 12, color: "#64748b", fontSize: 13, fontWeight: 800 }}>{workerPhotoStatus}</div>
            ) : null}
          </div>
        </div>,
        document.body
      )}

      {addModalOpen && createPortal(
        <div className="workers-add-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setAddModalOpen(false); }}>
          <div
            onMouseDown={(e) => e.stopPropagation()}
            className="workers-add-modal-shell"
          >
            <div className="workers-add-modal-head">
              <div>
                <strong>{t("Agregar persona")}</strong>
                <span>{t("Configura si será visible para reservas o solo para operación interna.")}</span>
              </div>
              <button type="button" className="workers-add-modal-close" onClick={() => setAddModalOpen(false)} aria-label={t("Cerrar")}>
                ✕
              </button>
            </div>

            <div className="workers-add-modal-body">
              <div className="workers-add-modal-field">
                <label>{t("Nombre")}</label>
                <input value={newName} onChange={(e) => setNewName(e.target.value)} />
              </div>
              <div className="workers-add-modal-field">
                <label>{t("Rol")}</label>
                <input value={newRole} onChange={(e) => setNewRole(e.target.value)} placeholder={t("Ej. Estilista, Barbero, Asistente")} />
              </div>

              <div className="workers-add-modal-field workers-add-phone-field">
                <PhoneInput
                  label="WhatsApp"
                  value={newPhone}
                  onChange={(display) => setNewPhone(display)}
                  defaultCountry="PA"
                  hint="Opcional por ahora. El acceso se genera con link seguro; WhatsApp estará disponible próximamente."
                />
              </div>

              <div className="workers-add-type">
                <label>{t("Tipo de persona")}</label>
                <div>
                  <button type="button" className={newWorkerType === "service" ? "active" : ""} onClick={() => setNewWorkerType("service")}>{t("De servicio")}</button>
                  <button type="button" className={newWorkerType === "operational" ? "active" : ""} onClick={() => setNewWorkerType("operational")}>{t("Operativo")}</button>
                </div>
                <p>{newWorkerType === "service" ? "Aparece en reservas si el cliente puede elegir trabajador." : "Solo aparece para agenda interna y operación."}</p>
              </div>
            </div>

            <div className="workers-add-modal-actions">
              <button type="button" className="workers-add-modal-cancel" onClick={() => setAddModalOpen(false)}>{t("Cancelar")}</button>
              <button type="button" className="workers-add-modal-submit" onClick={submitAddWorker}>
                Agregar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

        </>
      )}

      {/* Worker editor modal (portal) */}
      {modalOpen && editingEmployee && createPortal(
        <div
          className="workers-editor-modal-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeEdit();
          }}
        >
          <div
            onMouseDown={(e) => e.stopPropagation()}
            className="bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden workers-editor-shell workers-editor-modal-shell"
            style={{ display: "flex", flexDirection: "column" }}
          >
            {/* Sticky header */}
            <div className="sticky top-0 z-10 bg-white border-b border-slate-100 px-4 py-3 workers-editor-head">
              <div className="workers-editor-title-row">
                <div className="workers-editor-title-left">
                  <button className="workers-back-button" onClick={closeEdit}>{t("← Equipo")}</button>
                  <div className="workers-editor-title-copy">
                    <div className="workers-editor-kicker">{t("Editar persona")}</div>
                    <div className="workers-editor-name">{editingEmployee.name || t("Trabajador")}</div>
                    <div className="workers-editor-subtitle">
                      {(editingEmployee.role || workerTypeCopy(normalizeWorkerType(editingEmployee)).title)} · {workerTypeCopy(normalizeWorkerType(editingEmployee)).badge}
                    </div>
                  </div>
                </div>

                <div className="workers-editor-actions workers-editor-actions-minimal">
                  <span className="workers-editor-type-badge">
                    {isWorkerVisibleToCustomers(editingEmployee) ? "Visible al cliente" : "No visible al cliente"}
                  </span>
                </div>
              </div>

              {/* ✅ Horizontal tabs ALWAYS visible */}
              <div className="mt-3 flex gap-2 overflow-x-auto pb-1 workers-editor-tabs-clean" style={{ WebkitOverflowScrolling: "touch" }}>
                <TabPill active={activeTab === "general"} onClick={() => setActiveTab("general")}>{t("Perfil")}</TabPill>
                <TabPill active={activeTab === "servicesComp" || activeTab === "services" || activeTab === "compensation"} onClick={() => setActiveTab("servicesComp")}>{t("Servicios")}</TabPill>
                <TabPill active={activeTab === "schedule"} onClick={() => setActiveTab("schedule")}>Agenda</TabPill>
                <TabPill active={activeTab === "permissions"} onClick={() => setActiveTab("permissions")}>{t("Acceso")}</TabPill>
                <TabPill active={activeTab === "projects"} onClick={() => setActiveTab("projects")}>{t("Portfolio")}</TabPill>
              </div>
            </div>

            {/* Scroll body */}
            <div ref={editorBodyRef} className="flex-1 overflow-y-auto p-4 workers-editor-body" style={{ minHeight: 0 }}>
              {activeTab === "general" && (
                <div className="workers-general-tab workers-general-premium-tab workers-general-minimal-v21">
                  <section className="workers-general-premium-shell workers-general-premium-shell-v21">
                    <aside className="workers-profile-card-premium workers-profile-photo-only-v21">
                      <div className="workers-profile-photo-frame">
                        <img
                          src={editingEmployee.photoUrl || defaultWorker}
                          alt="worker"
                          className="workers-profile-photo-img"
                        />
                        <button
                          type="button"
                          onClick={() => photoInputRef.current && photoInputRef.current.click()}
                          className="workers-profile-photo-overlay"
                          disabled={Boolean(workerPhotoStatus)}
                        >
                          {workerPhotoStatus ? "Procesando..." : "Cambiar foto"}
                        </button>
                      </div>

                      <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif" className="hidden" onChange={onSelectPhoto} />

                      <button
                        type="button"
                        onClick={() => photoInputRef.current && photoInputRef.current.click()}
                        className="workers-profile-photo-button"
                        disabled={Boolean(workerPhotoStatus)}
                      >
                        {workerPhotoStatus ? "Procesando..." : "Cambiar foto"}
                      </button>

                      {workerPhotoStatus ? <div className="workers-photo-status">{workerPhotoStatus}</div> : null}
                    </aside>

                    <main className="workers-profile-form-premium workers-general-form-v21">
                      <div className="workers-profile-fields-card workers-profile-fields-card-minimal workers-general-card-v21">
                        <div className="workers-general-topbar-v21">
                          <div>
                            <span>{t("General")}</span>
                            <strong>{t("Identidad y contacto")}</strong>
                          </div>

                          <div className="workers-general-controls-v21">
                            <label className="workers-mini-select-v21">
                              <span>{t("Tipo")}</span>
                              <select
                                value={normalizeWorkerType(editingEmployee)}
                                onChange={(e) => {
                                  const value = e.target.value === "operational" ? "operational" : "service";
                                  setEditingEmployee((prev) => ({
                                    ...prev,
                                    workerType: value,
                                    staffType: value,
                                    type: value,
                                    isOperationalWorker: value === "operational",
                                    operationalOnly: value === "operational",
                                    hiddenFromBooking: value === "operational",
                                    hiddenFromPublicProfile: value === "operational",
                                    visibleToCustomers: value === "service",
                                    showOnBusinessPage: value === "service",
                                    canBeBookedOnline: value === "service",
                                  }));
                                }}
                              >
                                <option value="service">{t("De servicio")}</option>
                                <option value="operational">{t("Operativo")}</option>
                              </select>
                            </label>

                            <div className="workers-mini-visibility-v21">
                              <div>
                                <span>{t("Visible")}<i
                                    aria-label={t("Información sobre visibilidad")}
                                    title={normalizeWorkerType(editingEmployee) === "operational" ? "Los perfiles operativos quedan ocultos para clientes y sirven para gestión interna." : "Si está activo, esta persona puede aparecer en reservas y perfil público."}
                                  >i</i>
                                </span>
                              </div>
                              <NiceSwitch
                                checked={isWorkerVisibleToCustomers(editingEmployee)}
                                disabled={normalizeWorkerType(editingEmployee) === "operational"}
                                onChange={updateCustomerVisibility}
                                ariaLabel="Cambiar visibilidad del trabajador para clientes"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="workers-profile-fields-grid workers-profile-fields-grid-clean workers-general-fields-v21">
                          <label className="workers-premium-field">
                            <span>{t("Nombre")}</span>
                            <input
                              value={editingEmployee.name || ""}
                              onChange={(e) => updateField("name", e.target.value)}
                              placeholder={t("Nombre visible")}
                            />
                          </label>

                          <label className="workers-premium-field">
                            <span>{t("Rol")}</span>
                            <input
                              value={editingEmployee.role || ""}
                              onChange={(e) => updateField("role", e.target.value)}
                              placeholder={t("Ej. Estilista")}
                            />
                          </label>

                          <label className="workers-premium-field">
                            <span>{t("Email")}</span>
                            <input
                              value={editingEmployee.email || ""}
                              onChange={(e) => updateField("email", e.target.value)}
                              placeholder="correo@ejemplo.com"
                            />
                          </label>

                          <div className="workers-premium-field workers-phone-clean-field-v21">
                            <PhoneInput
                              label={t("Teléfono / WhatsApp")}
                              value={editingEmployee.phone || editingEmployee.phoneDisplay || editingEmployee.phoneE164 || ""}
                              onChange={(display, parsed) => {
                                setEditingEmployee((prev) => ({
                                  ...(prev || {}),
                                  ...buildWorkerPhoneIdentityFields(display, prev || {}),
                                  phone: display,
                                  phoneDisplay: parsed?.display || display,
                                  phoneE164: parsed?.e164 || normalizePhoneToE164(display, "PA"),
                                  phoneNormalized: parsed?.e164 || normalizePhoneToE164(display, "PA"),
                                }));
                              }}
                              defaultCountry="PA"
                            />
                          </div>
                        </div>
                      </div>
                    </main>
                  </section>
                </div>
              )}


              {activeTab === "access" && (
                <div className="workers-access-tab workers-access-wizard">
                  <section className={`workers-access-simple-card is-${accessWizardMeta.tone}`}>
                    <div className="workers-access-wizard-top">
                      <div className="workers-access-wizard-copy">
                        <span className="workers-access-wizard-eyebrow">
                          <b>{editingAccessState.key === "linked" ? "✓" : editingAccessState.key === "pending" ? "2" : "1"}</b>
                          {accessWizardMeta.badge}
                        </span>
                        <h3>{accessWizardMeta.title}</h3>
                        <p>{accessWizardMeta.subtitle}</p>
                      </div>

                      <div className="workers-access-wizard-actions">
                        <button
                          type="button"
                          className="workers-access-wizard-primary"
                          onClick={editingAccessState.key === "linked" ? () => setActiveTab("permissions") : openAccessShareChooser}
                          disabled={editingAccessState.key !== "linked" && (!editingEmployee?.id)}
                        >
                          {accessWizardMeta.primaryLabel}
                        </button>

                        {editingAccessState.key === "pending" || editingAccessState.key === "expired" ? (
                          <button
                            type="button"
                            className="workers-access-wizard-secondary"
                            onClick={handleResetWorkerAccess}
                            disabled={inviteCopying || !editingEmployee?.id}
                          >
                            {accessWizardMeta.secondaryLabel}
                          </button>
                        ) : null}
                      </div>
                    </div>

                    <div className="workers-access-steps" aria-label={t("Pasos para dar acceso al trabajador")}>
                      {accessWizardSteps.map((step) => (
                        <div key={step.number} className={`workers-access-step is-${step.state}`}>
                          <span className="workers-access-step-number">{step.state === "done" ? "✓" : step.number}</span>
                          <strong className="workers-access-step-label">{t(step.label)}</strong>
                        </div>
                      ))}
                    </div>

                    <div className="workers-access-mini-row">
                      <span>{accessWizardMeta.tinyNote}</span>
                      <strong>{editingAccessState.key === "linked" ? t("Activo") : editingAccessState.key === "pending" ? t("Pendiente") : t("Link seguro")}</strong>
                    </div>
                  </section>

                  <section className="workers-access-tech-toggle">
                    <button
                      type="button"
                      onClick={() => setAccessDetailsOpen((v) => !v)}
                    >
                      <span>{t("Detalles técnicos")}</span>
                      <span style={{ color: "#64748b", fontSize: 13 }}>{accessDetailsOpen ? "Ocultar" : t("Ver")}</span>
                    </button>

                    {accessDetailsOpen && (
                      <div className="workers-access-tech-grid">
                        <div>
                          <span>{t("Correo opcional")}</span>
                          <strong>{editingAccessState.invitedEmail || "No requerido"}</strong>
                        </div>
                        <div>
                          <span>{t("Cuenta autenticada")}</span>
                          <strong>{editingAccessState.authEmail || (editingAccessState.key === "linked" ? "Vinculada por UID" : t("Pendiente"))}</strong>
                        </div>
                        <div>
                          <span>{t("ID interno")}</span>
                          <strong>{editingAccessState.linkedUid ? maskWorkerUid(editingAccessState.linkedUid) : "No vinculado"}</strong>
                        </div>
                        <div>
                          <span>{t("Expiración")}</span>
                          <strong>{editingAccessState.expiresAt ? formatWorkerInviteExpiry(editingAccessState.expiresAt) : "Sin link activo"}</strong>
                        </div>
                        <div>
                          <span>WhatsApp</span>
                          <strong>{t("Próximamente")}</strong>
                        </div>
                        <div>
                          <span>{t("Acción avanzada")}</span>
                          <button
                            type="button"
                            onClick={editingAccessState.key === "linked" ? handleResetWorkerAccess : () => handleWhatsAppInviteStandby(editingEmployee)}
                            disabled={inviteCopying || !editingEmployee?.id}
                            style={{ marginTop: 6, minHeight: 34, borderRadius: 999, border: "1px solid rgba(0,103,95,.18)", background: "#fff", color: "#00675f", padding: "0 12px", fontSize: 11, fontWeight: 950 }}
                          >
                            {editingAccessState.key === "linked" ? "Cambiar acceso" : "Ver WhatsApp"}
                          </button>
                        </div>
                      </div>
                    )}
                  </section>
                </div>
              )}


              {activeTab === "schedule" && (
                <div className="workers-schedule-tab">
                  <div className="workers-schedule-summary workers-schedule-summary-clean">
                    <div className="workers-schedule-summary-top">
                      <div>
                        <h4>{t("Horario semanal")}</h4>
                        <p>
                          {WEEK_DISPLAY_DAYS.filter((d) => {
                            const avail = ensureAvailabilityShape(editingEmployee.availability);
                            return Array.isArray(avail[d.key]) && avail[d.key].length > 0;
                          }).length}{" " + t("días activos ·") + " "}{summarizeWeeklyAvailability(editingEmployee.availability)}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="workers-company-hours-button"
                        onClick={applyBusinessSchedule}
                        disabled={!businessAvailability}
                        title={businessAvailability ? "Copiar disponibilidad general del negocio" : "Configura primero la disponibilidad del negocio"}
                      >{t("Usar horario de la empresa")}</button>
                    </div>

                    <details className="workers-schedule-more-actions">
                      <summary>{t("Más opciones")}</summary>
                      <div>
                        <button type="button" onClick={applyStandardSchedule}>{t("Horario estándar")}</button>
                        <button type="button" onClick={copyMondayToWeekdays}>{t("Copiar Lun–Vie")}</button>
                        <button type="button" onClick={closeAllDays}>{t("Cerrar todos")}</button>
                      </div>
                    </details>
                  </div>

                  <div className="workers-schedule-list">
                    {WEEK_DISPLAY_DAYS.map((d) => {
                      const avail = ensureAvailabilityShape(editingEmployee.availability);
                      const ranges = Array.isArray(avail[d.key]) ? avail[d.key] : [];
                      const enabled = ranges.length > 0;
                      const main = ranges[0] || { from: "10:00", to: "19:00" };
                      const breakR = ranges[1] || null;
                      const totalMins = scheduleTotalsByDay[d.key] || 0;
                      const isOpen = openDayKey === d.key;

                      return (
                        <div key={d.key} className={["workers-day-row", isOpen ? "is-open" : ""].join(" ")}>
                          <div className="workers-day-row-button">
                            <button
                              type="button"
                              className="workers-day-row-main"
                              onClick={() => setOpenDayKey(isOpen ? "" : d.key)}
                            >
                              <div className="workers-day-icon" aria-hidden="true">▣</div>
                              <div className="workers-day-copy">
                                <strong>{prettyDayNameEs(d.key)}</strong>
                                <span>{dayScheduleLabel(ranges)}</span>
                              </div>
                            </button>

                            <div className="workers-day-row-side">
                              <div className={["workers-day-inline-state", enabled ? "is-active" : ""].join(" ")}>
                                {enabled ? formatDurationMins(totalMins) : t("Cerrado")}
                              </div>
                              <NiceSwitch
                                checked={enabled}
                                disabled={saving}
                                onChange={(v) => setDayEnabled(d.key, v)}
                                ariaLabel={`Disponibilidad ${prettyDayNameEs(d.key)}`}
                              />
                              <button
                                type="button"
                                className="workers-day-chevron-button"
                                onClick={() => setOpenDayKey(isOpen ? "" : d.key)}
                                aria-label={`${isOpen ? "Cerrar" : "Editar"} ${prettyDayNameEs(d.key)}`}
                              >
                                {isOpen ? "⌃" : "⌄"}
                              </button>
                            </div>
                          </div>

                          {isOpen ? (
                            <div className="workers-day-editor">
                              {enabled ? (
                                <>
                                  <div className="workers-time-grid">
                                    <div className="workers-time-field">
                                      <label>{t("Inicio")}</label>
                                      <input
                                        type="time"
                                        value={main.from}
                                        onChange={(e) => setDayRange(d.key, 0, "from", e.target.value)}
                                      />
                                    </div>
                                    <div className="workers-time-field">
                                      <label>{t("Fin")}</label>
                                      <input
                                        type="time"
                                        value={main.to}
                                        onChange={(e) => setDayRange(d.key, 0, "to", e.target.value)}
                                      />
                                    </div>
                                    {!breakR ? (
                                      <button type="button" onClick={() => addBreak(d.key)} className="workers-mini-action">{t("+ Descanso")}</button>
                                    ) : (
                                      <span />
                                    )}
                                  </div>

                                  {breakR ? (
                                    <div className="workers-time-grid compact">
                                      <div className="workers-time-field">
                                        <label>{t("Descanso inicia")}</label>
                                        <input
                                          type="time"
                                          value={breakR.from}
                                          onChange={(e) => setDayRange(d.key, 1, "from", e.target.value)}
                                        />
                                      </div>
                                      <div className="workers-time-field">
                                        <label>{t("Descanso termina")}</label>
                                        <input
                                          type="time"
                                          value={breakR.to}
                                          onChange={(e) => setDayRange(d.key, 1, "to", e.target.value)}
                                        />
                                      </div>
                                      <button type="button" onClick={() => removeRangeAt(d.key, 1)} className="workers-mini-action danger">{t("Quitar")}</button>
                                    </div>
                                  ) : null}
                                </>
                              ) : (
                                <div className="workers-closed-note">{t("Este día no aparecerá como disponible para esta persona.")}</div>
                              )}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>

                  <div className="workers-blocked-compact">
                    <div>
                      <strong>{t("Fechas no disponibles")}</strong>
                      <span>{blockedSplit.active.length}{" " + t("activas ·") + " "}{blockedSplit.history.length}{" " + t("en historial")}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setBlockedTab("active");
                        setBlockedModalOpen(true);
                      }}
                    >{t("Gestionar")}</button>
                  </div>

                  {/* Blocked dates popup (safe on mobile) */}
                  {blockedModalOpen ? (
                    <div className="fixed inset-0 z-[150] bg-black/60 p-3 overflow-auto" onClick={() => setBlockedModalOpen(false)}>
                      <div
                        onClick={(e) => e.stopPropagation()}
                        className="mx-auto w-full max-w-2xl bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden"
                        style={{ maxHeight: "92vh", display: "flex", flexDirection: "column" }}
                      >
                        <div className="sticky top-0 z-10 bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between">
                          <div>
                            <div className="font-black text-slate-900">{t("Fechas no disponibles")}</div>
                            <div className="text-xs text-slate-500">{t("Bloquea días completos por rango.")}</div>
                          </div>
                          <button className="px-3 py-2 rounded-xl bg-slate-100 font-black text-slate-700" onClick={() => setBlockedModalOpen(false)}>{t("Cerrar")}</button>
                        </div>

                        <div className="p-4 border-b border-slate-100 flex gap-2">
                          <TabPill active={blockedTab === "active"} onClick={() => setBlockedTab("active")}>{t("Activas (")}{blockedSplit.active.length})
                          </TabPill>
                          <TabPill active={blockedTab === "history"} onClick={() => setBlockedTab("history")}>{t("Historial (")}{blockedSplit.history.length})
                          </TabPill>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4" style={{ minHeight: 0 }}>
                          {blockedTab === "active" ? (
                            <div className="space-y-3">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-black text-slate-600 mb-1">{t("Desde")}</label>
                                  <input
                                    type="date"
                                    className="w-full p-3 border rounded-2xl"
                                    value={editingEmployee.__newBlockedFrom || ""}
                                    onChange={(e) => updateField("__newBlockedFrom", e.target.value)}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-black text-slate-600 mb-1">Hasta</label>
                                  <input
                                    type="date"
                                    className="w-full p-3 border rounded-2xl"
                                    value={editingEmployee.__newBlockedTo || ""}
                                    onChange={(e) => updateField("__newBlockedTo", e.target.value)}
                                  />
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const f = editingEmployee && editingEmployee.__newBlockedFrom;
                                    const t = editingEmployee && editingEmployee.__newBlockedTo;
                                    if (f && t) addBlockedRange(f, t);
                                  }}
                                  className="px-4 py-3 rounded-2xl bg-emerald-600 text-white font-extrabold"
                                >
                                  Agregar
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    updateField("__newBlockedFrom", "");
                                    updateField("__newBlockedTo", "");
                                  }}
                                  className="px-4 py-3 rounded-2xl bg-white border border-slate-200 font-extrabold"
                                >{t("Limpiar")}</button>
                              </div>
                            </div>
                          ) : null}

                          <div className="mt-4 space-y-2">
                            {(blockedTab === "active" ? blockedSplit.active : blockedSplit.history).length === 0 ? (
                              <div className="text-sm text-slate-500">
                                {blockedTab === "active" ? "No hay bloqueos activos." : "No hay historial de bloqueos."}
                              </div>
                            ) : (
                              (blockedTab === "active" ? blockedSplit.active : blockedSplit.history).map((r) => (
                                <div
                                  key={`${r.from}_${r.to}_${blockedTab}`}
                                  className="flex items-center justify-between gap-3 p-3 rounded-2xl border border-slate-200"
                                >
                                  <div className="font-bold text-slate-800 text-sm">
                                    {r.from} <span className="text-slate-400">→</span> {r.to}
                                  </div>
                                  {blockedTab === "active" ? (
                                    <button
                                      type="button"
                                      onClick={() => removeBlockedRange(r)}
                                      className="px-3 py-2 rounded-xl bg-red-50 border border-red-200 font-extrabold text-red-700"
                                    >{t("Eliminar")}</button>
                                  ) : (
                                    <div className="text-xs text-slate-400 font-bold">{t("Pasado")}</div>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}

              {(activeTab === "servicesComp" || activeTab === "services" || activeTab === "compensation") && (
                <div className="workers-services-comp-tab">
                  {(() => {
                    const isOperational = normalizeWorkerType(editingEmployee) === "operational";
                    const hasServices = (services || []).length > 0;
                    const commissionType = normalizeCommissionType(editingEmployee.defaultCommissionType);
                    const hasCommission = commissionType !== "none";
                    const commissionSummary = hasCommission
                      ? `${workerCommissionTypeLabel(editingEmployee)} · ${workerCommissionApplyLabel(editingEmployee)}`
                      : "Sin comisión";

                    return (
                      <>
                        <section className="workers-service-comp-premium-shell">
                          <div className="workers-service-comp-premium-head">
                            <h4>{t("Servicios y compensación")}</h4>
                          </div>

                          <div className="workers-service-comp-summary-row">
                            <div className="workers-service-comp-mini-card">
                              <span className="workers-service-comp-mini-icon" aria-hidden="true">▣</span>
                              <div>
                                <strong>{selectedWorkerServiceCount}{" " + t("de") + " "}{(services || []).length}</strong>
                                <span>{t("servicios")}</span>
                              </div>
                            </div>

                            <div className="workers-service-comp-mini-card is-compensation">
                              <span className="workers-service-comp-mini-icon" aria-hidden="true">%</span>
                              <div>
                                <strong>{commissionSummary}</strong>
                                <span>{hasCommission ? workerCommissionBaseLabel(editingEmployee) : "payout apagado"}</span>
                              </div>
                            </div>

                            <button
                              type="button"
                              className="workers-service-comp-mini-action"
                              onClick={() => setCompensationEditorOpen(true)}
                            >
                              <span className="workers-service-comp-mini-icon" aria-hidden="true">✎</span>
                              <strong>{t("Editar comisión")}</strong>
                            </button>
                          </div>
                        </section>

                        {isOperational ? (
                          <div className="workers-service-comp-inline-note">
                            <strong>{t("Equipo operativo")}</strong>
                            <span>{t("No aparece como opción pública al reservar.")}</span>
                          </div>
                        ) : null}

                        <section className="workers-service-chip-card">
                          <div className="workers-service-chip-head">
                            <span>{t("Servicios")}</span>
                          </div>

                          {hasServices ? (
                            <>
                              <div className="workers-service-chip-toolbar">
                                <label className="workers-service-chip-search">
                                  <span aria-hidden="true">⌕</span>
                                  <input
                                    value={workerServiceSearch}
                                    onChange={(e) => setWorkerServiceSearch(e.target.value)}
                                    placeholder={t("Buscar servicio")}
                                  />
                                </label>

                                <button type="button" className="primary-soft" onClick={assignAllServicesToWorker}>{t("Asignar todos")}</button>
                                <button type="button" onClick={clearWorkerServices}>{t("Limpiar")}</button>
                              </div>

                              <div className="workers-service-chip-grid">
                                {filteredWorkerServices.length === 0 ? (
                                  <div className="workers-services-empty">
                                    <strong>{t("No encontramos servicios.")}</strong>{t("Prueba con otro nombre o limpia la búsqueda.")}</div>
                                ) : (
                                  filteredWorkerServices.map((s) => {
                                    const checked = (editingEmployee.services || []).some((sid) => String(sid) === String(s.id));
                                    const displayName = getServiceDisplayName(s);

                                    return (
                                      <button
                                        type="button"
                                        key={s.id}
                                        onClick={() => toggleService(s.id)}
                                        className={["workers-service-chip", checked ? "is-selected" : "is-muted"].join(" ")}
                                      >
                                        <span className="workers-service-chip-icon" aria-hidden="true">
                                          {checked ? "✓" : "+"}
                                        </span>
                                        <strong>{displayName}</strong>
                                      </button>
                                    );
                                  })
                                )}
                              </div>
                            </>
                          ) : (
                            <div className="workers-services-empty">
                              <strong>{t("No hay servicios disponibles.")}</strong>{t("Primero agrega servicios al negocio para poder asignarlos a esta persona.")}</div>
                          )}
                        </section>
                      </>
                    );
                  })()}
                </div>
              )}




              {activeTab === "permissions" && (() => {
                const normalizedPermissions = normalizeWorkerPermissions(editingEmployee);
                const connectionState = workerConnectionStatusCopy(editingEmployee || {});
                const linked = Boolean(editingEmployee.userUid || editingEmployee.authUid);
                return <div className="worker-access-clean">
                  <section className="worker-account-line">
                    <div><span className="worker-field-caption">{t("Cuenta de Loop")}</span><strong>{editingAccessState.authEmail || (linked ? 'Cuenta conectada' : 'Sin cuenta conectada')}</strong><span className="worker-status-text">{connectionState.label}</span></div>
                    {!linked && <button type="button" className="worker-solid-button" onClick={openAccessShareChooser} disabled={!editingEmployee.id}>{t("Invitar trabajador")}</button>}
                    {linked && <details className="worker-action-menu"><summary>{t("Acciones")}</summary><div><button type="button" disabled={inviteCopying} onClick={changeWorkerAccessStatus}>{editingEmployee.accessStatus === 'suspended' ? 'Reactivar acceso' : 'Suspender acceso'}</button><button type="button" disabled={inviteCopying} onClick={handleResetWorkerAccess}>{t("Desvincular cuenta")}</button></div></details>}
                  </section>
                  {workerConnectionsError && <p role="alert" className="worker-inline-error">{workerConnectionsError}</p>}
                  <section className="worker-access-summary">
                    <h4>{t("Rol y agendas")}</h4>
                    <p>{t("Guarda los cambios antes de enviar la invitación.")}</p>
                    <label>{t("Rol de acceso")}<select value={getAccessRole(editingEmployee)} onChange={event => updateAccessRole(event.target.value)}>{LOOP_BUSINESS_ROLES.filter(role => role.key !== 'owner').map(role => <option key={role.key} value={role.key}>{t(role.label)}</option>)}</select></label>
                    <p className="worker-role-description">{({worker:'Atiende sus reservas y consulta su agenda.', reception:'Coordina reservas y la atención al cliente.', admin:'Gestiona la operación del negocio.', finance:'Consulta la información financiera.', readonly:'Consulta información sin modificarla.'})[getAccessRole(editingEmployee)] || 'Selecciona un rol y ajusta los permisos si lo necesitas.'}</p>
                              <label>{t("Agendas que puede consultar")}<select value={normalizedPermissions.canViewAllCalendars ? 'all' : normalizedPermissions.canViewSelectedCalendars ? 'selected' : normalizedPermissions.canViewOwnCalendar ? 'own' : 'none'} onChange={event => {
                                  const scope = event.target.value;
                                  setEditingEmployee(prev => ({...prev, permissions: {...prev.permissions, canViewOwnCalendar: scope !== 'none', canViewAllCalendars: scope === 'all', canViewSelectedCalendars: scope === 'selected'}}));
                                }}>
                                  <option value="own">{t("Solo su agenda")}</option><option value="selected">{t("Su agenda y agendas seleccionadas")}</option><option value="all">{t("Todas las agendas")}</option><option value="none">{t("Sin acceso a agendas")}</option>
                                </select>
                              </label>
                              {normalizedPermissions.canViewSelectedCalendars && !normalizedPermissions.canViewAllCalendars && <div className="worker-access-agendas"><input className="worker-agenda-search" type="search" aria-label={t("Buscar agenda")} placeholder={t("Buscar trabajador…")} value={agendaSearch} onChange={event => setAgendaSearch(event.target.value)} />{(employees || []).filter(employee => employee.id !== editingEmployee.id && String(employee.name || "").toLocaleLowerCase().includes(agendaSearch.toLocaleLowerCase())).map(employee => <label key={employee.id}><input type="checkbox" checked={(normalizedPermissions.allowedCalendarIds || []).includes(employee.id)} onChange={event => {
                                const checked = event.target.checked;
                                setEditingEmployee(prev => {const ids = prev.permissions?.allowedCalendarIds || []; return {...prev, permissions: {...prev.permissions, allowedCalendarIds: checked ? [...new Set([...ids, employee.id])] : ids.filter(id => id !== employee.id)}};});
                              }} />{employee.name || t("Trabajador")}</label>)}</div>}

                  </section>
                  <details className="worker-advanced-permissions"><summary>{t("Personalizar permisos")}<span>{t("Opcional")}</span></summary>
                    <label className="worker-compensation-field">{t("Compensación visible")}<select value={editingEmployee.compensationVisibilityOverride || 'business_default'} onChange={event => updateField('compensationVisibilityOverride', event.target.value)}><option value="business_default">{t("Según el negocio")}</option><option value="hidden">{t("Oculta")}</option><option value="tips_only">{t("Solo propinas")}</option><option value="payout_total">{t("Pago total")}</option><option value="full_breakdown">{t("Desglose completo")}</option></select></label>
                            <div className="workers-permission-groups-grid workers-permission-groups-grid-compact">
                              {WORKER_PERMISSION_GROUPS.map((group, groupIndex) => {
                                const groupItems = group.items || [];
                                const groupKey = String(group.key || group.title || groupIndex);
                                const groupTitle = String(group.title || "Permisos");
                                const groupActiveCount = groupItems.filter(([key]) => Boolean(normalizedPermissions[key])).length;
                                const groupTotal = groupItems.length;
                                const defaultOpen = false;
                                const hasCustomOpen = Object.prototype.hasOwnProperty.call(permissionAccordionOpen.groups || {}, groupKey);
                                const isOpen = hasCustomOpen ? Boolean(permissionAccordionOpen.groups?.[groupKey]) : defaultOpen;
                                const activeLabels = groupItems
                                  .filter(([key]) => Boolean(normalizedPermissions[key]))
                                  .map(([, label]) => label)
                                  .slice(0, 3);

                                return (
                                  <section key={group.title} className={`workers-permission-group-card workers-permission-group-accordion ${isOpen ? "is-open" : "is-collapsed"}`}>
                                    <button
                                      type="button"
                                      className="workers-permission-group-summary"
                                      onClick={() => togglePermissionGroupPanel(groupKey)}
                                      aria-expanded={isOpen}
                                    >
                                      <div>
                                        <h5>{t(group.title)}</h5>
                                        {group.description ? <p>{t(group.description)}</p> : null}
                                      </div>
                                      <span className="workers-permission-group-count">{groupActiveCount}/{groupTotal}</span>
                                      <i aria-hidden="true">{isOpen ? "−" : "+"}</i>
                                    </button>

                                    {isOpen ? (
                                      <div className="workers-permission-rows">
                                        {groupItems.map(([key, label]) => {
                                          const checked = Boolean(normalizedPermissions[key]);
                                          return (
                                            <div key={key} className="workers-permission-full-row">
                                              <div>
                                                <strong>{t(label)}</strong>
                                                <span>{t(checked ? "Activo" : "Desactivado")}</span>
                                              </div>
                                              <NiceSwitch
                                                checked={checked}
                                                disabled={EXCEPTION_KEYS.includes(key) && !canGrantScheduleExceptions}
                                                onChange={(next) => updatePermission(key, next)}
                                                ariaLabel={t(label)}
                                              />
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      <div className="workers-permission-group-preview">
                                        {activeLabels.length ? activeLabels.map((label) => <span key={label}>{t(label)}</span>) : <span>{t("Sin permisos activos")}</span>}
                                        {groupActiveCount > activeLabels.length ? <em>+{groupActiveCount - activeLabels.length}</em> : null}
                                      </div>
                                    )}
                                  </section>
                                );
                              })}
                            </div>
                  </details>
                  <details className="worker-history" onToggle={async event => {if (!event.currentTarget.open) return; try {const result = await manageWorkerAccess(businessId, editingEmployee.id, 'history'); setAccessHistory(result.items || []);} catch (err) {setWorkerConnectionsError(err.message);}}}>
                    <summary>{t("Historial de acceso")}</summary>
                    {accessHistory === null ? <p role="status">{t("Cargando historial…")}</p> : <ul>{accessHistory.length ? accessHistory.map(item => <li key={item.id}><strong>{({save:'Permisos guardados', invite:'Invitación creada', accepted:'Cuenta conectada', suspend:'Acceso suspendido', resume:'Acceso reactivado', unlink:'Cuenta desvinculada'})[item.operation] || item.operation}</strong><span>{item.createdAt ? new Date(item.createdAt).toLocaleString('es-PA') : ''}</span></li>) : <li>{t("Los próximos cambios aparecerán aquí.")}</li>}</ul>}
                  </details>
                </div>;
              })()}

              {activeTab === "projects" && (
                <div className="space-y-3">
                  <SectionCard title={t("Portfolio")}>
                    <div className="text-sm text-slate-600">{t("Sube imágenes para la galería pública o interna de esta persona.")}</div>

                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {(editingEmployee.projects || []).length === 0 ? (
                        <div className="text-sm text-slate-500 col-span-full">{t("No hay imágenes cargadas.")}</div>
                      ) : (
                        (editingEmployee.projects || []).map((url, i) => (
                          <div key={i} className="rounded-2xl overflow-hidden border border-slate-200 bg-slate-100">
                            <img src={url} alt={`project-${i}`} className="w-full h-28 object-cover" />
                          </div>
                        ))
                      )}
                    </div>

                    <div className="mt-3">
                      <input ref={fileInputRef} type="file" multiple accept={IMAGE_UPLOAD_ACCEPT} className="hidden" onChange={handleSelectProjectFiles} />
                      <button
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                        className="w-full px-4 py-3 rounded-2xl bg-white border border-slate-200 font-extrabold"
                      >{t("Subir archivos")}</button>
                    </div>
                  </SectionCard>
                </div>
              )}
            </div>

            {/* Sticky footer */}
            <div className="sticky bottom-0 z-10 bg-white border-t border-slate-100 p-4 workers-editor-footer">
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={closeEdit}
                  className="px-4 py-3 rounded-2xl bg-slate-100 font-extrabold"
                >{t("Cancelar")}</button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className={[
                    "px-4 py-3 rounded-2xl font-extrabold text-white",
                    saving ? "bg-emerald-400" : "bg-emerald-600",
                  ].join(" ")}
                >
                  {saving ? t("Guardando...") : t("Guardar cambios")}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}


      {compensationEditorOpen && modalOpen && editingEmployee && createPortal(
        <div className="workers-comp-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setCompensationEditorOpen(false); }}>
          <div className="workers-comp-modal-shell" onMouseDown={(e) => e.stopPropagation()}>
            <div className="workers-comp-modal-head workers-comp-modal-head-clean">
              <div>
                <span>{t("Compensación")}</span>
                <strong>{t("Editar comisión")}</strong>
                <p>{t("Define cómo gana esta persona. Los cambios se aplican al guardar.")}</p>
              </div>
              <button type="button" className="workers-comp-modal-close" onClick={() => setCompensationEditorOpen(false)} aria-label={t("Cerrar")}>×</button>
            </div>

            <div className="workers-comp-modal-body workers-comp-modal-body-clean">
              {(() => {
                const commissionType = normalizeCommissionType(editingEmployee.defaultCommissionType || "percentage");
                const hasCommission = commissionType !== "none";
                const amountLabel = commissionType === "fixed" ? "Monto" : "Porcentaje";
                const amountPlaceholder = commissionType === "fixed" ? "10.00" : "20";
                const amountPrefix = commissionType === "fixed" ? "$" : "";
                const amountSuffix = commissionType === "percentage" ? "%" : "";
                const value = normalizeCommissionValue(editingEmployee.defaultCommissionValue);
                const amountText = commissionType === "percentage"
                  ? `${value || 0}%`
                  : formatWorkerCommissionMoney(value || 0);
                const summary = hasCommission
                  ? `${amountText} · ${workerCommissionApplyLabel(editingEmployee)} · ${workerCommissionBaseLabel(editingEmployee)}`
                  : "Sin comisión automática";

                const changeCommissionType = (nextType) => {
                  updateField("defaultCommissionType", nextType);
                  if (nextType === "none") {
                    updateField("defaultCommissionValue", 0);
                    return;
                  }
                  if (normalizeCommissionValue(editingEmployee.defaultCommissionValue) <= 0) {
                    updateField("defaultCommissionValue", nextType === "percentage" ? 20 : 10);
                  }
                };

                return (
                  <div className="workers-comp-clean-card">
                    <div className="workers-comp-clean-row">
                      <label className="workers-comp-clean-field workers-comp-clean-model">
                        <span>{t("Modelo")}</span>
                        <select
                          value={commissionType}
                          onChange={(e) => changeCommissionType(e.target.value)}
                        >
                          <option value="none">{t("Sin comisión")}</option>
                          <option value="percentage">{t("Porcentaje")}</option>
                          <option value="fixed">{t("Monto fijo")}</option>
                        </select>
                      </label>

                      <label className={`workers-comp-clean-field workers-comp-clean-amount ${!hasCommission ? "is-disabled" : ""}`}>
                        <span>{hasCommission ? amountLabel : "Monto"}</span>
                        <div className="workers-comp-clean-amount-box">
                          {amountPrefix ? <em>{amountPrefix}</em> : null}
                          <input
                            inputMode="decimal"
                            disabled={!hasCommission}
                            value={hasCommission ? (editingEmployee.defaultCommissionValue ?? "") : ""}
                            onChange={(e) => updateField("defaultCommissionValue", e.target.value.replace(/[^0-9.]/g, ""))}
                            placeholder={hasCommission ? amountPlaceholder : "—"}
                          />
                          {amountSuffix ? <em>{amountSuffix}</em> : null}
                        </div>
                      </label>
                    </div>

                    {hasCommission ? (
                      <>
                        <button
                          type="button"
                          className={`workers-comp-clean-toggle ${editingEmployee.commissionOnlyCompleted !== false ? "is-active" : ""}`}
                          onClick={() => updateField("commissionOnlyCompleted", editingEmployee.commissionOnlyCompleted === false)}
                        >
                          <span>{t("Solo reservas completadas")}</span>
                          <i aria-hidden="true" />
                        </button>

                        <div className="workers-comp-clean-base" role="group" aria-label={t("Base de comisión")}>
                          <button
                            type="button"
                            className={!editingEmployee.commissionIncludesTips ? "is-active" : ""}
                            onClick={() => updateField("commissionIncludesTips", false)}
                          >{t("Solo servicio")}</button>
                          <button
                            type="button"
                            className={editingEmployee.commissionIncludesTips ? "is-active" : ""}
                            onClick={() => updateField("commissionIncludesTips", true)}
                          >{t("Servicio + propina")}</button>
                        </div>
                      </>
                    ) : (
                      <div className="workers-comp-clean-empty">{t("No se calculará payout automático para esta persona.")}</div>
                    )}

                    <div className={`workers-comp-clean-summary ${hasCommission ? "is-active" : ""}`}>
                      <span aria-hidden="true">{hasCommission ? "✓" : "—"}</span>
                      <strong>{summary}</strong>
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="workers-comp-modal-actions workers-comp-modal-actions-clean">
              <button type="button" onClick={() => setCompensationEditorOpen(false)}>{t("Cancelar")}</button>
              <button type="button" className="primary" onClick={() => setCompensationEditorOpen(false)}>{t("Listo")}</button>
            </div>
          </div>
        </div>,
        document.body
      )}


      {accessShareOpen && createPortal(
        <div className="workers-access-share-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) closeAccessShareModal(); }}>
          <div className="workers-access-share-shell" onMouseDown={(e) => e.stopPropagation()}>
            <div className="workers-access-share-head">
              <span>{accessShareMode === "choose" ? t("Paso 1 de 3") : accessShareMode === "qr" ? t("QR presencial") : t("Link seguro")}</span>
              <strong>{accessShareMode === "choose" ? "¿Cómo quieres conectar al trabajador?" : accessShareMode === "qr" ? `Escanear acceso de ${editingWorkerName}` : `Compartir acceso de ${editingWorkerName}`}</strong>
              <p>{accessShareMode === "choose" ? `Conecta a ${editingWorkerName} para activar agenda, reservas y permisos.` : accessShareMode === "qr" ? "El trabajador escanea este código con la cámara de su celular. Mantén esta ventana abierta." : "Copia el link, envíalo por correo o compártelo por el canal que prefieras."}</p>
              <button type="button" className="workers-access-share-close" onClick={closeAccessShareModal} aria-label={t("Cerrar")}>×</button>
            </div>

            <div className="workers-access-share-body">
              {accessShareError ? <div role="alert" style={{padding:16,marginBottom:16,border:'1px solid #efc7bd',borderRadius:12,background:'#fff7f4',color:'#793b2c'}}>
                <strong>{t("No se pudo preparar la invitación")}</strong>
                <p>{accessShareError}</p>
                <button type="button" className="workers-access-share-method-back" disabled={accessShareLoading} onClick={() => prepareAccessShareLink({mode:accessShareMode === 'qr' ? 'qr' : 'link'})}>Reintentar</button>
              </div> : null}
              {accessShareMode === "choose" ? (
                <>
                  <div className="workers-access-share-progress" aria-hidden="true">
                    <span className="workers-access-share-progress-pill">{t("Paso 1 de 3")}</span>
                    <span className="workers-access-share-dots"><i className="is-active" /><i /><i /></span>
                  </div>

                  <div className="workers-access-share-method-list" aria-label={t("Métodos para conectar trabajador")}>
                    <button type="button" className="workers-access-share-option is-recommended" onClick={handleShowInviteQr} disabled={accessShareLoading || !editingEmployee?.id}>
                      <span className="workers-access-share-option-icon">▦</span>
                      <span className="workers-access-share-option-copy">
                        <span className="workers-access-share-option-title"><h4>{t("QR presencial")}</h4><em className="workers-access-share-option-badge">{t("Más rápido")}</em></span>
                        <p>{t("Para conectar su cuenta escaneando el código.")}</p>
                      </span>
                      <span className="workers-access-share-option-arrow">›</span>
                    </button>

                    <button type="button" className="workers-access-share-option" onClick={handleOpenInviteLinkPanel} disabled={accessShareLoading || !editingEmployee?.id}>
                      <span className="workers-access-share-option-icon">↗</span>
                      <span className="workers-access-share-option-copy">
                        <span className="workers-access-share-option-title"><h4>{t("Link seguro")}</h4><em className="workers-access-share-option-badge">{t("Disponible ahora")}</em></span>
                        <p>{t("Cópialo, envíalo por correo o pégalo en cualquier chat.")}</p>
                      </span>
                      <span className="workers-access-share-option-arrow">›</span>
                    </button>


                  </div>

                  <div className="workers-access-share-bottom-note">{t("El trabajador inicia sesión y acepta la invitación.")}</div>
                </>
              ) : (
                <div className="workers-access-share-active">
                  <button type="button" className="workers-access-share-method-back" onClick={() => setAccessShareMode("choose")}>{t("← Cambiar método")}</button>

                  <div className="workers-access-share-active-head">
                    <div>
                      <span>{accessShareMode === "qr" ? "Paso 2 de 3 · QR presencial" : "Paso 2 de 3 · Link seguro"}</span>
                      <h3>{accessShareMode === "qr" ? "Muestra este código" : "Comparte este link"}</h3>
                      <p>{accessShareMode === "qr" ? "El trabajador debe escanearlo para conectar su cuenta con la empresa." : "El trabajador abre el link, inicia sesión y queda conectado a este perfil."}</p>
                    </div>
                    {accessShareSessionExpiresAt && <div className="workers-access-share-timer-pill" title={t("Vencimiento de la invitación")}>
                      <strong>{accessShareExpired ? "00:00" : accessShareTimerLabel}</strong>
                      <small>{accessShareExpired ? "Expirado" : "restante"}</small>
                    </div>}
                  </div>

                  <div className="workers-access-share-workspace">
                    <div className="workers-access-share-mainpanel">
                      {accessShareMode === "qr" ? (
                        <div className="workers-access-share-qr-frame">
                          {accessShareLoading ? (
                            <strong style={{ color: "#00675f" }}>{t("Creando QR...")}</strong>
                          ) : accessShareDataUrl ? (
                            <img src={accessShareDataUrl} alt="QR de acceso del trabajador" />
                          ) : accessShareUrl ? (
                            <div className="workers-qr-link-fallback">{t("No pudimos dibujar el QR. Copia este link:")}<br /><br />{accessShareUrl}</div>
                          ) : (
                            <strong style={{ color: "#64748b" }}>{t("QR no disponible")}</strong>
                          )}
                        </div>
                      ) : null}

                      {accessShareMode === "link" ? (
                        <div className="workers-access-share-link-box">
                          <label>{t("Link de acceso")}</label>
                          <div className="workers-access-share-link-inputrow">
                            <div className="workers-access-share-link-value">
                              {accessShareLoading ? "Creando link..." : accessShareUrl || "Link no disponible"}
                            </div>
                            <button type="button" className="workers-access-share-mini-copy" onClick={copyAccessShareUrl} disabled={accessShareLoading || !accessShareUrl}>⧉</button>
                          </div>
                        </div>
                      ) : null}
                    </div>

                    <aside className="workers-access-share-sidepanel">
                      <h4 className="workers-access-share-side-title">{t("Estado del acceso")}</h4>
                      <div className="workers-access-share-sync-card">
                        <strong>{accessShareExpired ? "Acceso vencido" : accessShareStatus.label}</strong>
                        <span>{accessShareExpired ? "Genera un nuevo acceso para seguir compartiéndolo." : accessShareStatus.helper}</span>
                      </div>

                      <div className="workers-access-share-next-steps">
                        <div className="workers-access-share-next-step"><i>1</i><span>{accessShareMode === "qr" ? "El trabajador escanea el código." : "Comparte el link con el trabajador."}</span></div>
                        <div className="workers-access-share-next-step"><i>2</i><span>{t("El trabajador inicia sesión con su cuenta.")}</span></div>
                        <div className="workers-access-share-next-step"><i>3</i><span>{t("Loop lo conecta y activa sus permisos.")}</span></div>
                      </div>
                    </aside>
                  </div>

                  <div className="workers-access-share-actions">
                    {accessShareMode === "link" ? (
                      <button type="button" onClick={handleEmailCurrentAccessLink} disabled={accessShareLoading || !accessShareUrl}>{t("Enviar por correo")}</button>
                    ) : null}
                    <button type="button" onClick={accessShareMode === "qr" ? handleShowInviteQr : handleOpenInviteLinkPanel} disabled={accessShareLoading || !editingEmployee?.id}>
                      {accessShareMode === "qr" ? "Generar nuevo QR" : "Crear nuevo link"}
                    </button>
                    <button type="button" className="primary" onClick={copyAccessShareUrl} disabled={accessShareLoading || !accessShareUrl}>
                      {accessShareCopied ? "Copiado" : accessShareMode === "qr" ? t("Copiar link") : t("Copiar link")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {accessQrOpen && createPortal(
        <div className="workers-qr-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setAccessQrOpen(false); }}>
          <div className="workers-qr-modal-shell" onMouseDown={(e) => e.stopPropagation()}>
            <div className="workers-qr-modal-head">
              <div>
                <strong>{t("Escanear acceso")}</strong>
                <span>{t("El trabajador abre la cámara, escanea y se conecta a este perfil.")}</span>
              </div>
              <button type="button" className="workers-qr-close" onClick={() => setAccessQrOpen(false)} aria-label={t("Cerrar")}>✕</button>
            </div>

            <div className="workers-qr-box">
              {accessQrLoading ? (
                <strong style={{ color: "#00675f" }}>{t("Creando QR...")}</strong>
              ) : accessQrDataUrl ? (
                <img src={accessQrDataUrl} alt="QR de acceso del trabajador" />
              ) : accessQrUrl ? (
                <div className="workers-qr-link-fallback">{t("No pudimos dibujar el QR automáticamente. Copia este link:")}<br /><br />{accessQrUrl}
                </div>
              ) : (
                <strong style={{ color: "#64748b" }}>{t("QR no disponible")}</strong>
              )}
            </div>

            <div className="workers-qr-actions">
              <button type="button" onClick={() => setAccessQrOpen(false)}>{t("Cerrar")}</button>
              <button
                type="button"
                className="primary"
                disabled={!accessQrUrl}
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(accessQrUrl);
                    alert("Link copiado");
                  } catch (_) {
                    window.prompt("Copia este link:", accessQrUrl);
                  }
                }}
              >{t("Copiar link")}</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
